import { defineComponent, reactive, readonly, ref, SetupContext } from '@vue/composition-api';
import {
    ParameterType,
    CategoryList,
    TableHeader,
    PullDown,
    ColumnMenu,
    ErrorTableType,
    ColumnSearch } from '@/shared/type';                        // パラメータ管理の型をimport
import SystemConst from '@/shared/system-const';                // 定数定義
import MessageConst from '@/shared/message-const';              // メッセージ定数定義
import CommonFunction from '@/shared/common-function';          // 共通関数定義
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';
import EmailValidator from 'email-validator';
import HttpStatus from 'http-status-codes';

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };                               // Windowサイズ
    categoryListItems: CategoryList[];                                  // カテゴリ一覧情報
    categoryDisplayTableHeaders: TableHeader[];                         // カテゴリ一覧ヘッダー情報
    total: number;                                                      // 全ページの件数
    isLoading: boolean;                                                 // テーブルがロード中: true ロード中でない: false
    schoolTypePulldownItems: PullDown[];                                // 校種プルダウン
    selectSchoolTypePulldown: string;                                   // 選択した校種
    curriculumPulldownItems: PullDown[];                                // 教科プルダウン
    selectCurriculumPulldown: string;                                   // 選択した教科
    isEnabledPulldownItems: PullDown[];                                 // 状態プルダウン
    orgPulldownItems: PullDown[];                                       // 組織名称プルダウン
    isAdminPulldownItems: PullDown[];                                   // 管理者表示プルダウン
    materialFileSize: number;                                           // 教材最大ファイルサイズ
    thumbnailFileSize: number;                                          // サムネイル最大ファイルサイズ
    thumbnailExtension: string[];                                       // サムネイル拡張子
    tableName: string;                                                  // カテゴリ一覧名称
    functionPulldownItems: PullDown[];                                  // 機能選択プルダウン
    selectFunctionPulldown: string;                                     // 選択した機能
    tabs: { id: number; name: string }[];                               // タブ内容
    selectTab: string;                                                  // 選択したタブ
    parameters: ParameterType;                                          // パラメータ設定フォーム
    serviceCode: string;                                                // パラメータマスタのサービスコード
    validateValues: ValidateType;                                       // バリデーション値
    isNewArrivalDaysValid: boolean;                                     // バリデーション結果（新着日数）
    isFileExtensionValid: boolean;                                      // バリデーション結果（ファイル拡張子）
    isFileSizeValid: boolean;                                           // バリデーション結果（最大ファイルサイズ）
    isFileCountValid: boolean;                                          // バリデーション結果（最大ファイル数）
    isPublishEndDaysValid: boolean;                                     // バリデーション結果（掲載期間終了日数）
    isBasicFileSizeValid: boolean;                                      // バリデーション結果（基本データファイル最大ファイルサイズ）
    isAttachmentFileSizeValid: boolean;                                 // バリデーション結果（添付データファイル最大ファイルサイズ）
    isAttachmentDetailFileExtensionValid: boolean;                      // バリデーション結果（送付ファイルファイル拡張子）
    isAttachmentDetailFileSizeValid: boolean;                           // バリデーション結果（送付ファイル最大ファイルサイズ）
    isAttachmentDetailFileCountValid: boolean;                          // バリデーション結果（送付ファイル最大ファイル数）
    isMaterialFileExtensionValid: boolean;                              // バリデーション結果（教材ファイルファイル拡張子）
    isMaterialFileSizeValid: boolean;                                   // バリデーション結果（教材ファイル最大ファイルサイズ）
    isMaterialFileCountValid: boolean;                                  // バリデーション結果（教材ファイル最大ファイル数）
    isThumbnailFileExtensionValid: boolean;                             // バリデーション結果（サムネイルファイルファイル拡張子）
    isThumbnailFileSizeValid: boolean;                                  // バリデーション結果（サムネイルファイル最大ファイルサイズ）
    isBulkAddUsersFileSizeValid: boolean;                               // バリデーション結果（一括登録ファイル最大ファイルサイズ）
    isReplyFileExtensionValid: boolean;                                 // バリデーション結果（返信ファイルファイル拡張子）
    isReplyFileSizeValid: boolean;                                      // バリデーション結果（返信ファイル最大ファイルサイズ）
    isReplyFileCountValid: boolean;                                     // バリデーション結果（返信ファイル最大ファイル数）
    isApplicationFileExtensionValid: boolean;                           // バリデーション結果（申請ファイルファイル拡張子）
    isApplicationFileSizeValid: boolean;                                // バリデーション結果（申請ファイル最大ファイルサイズ）
    isAttachFileExtensionValid: boolean;                                // バリデーション結果（添付ファイルファイル拡張子）
    isAttachFileSizeValid: boolean;                                     // バリデーション結果（添付ファイル最大ファイルサイズ）
    isAttachFileCountValid: boolean;                                    // バリデーション結果（添付ファイル最大ファイル数）
    isAffiliationCodeFileSizeValid: boolean;                            // バリデーション結果（所属コードファイル最大ファイルサイズ）
    isPersonnelChangeFileSizeValid: boolean;                            // バリデーション結果（人事異動データファイル最大ファイルサイズ）
    isNotificationPasswordFileSizeValid: boolean;                       // バリデーション結果（パスワード情報ファイル最大ファイルサイズ）
    isNotificationAddresseeFileSizeValid: boolean;                      // バリデーション結果（宛名ファイル最大ファイルサイズ）
    isNotificationTemplateFileSizeValid: boolean;                       // バリデーション結果（雛形ファイル最大ファイルサイズ）
    isNotificationDataTitleValid: boolean;                              // バリデーション結果（基本データファイルタイトル）
    isDeleteDaysValid: boolean;                                         // バリデーション結果（メールエイリアス削除日数）
    isTeacherMailReuseDayValid: boolean;                                // バリデーション結果（a1、c1、s1アカウントメールアドレス凍結日数）
    isStudentGeneralMailReuseDayValid: boolean;                         // バリデーション結果（b1、b2アカウントメールアドレス凍結日数）
    newArrivalDaysErrorMessage: string;                                 // バリデーションメッセージ文字列（新着日数）
    fileExtensionErrorMessage: string;                                  // バリデーションメッセージ文字列（ファイル拡張子）
    fileSizeErrorMessage: string;                                       // バリデーションメッセージ文字列（最大ファイルサイズ）
    fileCountErrorMessage: string;                                      // バリデーションメッセージ文字列（最大ファイル数）
    publishEndDaysErrorMessage: string;                                 // バリデーションメッセージ文字列（掲載期間終了日数）
    basicFileSizeErrorMessage: string;                                  // バリデーションメッセージ文字列（基本データファイル最大ファイルサイズ）
    attachmentFileSizeErrorMessage: string;                             // バリデーションメッセージ文字列（添付データファイル最大ファイルサイズ）
    attachmentDetailFileExtensionErrorMessage: string;                  // バリデーションメッセージ文字列（送付ファイルファイル拡張子）
    attachmentDetailFileSizeErrorMessage: string;                       // バリデーションメッセージ文字列（送付ファイル最大ファイルサイズ）
    attachmentDetailFileCountErrorMessage: string;                      // バリデーションメッセージ文字列（送付ファイル最大ファイル数）
    materialFileExtensionErrorMessage: string;                          // バリデーションメッセージ文字列（教材ファイルファイル拡張子）
    materialFileSizeErrorMessage: string;                               // バリデーションメッセージ文字列（教材ファイル最大ファイルサイズ）
    materialFileCountErrorMessage: string;                              // バリデーションメッセージ文字列（教材ファイル最大ファイル数）
    thumbnailFileExtensionErrorMessage: string;                         // バリデーションメッセージ文字列（サムネイルファイルファイル拡張子）
    thumbnailFileSizeErrorMessage: string;                              // バリデーションメッセージ文字列（サムネイルファイル最大ファイルサイズ）
    bulkAddUsersFileSizeErrorMessage: string;                           // バリデーションメッセージ文字列（一括登録ファイル最大ファイルサイズ）
    replyFileExtensionErrorMessage: string;                             // バリデーションメッセージ文字列（返信ファイルファイル拡張子）
    replyFileSizeErrorMessage: string;                                  // バリデーションメッセージ文字列（返信ファイル最大ファイルサイズ）
    replyFileCountErrorMessage: string;                                 // バリデーションメッセージ文字列（返信ファイル最大ファイル数）
    applicationFileExtensionErrorMessage: string;                       // バリデーションメッセージ文字列（申請ファイルファイル拡張子）
    applicationFileSizeErrorMessage: string;                            // バリデーションメッセージ文字列（申請ファイル最大ファイルサイズ）
    attachFileExtensionErrorMessage: string;                            // バリデーションメッセージ文字列（添付ファイルファイル拡張子）
    attachFileSizeErrorMessage: string;                                 // バリデーションメッセージ文字列（添付ファイル最大ファイルサイズ）
    attachFileCountErrorMessage: string;                                // バリデーションメッセージ文字列（添付ファイル最大ファイル数）
    affiliationCodeFileSizeErrorMessage: string;                        // バリデーションメッセージ文字列（所属コードファイル最大ファイルサイズ）
    personnelChangeFileSizeErrorMessage: string;                        // バリデーションメッセージ文字列（人事異動データファイル最大ファイルサイズ）
    notificationPasswordFileSizeErrorMessage: string;                   // バリデーションメッセージ文字列（パスワード情報ファイル最大ファイルサイズ）
    notificationAddresseeFileSizeErrorMessage: string;                  // バリデーションメッセージ文字列（宛名ファイル最大ファイルサイズ）
    notificationTemplateFileSizeErrorMessage: string;                   // バリデーションメッセージ文字列（雛形ファイル最大ファイルサイズ）
    notificationDataTitleErrorMessage : string;                         // バリデーションメッセージ文字列（基本データファイルタイトル）
    deleteDaysErrorMessage: string;                                     // バリデーションメッセージ文字列（メールエイリアス削除日数）
    teacherMailReuseDayErrorMessage: string;                            // バリデーションメッセージ文字列（a1、c1、s1アカウントメールアドレス凍結日数）
    studentGeneralMailReuseDayErrorMessage: string;                     // バリデーションメッセージ文字列（b1、b2アカウントメールアドレス凍結日数）
    categoryDeleteDialog: ColumnMenu;                                   // カテゴリ設定削除ダイアログ
    visibleRowMenu: ColumnMenu;                                         // 行メニュー表示切替
    showErrorMessageDialog: boolean;                                    // エラーメッセージダイアログ表示切り替え
    errorItems: ErrorTableType[];                                       // バリデーションエラーのデータ表示内容
    isFileExtensionCountError: boolean;                                 // ファイル拡張子桁数エラーフラグ
    isAttachmentDetailFileExtensionCountError: boolean;                 // 送付ファイルファイル拡張子桁数エラーフラグ
    isMaterialFileExtensionCountError: boolean;                         // 教材ファイルファイル拡張子桁数エラーフラグ
    isThumbnailFileExtensionCountError: boolean;                        // サムネイルファイルファイル拡張子桁数エラーフラグ
    isReplyFileExtensionCountError: boolean;                            // 返信ファイルファイル拡張子桁数エラーフラグ
    isApplicationFileExtensionCountError: boolean;                      // 申請ファイルファイル拡張子桁数エラーフラグ
    isAttachFileExtensionCountError: boolean;                           // 添付ファイルファイル拡張子桁数エラーフラグ
    searchIndividual: ColumnSearch;                                     // 組織名称ダイアログ検索情報
    curriculumSearchIndividual: ColumnSearch;                           // 教科プルダウン検索情報
    displayConfirmationDialog: boolean;                                 // 行削除確認ダイアログ表示フラグ
    isDeleteRow: boolean;                                               // 行削除処理フラグ
    successGetSchoolTypePulldownItems: boolean;                         // 校種プルダウンデータ取得成功フラグ
    successGetCurriculumPulldownItems: boolean;                         // 教科プルダウンデータ取得成功フラグ
    displayProcessCategoryButton: boolean;                              // カテゴリ設定登録ボタン表示フラグ
};

// バリデーション用パラメータ
interface ValidateType {
    [key: string]: ValidateValues
}

// バリデーション用パラメータデータ
type ValidateValues = {
    parameterName: string;                                                                // パラメータ名
    minimum: string;                                                                      // パラメータ最小値
    maximum: string;                                                                      // パラメータ最大値
}

export default defineComponent({
    components: { CommonMessageDialog },
    setup(props, ctx: SetupContext) {
        const formHeight = SystemConst.FORM_HEIGHT.PARAMETER;                             // フォーム高さ調整用変数
        const commonRef = ref<any>(null);                                                 // 共通ダイアログ
        const form = ref<any>(null);                                                      // 入力フォーム
        const selectFunctionPulldownItems = SystemConst.SELECT_FUNCTION_PULLDOWN_ITEMS;   // 選択済み機能選択プルダウン定義
        const processCode = SystemConst.PROCESS_CODE;                                     // 処理フラグ定義
        const tabLists = SystemConst.TABS;                                                // タブ番号定義
        let errors: string[] = [];                                                        // エラーメッセージ保存用変数

        // リアクティブな変数定義
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },                                                   // ウィンドウサイズ
            categoryListItems: [],                                                        // カテゴリ一覧情報
            categoryDisplayTableHeaders: [],                                              // カテゴリ一覧ヘッダー
            total: 0,                                                                     // 全ページの件数
            isLoading: false,                                                             // テーブルがロード中: true ロード中でない: false
            tableName: '',
            schoolTypePulldownItems: [],
            selectSchoolTypePulldown: '',
            curriculumPulldownItems: [],
            selectCurriculumPulldown: '',
            isEnabledPulldownItems: [],
            orgPulldownItems: [],
            isAdminPulldownItems: [],
            thumbnailExtension: ['bmp', 'jpg', 'jpeg', 'gif', 'png'],
            materialFileSize: 102400,
            thumbnailFileSize: 2048,
            functionPulldownItems: SystemConst.FUNCTION_PULLDOWN_ITEMS,
            selectFunctionPulldown: '1',
            tabs: [],
            selectTab: tabLists['FIRST'],
            parameters: {
                newArrivalDays: { parameterCode: '', value: '' },
                fileExtension: { parameterCode: '', value: [] },
                fileSize: { parameterCode: '', value: '' },
                fileCount: { parameterCode: '', value: '' },
                mailText: { parameterCode: '', value: '' },
                publishEndDays: { parameterCode: '', value: '' },
                basicFileSize: { parameterCode: '', value: '' },
                attachmentFileSize: { parameterCode: '', value: '' },
                attachmentDetailFileExtension: { parameterCode: '', value: [] },
                attachmentDetailFileSize: { parameterCode: '', value: '' },
                attachmentDetailFileCount: { parameterCode: '', value: '' },
                materialFileExtension: { parameterCode: '', value: [] },
                materialFileSize: { parameterCode: '', value: '' },
                materialFileCount: { parameterCode: '', value: '' },
                thumbnailFileExtension: { parameterCode: '', value: [] },
                thumbnailFileSize: { parameterCode: '', value: '' },
                bulkAddUsersFileSize: { parameterCode: '', value: '' },
                replyFileExtension: { parameterCode: '', value: [] },
                replyFileSize: { parameterCode: '', value: '' },
                replyFileCount: { parameterCode: '', value: '' },
                affiliationCodeFileSize: { parameterCode: '', value: '' },
                personnelChangeFileSize: { parameterCode: '', value: '' },
                notificationPasswordFileSize: { parameterCode: '', value: '' },
                notificationAddresseeFileSize: { parameterCode: '', value: '' },
                notificationTemplateFileSize: { parameterCode: '', value: '' },
                notificationDataTitle: { parameterCode: '', value: '' },
                notificationDataMessage: { parameterCode: '', value: '' },
                applicationFileExtension: { parameterCode: '', value: [] },
                applicationFileSize: { parameterCode: '', value: '' },
                attachFileExtension: { parameterCode: '', value: [] },
                attachFileSize: { parameterCode: '', value: '' },
                attachFileCount: { parameterCode: '', value: '' },
                deleteDays: { parameterCode: '', value: '' },
                teacherMailReuseDay: { parameterCode: '', value: '' },
                studentGeneralMailReuseDay: { parameterCode: '', value: '' }
            },
            serviceCode: '',
            validateValues: {},
            isNewArrivalDaysValid: true,
            isFileExtensionValid: true,
            isFileSizeValid: true,
            isFileCountValid: true,
            isPublishEndDaysValid: true,
            isBasicFileSizeValid: true,
            isAttachmentFileSizeValid: true,
            isAttachmentDetailFileExtensionValid: true,
            isAttachmentDetailFileSizeValid: true,
            isAttachmentDetailFileCountValid: true,
            isMaterialFileExtensionValid: true,
            isMaterialFileSizeValid: true,
            isMaterialFileCountValid: true,
            isThumbnailFileExtensionValid: true,
            isThumbnailFileSizeValid: true,
            isBulkAddUsersFileSizeValid: true,
            isReplyFileExtensionValid: true,
            isReplyFileSizeValid: true,
            isReplyFileCountValid: true,
            isApplicationFileExtensionValid: true,
            isApplicationFileSizeValid: true,
            isAttachFileExtensionValid: true,
            isAttachFileSizeValid: true,
            isAttachFileCountValid: true,
            isAffiliationCodeFileSizeValid: true,
            isPersonnelChangeFileSizeValid: true,
            isNotificationPasswordFileSizeValid: true,
            isNotificationAddresseeFileSizeValid: true,
            isNotificationTemplateFileSizeValid: true,
            isNotificationDataTitleValid: true,
            isDeleteDaysValid: true,
            isTeacherMailReuseDayValid: true,
            isStudentGeneralMailReuseDayValid: true,
            newArrivalDaysErrorMessage: '',
            fileExtensionErrorMessage: '',
            fileSizeErrorMessage: '',
            fileCountErrorMessage: '',
            publishEndDaysErrorMessage: '',
            basicFileSizeErrorMessage: '',
            attachmentFileSizeErrorMessage: '',
            attachmentDetailFileExtensionErrorMessage: '',
            attachmentDetailFileSizeErrorMessage: '',
            attachmentDetailFileCountErrorMessage: '',
            materialFileExtensionErrorMessage: '',
            materialFileSizeErrorMessage: '',
            materialFileCountErrorMessage: '',
            thumbnailFileExtensionErrorMessage: '',
            thumbnailFileSizeErrorMessage: '',
            bulkAddUsersFileSizeErrorMessage: '',
            replyFileExtensionErrorMessage: '',
            replyFileSizeErrorMessage: '',
            replyFileCountErrorMessage: '',
            applicationFileExtensionErrorMessage: '',
            applicationFileSizeErrorMessage: '',
            attachFileExtensionErrorMessage: '',
            attachFileSizeErrorMessage: '',
            attachFileCountErrorMessage: '',
            affiliationCodeFileSizeErrorMessage: '',
            personnelChangeFileSizeErrorMessage: '',
            notificationPasswordFileSizeErrorMessage: '',
            notificationAddresseeFileSizeErrorMessage: '',
            notificationTemplateFileSizeErrorMessage: '',
            notificationDataTitleErrorMessage: '',
            deleteDaysErrorMessage: '',
            teacherMailReuseDayErrorMessage: '',
            studentGeneralMailReuseDayErrorMessage: '',
            categoryDeleteDialog: {},
            visibleRowMenu: {},
            showErrorMessageDialog: false,
            errorItems: [],
            isFileExtensionCountError: false,
            isAttachmentDetailFileExtensionCountError: false,
            isMaterialFileExtensionCountError: false,
            isThumbnailFileExtensionCountError: false,
            isReplyFileExtensionCountError: false,
            isApplicationFileExtensionCountError: false,
            isAttachFileExtensionCountError: false,
            searchIndividual: {},
            curriculumSearchIndividual: {},
            displayConfirmationDialog:false,
            isDeleteRow: false,
            successGetSchoolTypePulldownItems: false,
            successGetCurriculumPulldownItems: false,
            displayProcessCategoryButton: false
        });

        const tableLoadingText = SystemConst.TABLE_LOADING_TEXT;                          // テーブルのデータ取得中に表示するテキスト
        const tableHeaderClass: string[] = ['secondary subtitle-2'];                      // カテゴリ設定のヘッダー色
        const lastTableHeaderClass: string[] = [                                          // 発番行アイコンのカテゴリ設定
            'secondary subtitle-2',
            'last-child'
        ];

        const tableHeight = SystemConst.TABLE_HEIGHT.CATEGORY_LIST;                       // テーブル高さ調整用変数
        const curriculumTableHeight = SystemConst.TABLE_HEIGHT.CURRICULUM_LIST;           // 教科設定のテーブル高さ調整用変数
        const subjectTableHeight = SystemConst.TABLE_HEIGHT.SUBJECT_LIST;                 // 科目設定のテーブル高さ調整用変数

        // 公文書タブ
        const documentTabs: { id: number; name: string }[] = [
            {
                id: 1,
                name: 'パラメータ設定'
            },
            {
                id: 2,
                name: '発番カテゴリ設定'
            },
            {
                id: 3,
                name: 'メール通知先設定'
            }
        ];

        // お知らせ・重要情報のお知らせ・システム管理・共通（システム管理・組織内管理）タブ
        const singleTab: { id: number; name: string }[] = [
            {
                id: 1,
                name: 'パラメータ設定'
            }
        ];
        // 教材DBタブ
        const materialDBTabs: { id: number; name: string }[] = [
            {
                id: 1,
                name: 'パラメータ設定'
            },
            {
                id: 2,
                name: '校種設定'
            },
            {
                id: 3,
                name: '教科設定'
            },
            {
                id: 4,
                name: '科目設定'
            }
        ];
        // 教育用コンテンツタブ
        const contentTabs: { id: number; name: string }[] = [
            {
                id: 1,
                name: 'パラメータ設定'
            },
            {
                id: 2,
                name: 'コンテンツカテゴリ設定'
            }
        ];
        // 組織内管理タブ
        const orgAdminTabs: { id: number; name: string }[] = [
            {
                id: 1,
                name: 'パラメータ設定'
            },
            {
                id: 2,
                name: '処理依頼内容設定'
            }
        ];
        // テーブルヘッダー（発番）
        const numberPrefixTableHeaders: TableHeader[] = [
            {
                text: '表示順',
                value: 'displayOrder',
                width: '80px',
                class: tableHeaderClass,
                sortable: false
            },
            {
                text: '組織名称',
                value: 'organizationName',
                width: '250px',
                class: tableHeaderClass,
                sortable: false
            },
            {
                text: '発番接頭辞',
                value: 'numberPrefix',
                width: '120px',
                class: tableHeaderClass,
                sortable: false
            },
            {
                text: 'カテゴリ名称',
                value: 'categoryName',
                width: '170px',
                class: tableHeaderClass,
                sortable: false
            },
            {
                text: '部門名',
                value: 'departmentName',
                width: '250px',
                class: tableHeaderClass,
                sortable: false
            },
            {
                text: '電話番号',
                value: 'tel',
                width: '200px',
                class: tableHeaderClass,
                sortable: false
            },
            {
                text: '管理者のみ表示',
                value: 'isAdminOnly',
                width: '140px',
                class: tableHeaderClass,
                sortable: false
            },
            // 行アイコン
            {
                text: '',
                value: 'icons',
                width: '5px',
                class: lastTableHeaderClass,
                sortable: false,
                align: 'end'
            }
        ];

        // テーブルヘッダー（メール通知先）
        const notificationsTableHeaders: TableHeader[] = [
            {
                text: '表示順',
                value: 'displayOrder',
                width: '10%',
                class: tableHeaderClass,
                sortable: false
            },
            {
                text: 'メール通知先名称',
                value: 'notificationsName',
                width: '35%',
                class: tableHeaderClass,
                sortable: false
            },
            {
                text: 'メールアドレス',
                value: 'mail',
                width: '40%',
                class: tableHeaderClass,
                sortable: false
            },
            {
                text: '状態',
                value: 'isEnabled',
                width: '10%',
                class: tableHeaderClass,
                sortable: false
            },
            // 行アイコン
            {
                text: '',
                value: 'icons',
                width: '5%',
                class: tableHeaderClass,
                sortable: false,
                align: 'end'
            }
        ];

        // テーブルヘッダー（校種）
        const schoolTypeTableHeaders: TableHeader[] = [
            {
                text: '表示順',
                value: 'displayOrder',
                width: '10%',
                class: tableHeaderClass,
                sortable: false
            },
            {
                text: '校種名称',
                value: 'schoolTypeName',
                width: '90%',
                class: tableHeaderClass,
                sortable: false
            }
        ];

        // テーブルヘッダー（教科）
        const curriculumTableHeaders: TableHeader[] = [
            {
                text: '表示順',
                value: 'displayOrder',
                width: '10%',
                class: tableHeaderClass,
                sortable: false
            },
            {
                text: '教科名称',
                value: 'curriculumName',
                width: '85%',
                class: tableHeaderClass,
                sortable: false
            },
            // 行アイコン
            {
                text: '',
                value: 'icons',
                width: '5%',
                class: tableHeaderClass,
                sortable: false,
                align: 'end'
            }
        ];
        // テーブルヘッダー（科目）
        const subjectTableHeaders: TableHeader[] = [
            {
                text: '表示順',
                value: 'displayOrder',
                width: '10%',
                class: tableHeaderClass,
                sortable: false
            },
            {
                text: '科目名称',
                value: 'subjectName',
                width: '85%',
                class: tableHeaderClass,
                sortable: false
            },
            // 行アイコン
            {
                text: '',
                value: 'icons',
                width: '5%',
                class: tableHeaderClass,
                sortable: false,
                align: 'end'
            }
        ];

        // テーブルヘッダー（コンテンツカテゴリ）
        const contentTableHeaders: TableHeader[] = [
            {
                text: '表示順',
                value: 'displayOrder',
                width: '10%',
                class: tableHeaderClass,
                sortable: false
            },
            {
                text: 'カテゴリ名称',
                value: 'categoryName',
                width: '75%',
                class: tableHeaderClass,
                sortable: false
            },
            {
                text: '状態',
                value: 'isEnabled',
                width: '10%',
                class: tableHeaderClass,
                sortable: false
            },
            // 行アイコン
            {
                text: '',
                value: 'icons',
                width: '5%',
                class: tableHeaderClass,
                sortable: false,
                align: 'end'
            }
        ];

        // テーブルヘッダー（処理依頼内容）
        const processingRequestTableHeaders: TableHeader[] = [
            {
                text: '表示順',
                value: 'displayOrder',
                width: '10%',
                class: tableHeaderClass,
                sortable: false
            },
            {
                text: '依頼内容名称',
                value: 'categoryName',
                width: '75%',
                class: tableHeaderClass,
                sortable: false
            },
            {
                text: '状態',
                value: 'isEnabled',
                width: '10%',
                class: tableHeaderClass,
                sortable: false
            },
            // 行アイコン
            {
                text: '',
                value: 'icons',
                width: '5%',
                class: tableHeaderClass,
                sortable: false,
                align: 'end'
            }
        ];

        // パラメータ設定バリデーション用最大桁数
        const paramMaxLength = {
            // ファイル拡張子
            fileExtension: SystemConst.PARAMETER_MAX_LENGTH.FILE_EXTENSION,
            // メール通知定型文
            mailText: SystemConst.PARAMETER_MAX_LENGTH.MAIL_TEXT,
            // 送付ファイルファイル拡張子
            attachmentDetailFileExtension: SystemConst.PARAMETER_MAX_LENGTH.ATTACHMENT_DETAIL_FILE_EXTENSION,
            // 教材ファイルファイル拡張子
            materialFileExtension: SystemConst.PARAMETER_MAX_LENGTH.MATERIAL_FILE_EXTENSION,
            // サムネイルファイルファイル拡張子
            thumbnailFileExtension: SystemConst.PARAMETER_MAX_LENGTH.THUMBNAIL_FILE_EXTENSION,
            // 返信ファイルファイル拡張子
            replyFileExtension: SystemConst.PARAMETER_MAX_LENGTH.REPLY_FILE_EXTENSION,
            // 申請ファイルファイル拡張子
            applicationFileExtension: SystemConst.PARAMETER_MAX_LENGTH.APPLICATION_FILE_EXTENSION,
            // 添付ファイルファイル拡張子
            attachFileExtension: SystemConst.PARAMETER_MAX_LENGTH.ATTACH_FILE_EXTENSION,
            // 通知データタイトル
            notificationDataTitle: SystemConst.PARAMETER_MAX_LENGTH.NOTIFICATION_DATA_TITLE,
            // 通知データメッセージ
            notificationDataMessage: SystemConst.PARAMETER_MAX_LENGTH.NOTIFICATION_DATA_MESSAGE
        };

        // カテゴリ設定バリデーション用最大桁数
        const categoryMaxLength = {
            // 発番カテゴリ
            // 発番接頭辞
            numberPrefix: SystemConst.CATEGORY_MAX_LENGTH.NUMBER_PREFIX,
            // カテゴリ名称
            numberPrefixCategoryName: SystemConst.CATEGORY_MAX_LENGTH.NUMBER_PREFIX_CATEGORY_NAME,
            // 部門名
            departmentName: SystemConst.CATEGORY_MAX_LENGTH.DEPARTMENT_NAME,
            // 電話番号
            tel: SystemConst.CATEGORY_MAX_LENGTH.TEL,

            // メール通知先
            // メール通知先名称
            notificationName: SystemConst.CATEGORY_MAX_LENGTH.NOTIFICATION_NAME,
            // 電話番号
            mail: SystemConst.CATEGORY_MAX_LENGTH.MAIL,

            // 以下の機能の項目で使用する名称の最大桁数
            // ・校種設定：校種名称
            // ・教科設定：教科名称
            // ・科目設定：科目名称
            // ・コンテンツカテゴリ設定：カテゴリ名称
            // ・処理依頼内容設定：依頼内容名称
            categoryName: SystemConst.CATEGORY_MAX_LENGTH.CATEGORY_NAME
        };

        // 新着日数のバリデーション
        const newArrivalDayRules = [
            () => {
                // バリデーション値が取得されていない段階では以降を実行しない
                if (!('newArrivalDays' in state.validateValues)) {
                    return;
                }

                // バリデーション値
                const minimum = Number(state.validateValues['newArrivalDays'].minimum);
                const maximum = Number(state.validateValues['newArrivalDays'].maximum);

                // 必須チェック            
                if (!state.parameters.newArrivalDays.value) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REQUIRED, ['新着日数']);
                }

                // 属性チェック（半角数字）
                if (!state.parameters.newArrivalDays.value.match(/^[0-9]+$/)) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REGEX, ['新着日数', '半角数字']);
                }

                // 属性チェック（入力可能範囲）
                if (Number(state.parameters.newArrivalDays.value) < minimum || Number(state.parameters.newArrivalDays.value) > maximum) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_MIN_MAX_LENGTH, ['新着日数', minimum, maximum]);
                }

                return true;
            }
        ];

        // 掲載期間終了日数のバリデーション
        const publishEndDayRules = [
            () => {
                // バリデーション値が取得されていない段階では以降を実行しない
                if (!('publishEndDays' in state.validateValues)) {
                    return;
                }

                // バリデーション値
                const minimum = Number(state.validateValues['publishEndDays'].minimum);
                const maximum = Number(state.validateValues['publishEndDays'].maximum);

                // 必須チェック            
                if (!state.parameters.publishEndDays.value) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REQUIRED, ['掲載期間終了日数']);
                }

                // 属性チェック（半角数字）
                if (!state.parameters.publishEndDays.value.match(/^[0-9]+$/)) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REGEX, ['掲載期間終了日数', '半角数字']);
                }

                // 属性チェック（入力可能範囲）
                if (Number(state.parameters.publishEndDays.value) < minimum || Number(state.parameters.publishEndDays.value) > maximum) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_MIN_MAX_LENGTH, ['掲載期間終了日数', minimum, maximum]);
                }

                return true;
            }
        ];

        // ファイル拡張子のバリデーション
        const fileExtensionRules = [
            () => {
                // ファイル拡張子の文字数を格納する
                const fileExtensionCnt = state.parameters.fileExtension.value.join('').length;

                // Setオブジェクトを作成してファイル拡張子の重複を消す
                const setFileExtension = new Set(state.parameters.fileExtension.value);

                // 必須チェック            
                if (state.parameters.fileExtension.value.length === 0) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REQUIRED, ['ファイル拡張子']);
                }

                // 桁数チェック
                if (fileExtensionCnt > paramMaxLength['fileExtension']) {
                    // ファイル拡張子桁数エラーフラグをtrueにする
                    state.isFileExtensionCountError = true;

                    return CommonFunction.replaceArray(
                        MessageConst.E_MAX, ['ファイル拡張子', paramMaxLength['fileExtension']]);
                }

                // ファイル拡張子桁数エラーフラグをfalseにする
                state.isFileExtensionCountError = false;

                // ファイル拡張子配列でループ
                for (const key in state.parameters.fileExtension.value) {
                    // 属性チェック（半角英数字）
                    if (!state.parameters.fileExtension.value[key].match(/^[0-9a-zA-Z]+$/)) {
                        return CommonFunction.replaceArray(
                            MessageConst.E_REGEX, ['ファイル拡張子', '半角英数字']);
                    }
                }

                // 重複チェック
                if (state.parameters.fileExtension.value.length !== setFileExtension.size) {
                    // ファイル拡張子とSetオブジェクトの数が等しくない場合、ファイル拡張子が重複している
                    return MessageConst.E_FILE_EXTENSION_DUPLICATE;
                }

                return true;
            }
        ];

        // 最大ファイルサイズのバリデーション
        const fileSizeRules = [
            () => {
                // バリデーション値が取得されていない段階では以降を実行しない
                if (!('fileSize' in state.validateValues)) {
                    return;
                }

                // バリデーション値
                const minimum = Number(state.validateValues['fileSize'].minimum);
                const maximum = Number(state.validateValues['fileSize'].maximum);

                // 必須チェック            
                if (!state.parameters.fileSize.value) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REQUIRED, ['最大ファイルサイズ']);
                }

                // 属性チェック（半角数字）
                if (!state.parameters.fileSize.value.match(/^[0-9]+$/)) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REGEX, ['最大ファイルサイズ', '半角数字']);
                }

                // 属性チェック（入力可能範囲）
                if (Number(state.parameters.fileSize.value) < minimum || Number(state.parameters.fileSize.value) > maximum) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_MIN_MAX_LENGTH, ['最大ファイルサイズ', minimum, maximum]);
                }

                return true;
            }
        ];

        // 最大ファイル数のバリデーション
        const fileCountRules = [
            () => {
                // バリデーション値が取得されていない段階では以降を実行しない
                if (!('fileCount' in state.validateValues)) {
                    return;
                }

                // バリデーション値
                const minimum = Number(state.validateValues['fileCount'].minimum);
                const maximum = Number(state.validateValues['fileCount'].maximum);

                // 必須チェック            
                if (!state.parameters.fileCount.value) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REQUIRED, ['最大ファイル数']);
                }

                // 属性チェック（半角数字）
                if (!state.parameters.fileCount.value.match(/^[0-9]+$/)) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REGEX, ['最大ファイル数', '半角数字']);
                }

                // 属性チェック（入力可能範囲）
                if (Number(state.parameters.fileCount.value) < minimum || Number(state.parameters.fileCount.value) > maximum) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_MIN_MAX_LENGTH, ['最大ファイル数', minimum, maximum]);
                }

                return true;
            }
        ];

        // 基本データファイル最大ファイルサイズのバリデーション
        const basicFileSizeRules = [
            () => {
                // バリデーション値が取得されていない段階では以降を実行しない
                if (!('basicFileSize' in state.validateValues)) {
                    return;
                }

                // バリデーション値
                const minimum = Number(state.validateValues['basicFileSize'].minimum);
                const maximum = Number(state.validateValues['basicFileSize'].maximum);

                // 必須チェック            
                if (!state.parameters.basicFileSize.value) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REQUIRED, ['基本データファイルの最大ファイルサイズ']);
                }

                // 属性チェック（半角数字）
                if (!state.parameters.basicFileSize.value.match(/^[0-9]+$/)) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REGEX, ['基本データファイルの最大ファイルサイズ', '半角数字']);
                }

                // 属性チェック（入力可能範囲）
                if (Number(state.parameters.basicFileSize.value) < minimum || Number(state.parameters.basicFileSize.value) > maximum) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_MIN_MAX_LENGTH, ['基本データファイルの最大ファイルサイズ', minimum, maximum]);
                }

                return true;
            }
        ];

        // 添付データファイル最大ファイルサイズのバリデーション
        const attachmentFileSizeRules = [
            () => {
                // バリデーション値が取得されていない段階では以降を実行しない
                if (!('attachmentFileSize' in state.validateValues)) {
                    return;
                }

                // バリデーション値
                const minimum = Number(state.validateValues['attachmentFileSize'].minimum);
                const maximum = Number(state.validateValues['attachmentFileSize'].maximum);

                // 必須チェック            
                if (!state.parameters.attachmentFileSize.value) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REQUIRED, ['添付データファイルの最大ファイルサイズ']);
                }

                // 属性チェック（半角数字）
                if (!state.parameters.attachmentFileSize.value.match(/^[0-9]+$/)) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REGEX, ['添付データファイルの最大ファイルサイズ', '半角数字']);
                }

                // 属性チェック（入力可能範囲）
                if (Number(state.parameters.attachmentFileSize.value) < minimum || Number(state.parameters.attachmentFileSize.value) > maximum) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_MIN_MAX_LENGTH, ['添付データファイルの最大ファイルサイズ', minimum, maximum]);
                }

                return true;
            }
        ];

        // 送付ファイルファイル拡張子のバリデーション
        const attachmentDetailFileExtensionRules = [
            () => {
                // ファイル拡張子の文字数を格納する
                const fileExtensionCnt = state.parameters.attachmentDetailFileExtension.value.join('').length;

                // Setオブジェクトを作成してファイル拡張子の重複を消す
                const setFileExtension = new Set(state.parameters.attachmentDetailFileExtension.value);

                // 必須チェック            
                if (state.parameters.attachmentDetailFileExtension.value.length === 0) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REQUIRED, ['送付ファイルのファイル拡張子']);
                }

                // 桁数チェック
                if (fileExtensionCnt > paramMaxLength['attachmentDetailFileExtension']) {
                    // 送付ファイルファイル拡張子桁数エラーフラグをtrueにする
                    state.isAttachmentDetailFileExtensionCountError = true;

                    return CommonFunction.replaceArray(
                        MessageConst.E_MAX, ['送付ファイルのファイル拡張子', paramMaxLength['attachmentDetailFileExtension']]);
                }

                // ファイル拡張子桁数エラーフラグをfalseにする
                state.isAttachmentDetailFileExtensionCountError = false;

                // ファイル拡張子配列でループ
                for (const key in state.parameters.attachmentDetailFileExtension.value) {
                    // 属性チェック（半角英数字）
                    if (!state.parameters.attachmentDetailFileExtension.value[key].match(/^[0-9a-zA-Z]+$/)) {
                        return CommonFunction.replaceArray(
                            MessageConst.E_REGEX, ['送付ファイルのファイル拡張子', '半角英数字']);
                    }
                }

                // 重複チェック
                if (state.parameters.attachmentDetailFileExtension.value.length !== setFileExtension.size) {
                    // ファイル拡張子とSetオブジェクトの数が等しくない場合、ファイル拡張子が重複している
                    return MessageConst.E_FILE_EXTENSION_DUPLICATE;
                }

                return true;
            }
        ];

        // 送付ファイル最大ファイルサイズのバリデーション
        const attachmentDetailFileSizeRules = [
            () => {
                // バリデーション値が取得されていない段階では以降を実行しない
                if (!('attachmentDetailFileSize' in state.validateValues)) {
                    return;
                }

                // バリデーション値
                const minimum = Number(state.validateValues['attachmentDetailFileSize'].minimum);
                const maximum = Number(state.validateValues['attachmentDetailFileSize'].maximum);

                // 必須チェック            
                if (!state.parameters.attachmentDetailFileSize.value) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REQUIRED, ['送付ファイルの最大ファイルサイズ']);
                }

                // 属性チェック（半角数字）
                if (!state.parameters.attachmentDetailFileSize.value.match(/^[0-9]+$/)) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REGEX, ['送付ファイルの最大ファイルサイズ', '半角数字']);
                }

                // 属性チェック（入力可能範囲）
                if (Number(state.parameters.attachmentDetailFileSize.value) < minimum || Number(state.parameters.attachmentDetailFileSize.value) > maximum) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_MIN_MAX_LENGTH, ['送付ファイルの最大ファイルサイズ', minimum, maximum]);
                }

                return true;
            }
        ];

        // 送付ファイル最大ファイル数のバリデーション
        const attachmentDetailFileCountRules = [
            () => {
                // バリデーション値が取得されていない段階では以降を実行しない
                if (!('attachmentDetailFileCount' in state.validateValues)) {
                    return;
                }

                // バリデーション値
                const minimum = Number(state.validateValues['attachmentDetailFileCount'].minimum);
                const maximum = Number(state.validateValues['attachmentDetailFileCount'].maximum);

                // 必須チェック            
                if (!state.parameters.attachmentDetailFileCount.value) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REQUIRED, ['送付ファイルの最大ファイル数']);
                }

                // 属性チェック（半角数字）
                if (!state.parameters.attachmentDetailFileCount.value.match(/^[0-9]+$/)) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REGEX, ['送付ファイルの最大ファイル数', '半角数字']);
                }

                // 属性チェック（入力可能範囲）
                if (Number(state.parameters.attachmentDetailFileCount.value) < minimum || Number(state.parameters.attachmentDetailFileCount.value) > maximum) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_MIN_MAX_LENGTH, ['送付ファイルの最大ファイル数', minimum, maximum]);
                }

                return true;
            }
        ];

        // 教材ファイルファイル拡張子のバリデーション
        const materialFileExtensionRules = [
            () => {
                // ファイル拡張子の文字数を格納する
                const fileExtensionCnt = state.parameters.materialFileExtension.value.join('').length;

                // Setオブジェクトを作成してファイル拡張子の重複を消す
                const setFileExtension = new Set(state.parameters.materialFileExtension.value);

                // 必須チェック            
                if (state.parameters.materialFileExtension.value.length === 0) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REQUIRED, ['教材ファイルのファイル拡張子']);
                }

                // 桁数チェック
                if (fileExtensionCnt > paramMaxLength['materialFileExtension']) {
                    // 教材ファイルファイル拡張子桁数エラーフラグをtrueにする
                    state.isMaterialFileExtensionCountError = true;

                    return CommonFunction.replaceArray(
                        MessageConst.E_MAX, ['教材ファイルのファイル拡張子', paramMaxLength['materialFileExtension']]);
                }

                // ファイル拡張子桁数エラーフラグをfalseにする
                state.isMaterialFileExtensionCountError = false;

                // ファイル拡張子配列でループ
                for (const key in state.parameters.materialFileExtension.value) {
                    // 属性チェック（半角英数字）
                    if (!state.parameters.materialFileExtension.value[key].match(/^[0-9a-zA-Z]+$/)) {
                        return CommonFunction.replaceArray(
                            MessageConst.E_REGEX, ['教材ファイルのファイル拡張子', '半角英数字']);
                    }
                }

                // 重複チェック
                if (state.parameters.materialFileExtension.value.length !== setFileExtension.size) {
                    // ファイル拡張子とSetオブジェクトの数が等しくない場合、ファイル拡張子が重複している
                    return MessageConst.E_FILE_EXTENSION_DUPLICATE;
                }

                return true;
            }
        ];

        // 教材ファイル最大ファイルサイズのバリデーション
        const materialFileSizeRules = [
            () => {
                // バリデーション値が取得されていない段階では以降を実行しない
                if (!('materialFileSize' in state.validateValues)) {
                    return;
                }

                // バリデーション値
                const minimum = Number(state.validateValues['materialFileSize'].minimum);
                const maximum = Number(state.validateValues['materialFileSize'].maximum);

                // 必須チェック            
                if (!state.parameters.materialFileSize.value) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REQUIRED, ['教材ファイルの最大ファイルサイズ']);
                }

                // 属性チェック（半角数字）
                if (!state.parameters.materialFileSize.value.match(/^[0-9]+$/)) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REGEX, ['教材ファイルの最大ファイルサイズ', '半角数字']);
                }

                // 属性チェック（入力可能範囲）
                if (Number(state.parameters.materialFileSize.value) < minimum || Number(state.parameters.materialFileSize.value) > maximum) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_MIN_MAX_LENGTH, ['教材ファイルの最大ファイルサイズ', minimum, maximum]);
                }

                return true;
            }
        ];

        // 教材ファイル最大ファイル数のバリデーション
        const materialFileCountRules = [
            () => {
                // バリデーション値が取得されていない段階では以降を実行しない
                if (!('materialFileCount' in state.validateValues)) {
                    return;
                }

                // バリデーション値
                const minimum = Number(state.validateValues['materialFileCount'].minimum);
                const maximum = Number(state.validateValues['materialFileCount'].maximum);

                // 必須チェック            
                if (!state.parameters.materialFileCount.value) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REQUIRED, ['教材ファイルの最大ファイル数']);
                }

                // 属性チェック（半角数字）
                if (!state.parameters.materialFileCount.value.match(/^[0-9]+$/)) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REGEX, ['教材ファイルの最大ファイル数', '半角数字']);
                }

                // 属性チェック（入力可能範囲）
                if (Number(state.parameters.materialFileCount.value) < minimum || Number(state.parameters.materialFileCount.value) > maximum) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_MIN_MAX_LENGTH, ['教材ファイルの最大ファイル数', minimum, maximum]);
                }

                return true;
            }
        ];

        // サムネイルファイル拡張子のバリデーション
        const thumbnailFileExtensionRules = [
            () => {
                // ファイル拡張子の文字数を格納する
                const fileExtensionCnt = state.parameters.thumbnailFileExtension.value.join('').length;

                // Setオブジェクトを作成してファイル拡張子の重複を消す
                const setFileExtension = new Set(state.parameters.thumbnailFileExtension.value);

                // 必須チェック            
                if (state.parameters.thumbnailFileExtension.value.length === 0) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REQUIRED, ['サムネイルファイルのファイル拡張子']);
                }

                // 桁数チェック
                if (fileExtensionCnt > paramMaxLength['thumbnailFileExtension']) {
                    // サムネイルファイルファイル拡張子桁数エラーフラグをtrueにする
                    state.isThumbnailFileExtensionCountError = true;

                    return CommonFunction.replaceArray(
                        MessageConst.E_MAX, ['サムネイルファイルのファイル拡張子', paramMaxLength['thumbnailFileExtension']]);
                }

                // ファイル拡張子桁数エラーフラグをfalseにする
                state.isThumbnailFileExtensionCountError = false;

                // ファイル拡張子配列でループ
                for (const key in state.parameters.thumbnailFileExtension.value) {
                    // 属性チェック（半角英数字）
                    if (!state.parameters.thumbnailFileExtension.value[key].match(/^[0-9a-zA-Z]+$/)) {
                        return CommonFunction.replaceArray(
                            MessageConst.E_REGEX, ['サムネイルファイルのファイル拡張子', '半角英数字']);
                    }
                }

                // 重複チェック
                if (state.parameters.thumbnailFileExtension.value.length !== setFileExtension.size) {
                    // ファイル拡張子とSetオブジェクトの数が等しくない場合、ファイル拡張子が重複している
                    return MessageConst.E_FILE_EXTENSION_DUPLICATE;
                }

                return true;
            }
        ];

        // サムネイルファイル最大ファイルサイズのバリデーション
        const thumbnailFileSizeRules = [
            () => {
                // バリデーション値が取得されていない段階では以降を実行しない
                if (!('thumbnailFileSize' in state.validateValues)) {
                    return;
                }

                // バリデーション値
                const minimum = Number(state.validateValues['thumbnailFileSize'].minimum);
                const maximum = Number(state.validateValues['thumbnailFileSize'].maximum);

                // 必須チェック            
                if (!state.parameters.thumbnailFileSize.value) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REQUIRED, ['サムネイルファイルの最大ファイルサイズ']);
                }

                // 属性チェック（半角数字）
                if (!state.parameters.thumbnailFileSize.value.match(/^[0-9]+$/)) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REGEX, ['サムネイルファイルの最大ファイルサイズ', '半角数字']);
                }

                // 属性チェック（入力可能範囲）
                if (Number(state.parameters.thumbnailFileSize.value) < minimum || Number(state.parameters.thumbnailFileSize.value) > maximum) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_MIN_MAX_LENGTH, ['サムネイルファイルの最大ファイルサイズ', minimum, maximum]);
                }

                return true;
            }
        ];

        // （システム管理・組織内管理：ユーザー管理）一括登録ファイル最大ファイルサイズのバリデーション
        const bulkAddUsersFileSizeRules = [
            () => {
                // バリデーション値が取得されていない段階では以降を実行しない
                if (!('bulkAddUsersFileSize' in state.validateValues)) {
                    return;
                }

                // バリデーション値
                const minimum = Number(state.validateValues['bulkAddUsersFileSize'].minimum);
                const maximum = Number(state.validateValues['bulkAddUsersFileSize'].maximum);

                // 必須チェック            
                if (!state.parameters.bulkAddUsersFileSize.value) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REQUIRED, ['一括登録ファイルの最大ファイルサイズ']);
                }

                // 属性チェック（半角数字）
                if (!state.parameters.bulkAddUsersFileSize.value.match(/^[0-9]+$/)) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REGEX, ['一括登録ファイルの最大ファイルサイズ', '半角数字']);
                }

                // 属性チェック（入力可能範囲）
                if (Number(state.parameters.bulkAddUsersFileSize.value) < minimum || Number(state.parameters.bulkAddUsersFileSize.value) > maximum) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_MIN_MAX_LENGTH, ['一括登録ファイルの最大ファイルサイズ', minimum, maximum]);
                }

                return true;
            }
        ];

        // （システム管理：処理依頼管理）返信ファイルファイル拡張子のバリデーション
        const replyFileExtensionRules = [
            () => {
                // ファイル拡張子の文字数を格納する
                const fileExtensionCnt = state.parameters.replyFileExtension.value.join('').length;

                // Setオブジェクトを作成してファイル拡張子の重複を消す
                const setFileExtension = new Set(state.parameters.replyFileExtension.value);

                // 必須チェック            
                if (state.parameters.replyFileExtension.value.length === 0) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REQUIRED, ['返信ファイルのファイル拡張子']);
                }

                // 桁数チェック
                if (fileExtensionCnt > paramMaxLength['replyFileExtension']) {
                    // 返信ファイルファイル拡張子桁数エラーフラグをtrueにする
                    state.isReplyFileExtensionCountError = true;

                    return CommonFunction.replaceArray(
                        MessageConst.E_MAX, ['返信ファイルのファイル拡張子', paramMaxLength['replyFileExtension']]);
                }

                // ファイル拡張子桁数エラーフラグをfalseにする
                state.isReplyFileExtensionCountError = false;

                // ファイル拡張子配列でループ
                for (const key in state.parameters.replyFileExtension.value) {
                    // 属性チェック（半角英数字）
                    if (!state.parameters.replyFileExtension.value[key].match(/^[0-9a-zA-Z]+$/)) {
                        return CommonFunction.replaceArray(
                            MessageConst.E_REGEX, ['返信ファイルのファイル拡張子', '半角英数字']);
                    }
                }

                // 重複チェック
                if (state.parameters.replyFileExtension.value.length !== setFileExtension.size) {
                    // ファイル拡張子とSetオブジェクトの数が等しくない場合、ファイル拡張子が重複している
                    return MessageConst.E_FILE_EXTENSION_DUPLICATE;
                }

                return true;
            }
        ];

        // （システム管理：処理依頼管理）返信ファイル最大ファイルサイズのバリデーション
        const replyFileSizeRules = [
            () => {
                // バリデーション値が取得されていない段階では以降を実行しない
                if (!('replyFileSize' in state.validateValues)) {
                    return;
                }

                // バリデーション値
                const minimum = Number(state.validateValues['replyFileSize'].minimum);
                const maximum = Number(state.validateValues['replyFileSize'].maximum);

                // 必須チェック            
                if (!state.parameters.replyFileSize.value) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REQUIRED, ['返信ファイルの最大ファイルサイズ']);
                }

                // 属性チェック（半角数字）
                if (!state.parameters.replyFileSize.value.match(/^[0-9]+$/)) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REGEX, ['返信ファイルの最大ファイルサイズ', '半角数字']);
                }

                // 属性チェック（入力可能範囲）
                if (Number(state.parameters.replyFileSize.value) < minimum || Number(state.parameters.replyFileSize.value) > maximum) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_MIN_MAX_LENGTH, ['返信ファイルの最大ファイルサイズ', minimum, maximum]);
                }

                return true;
            }
        ];

        // （システム管理：処理依頼管理）返信ファイル最大ファイル数のバリデーション
        const replyFileCountRules = [
            () => {
                // バリデーション値が取得されていない段階では以降を実行しない
                if (!('replyFileCount' in state.validateValues)) {
                    return;
                }

                // バリデーション値
                const minimum = Number(state.validateValues['replyFileCount'].minimum);
                const maximum = Number(state.validateValues['replyFileCount'].maximum);

                // 必須チェック            
                if (!state.parameters.replyFileCount.value) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REQUIRED, ['返信ファイルの最大ファイル数']);
                }

                // 属性チェック（半角数字）
                if (!state.parameters.replyFileCount.value.match(/^[0-9]+$/)) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REGEX, ['返信ファイルの最大ファイル数', '半角数字']);
                }

                // 属性チェック（入力可能範囲）
                if (Number(state.parameters.replyFileCount.value) < minimum || Number(state.parameters.replyFileCount.value) > maximum) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_MIN_MAX_LENGTH, ['返信ファイルの最大ファイル数', minimum, maximum]);
                }

                return true;
            }
        ];

        // （システム管理：年度更新）所属コードファイル最大ファイルサイズのバリデーション
        const affiliationCodeFileSizeRules = [
            () => {
                // バリデーション値が取得されていない段階では以降を実行しない
                if (!('affiliationCodeFileSize' in state.validateValues)) {
                    return;
                }

                // バリデーション値
                const minimum = Number(state.validateValues['affiliationCodeFileSize'].minimum);
                const maximum = Number(state.validateValues['affiliationCodeFileSize'].maximum);

                // 必須チェック
                if (!state.parameters.affiliationCodeFileSize.value) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REQUIRED, ['所属コードファイルの最大ファイルサイズ']);
                }

                // 属性チェック（半角数字）
                if (!state.parameters.affiliationCodeFileSize.value.match(/^[0-9]+$/)) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REGEX, ['所属コードファイルの最大ファイルサイズ', '半角数字']);
                }

                // 属性チェック（入力可能範囲）
                if (Number(state.parameters.affiliationCodeFileSize.value) < minimum || Number(state.parameters.affiliationCodeFileSize.value) > maximum) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_MIN_MAX_LENGTH, ['所属コードファイルの最大ファイルサイズ', minimum, maximum]);
                }

                return true;
            }
        ];

        // （システム管理：年度更新）人事異動データファイル最大ファイルサイズのバリデーション
        const personnelChangeFileSizeRules = [
            () => {
                // バリデーション値が取得されていない段階では以降を実行しない
                if (!('personnelChangeFileSize' in state.validateValues)) {
                    return;
                }

                // バリデーション値
                const minimum = Number(state.validateValues['personnelChangeFileSize'].minimum);
                const maximum = Number(state.validateValues['personnelChangeFileSize'].maximum);

                // 必須チェック
                if (!state.parameters.personnelChangeFileSize.value) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REQUIRED, ['人事異動データファイルの最大ファイルサイズ']);
                }

                // 属性チェック（半角数字）
                if (!state.parameters.personnelChangeFileSize.value.match(/^[0-9]+$/)) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REGEX, ['人事異動データファイルの最大ファイルサイズ', '半角数字']);
                }

                // 属性チェック（入力可能範囲）
                if (Number(state.parameters.personnelChangeFileSize.value) < minimum || Number(state.parameters.personnelChangeFileSize.value) > maximum) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_MIN_MAX_LENGTH, ['人事異動データファイルの最大ファイルサイズ', minimum, maximum]);
                }

                return true;
            }
        ];

        // （システム管理：年度更新）パスワード情報ファイル最大ファイルサイズのバリデーション
        const notificationPasswordFileSizeRules = [
            () => {
                // バリデーション値が取得されていない段階では以降を実行しない
                if (!('notificationPasswordFileSize' in state.validateValues)) {
                    return;
                }

                // バリデーション値
                const minimum = Number(state.validateValues['notificationPasswordFileSize'].minimum);
                const maximum = Number(state.validateValues['notificationPasswordFileSize'].maximum);

                // 必須チェック
                if (!state.parameters.notificationPasswordFileSize.value) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REQUIRED, ['パスワード情報ファイルの最大ファイルサイズ']);
                }

                // 属性チェック（半角数字）
                if (!state.parameters.notificationPasswordFileSize.value.match(/^[0-9]+$/)) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REGEX, ['パスワード情報ファイルの最大ファイルサイズ', '半角数字']);
                }

                // 属性チェック（入力可能範囲）
                if (Number(state.parameters.notificationPasswordFileSize.value) < minimum || Number(state.parameters.notificationPasswordFileSize.value) > maximum) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_MIN_MAX_LENGTH, ['パスワード情報ファイルの最大ファイルサイズ', minimum, maximum]);
                }

                return true;
            }
        ];

        // （システム管理：年度更新）宛名ファイル最大ファイルサイズのバリデーション
        const notificationAddresseeFileSizeRules = [
            () => {
                // バリデーション値が取得されていない段階では以降を実行しない
                if (!('notificationAddresseeFileSize' in state.validateValues)) {
                    return;
                }

                // バリデーション値
                const minimum = Number(state.validateValues['notificationAddresseeFileSize'].minimum);
                const maximum = Number(state.validateValues['notificationAddresseeFileSize'].maximum);

                // 必須チェック
                if (!state.parameters.notificationAddresseeFileSize.value) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REQUIRED, ['宛名ファイルの最大ファイルサイズ']);
                }

                // 属性チェック（半角数字）
                if (!state.parameters.notificationAddresseeFileSize.value.match(/^[0-9]+$/)) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REGEX, ['宛名ファイルの最大ファイルサイズ', '半角数字']);
                }

                // 属性チェック（入力可能範囲）
                if (Number(state.parameters.notificationAddresseeFileSize.value) < minimum || Number(state.parameters.notificationAddresseeFileSize.value) > maximum) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_MIN_MAX_LENGTH, ['宛名ファイルの最大ファイルサイズ', minimum, maximum]);
                }

                return true;
            }
        ];

        // （システム管理：年度更新）雛形ファイル最大ファイルサイズのバリデーション
        const notificationTemplateFileSizeRules = [
            () => {
                // バリデーション値が取得されていない段階では以降を実行しない
                if (!('notificationTemplateFileSize' in state.validateValues)) {
                    return;
                }

                // バリデーション値
                const minimum = Number(state.validateValues['notificationTemplateFileSize'].minimum);
                const maximum = Number(state.validateValues['notificationTemplateFileSize'].maximum);

                // 必須チェック
                if (!state.parameters.notificationTemplateFileSize.value) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REQUIRED, ['雛形ファイルの最大ファイルサイズ']);
                }

                // 属性チェック（半角数字）
                if (!state.parameters.notificationTemplateFileSize.value.match(/^[0-9]+$/)) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REGEX, ['雛形ファイルの最大ファイルサイズ', '半角数字']);
                }

                // 属性チェック（入力可能範囲）
                if (Number(state.parameters.notificationTemplateFileSize.value) < minimum || Number(state.parameters.notificationTemplateFileSize.value) > maximum) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_MIN_MAX_LENGTH, ['雛形ファイルの最大ファイルサイズ', minimum, maximum]);
                }

                return true;
            }
        ];

        // （システム管理：年度更新）基本データファイルタイトルのバリデーション
        const notificationDataTitleRules = [
            () => {
                // 必須チェック
                if (state.parameters.notificationDataTitle.value.length === 0) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REQUIRED, ['基本データファイルのタイトル']);
                }

                // 桁数チェック
                if (state.parameters.notificationDataTitle.value.length > paramMaxLength['notificationDataTitle']) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_MAX, ['基本データファイルのタイトル', paramMaxLength['notificationDataTitle']]);
                }

                // 属性チェック（書式）
                if (!state.parameters.notificationDataTitle.value.match(/\S/)) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_ONLY_BLANK, ['基本データファイルのタイトル']);
                }

                return true;
            }
        ];

        // （システム管理：年度更新）基本データファイルメッセージのバリデーション
        const notificationDataMessageRules = [
            // 必須チェック
            (v: any) => !!v || CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['基本データファイルのメッセージ']),
            // 属性チェック（書式）
            (v: any) => v.match(/\S/) || CommonFunction.replaceArray(MessageConst.E_ONLY_BLANK, ['基本データファイルのメッセージ']),
            // 桁数チェック
            (v: any) => CommonFunction.checkLength(v, paramMaxLength['notificationDataMessage'], '基本データファイルのメッセージ')
        ];

        // （組織内管理：処理依頼管理）申請ファイルファイル拡張子のバリデーション
        const applicationFileExtensionRules = [
            () => {
                // ファイル拡張子の文字数を格納する
                const fileExtensionCnt = state.parameters.applicationFileExtension.value.join('').length;

                // Setオブジェクトを作成してファイル拡張子の重複を消す
                const setFileExtension = new Set(state.parameters.applicationFileExtension.value);

                // 必須チェック            
                if (state.parameters.applicationFileExtension.value.length === 0) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REQUIRED, ['申請ファイルのファイル拡張子']);
                }

                // 桁数チェック
                if (fileExtensionCnt > paramMaxLength['applicationFileExtension']) {
                    // 申請ファイルファイル拡張子桁数エラーフラグをtrueにする
                    state.isApplicationFileExtensionCountError = true;

                    return CommonFunction.replaceArray(
                        MessageConst.E_MAX, ['申請ファイルのファイル拡張子', paramMaxLength['applicationFileExtension']]);
                }

                // ファイル拡張子桁数エラーフラグをfalseにする
                state.isApplicationFileExtensionCountError = false;

                // ファイル拡張子配列でループ
                for (const key in state.parameters.applicationFileExtension.value) {
                    // 属性チェック（半角英数字）
                    if (!state.parameters.applicationFileExtension.value[key].match(/^[0-9a-zA-Z]+$/)) {
                        return CommonFunction.replaceArray(
                            MessageConst.E_REGEX, ['申請ファイルのファイル拡張子', '半角英数字']);
                    }
                }

                // 重複チェック
                if (state.parameters.applicationFileExtension.value.length !== setFileExtension.size) {
                    // ファイル拡張子とSetオブジェクトの数が等しくない場合、ファイル拡張子が重複している
                    return MessageConst.E_FILE_EXTENSION_DUPLICATE;
                }

                return true;
            }
        ];

        // （組織内管理：処理依頼管理）申請ファイル最大ファイルサイズのバリデーション
        const applicationFileSizeRules = [
            () => {
                // バリデーション値が取得されていない段階では以降を実行しない
                if (!('applicationFileSize' in state.validateValues)) {
                    return;
                }

                // バリデーション値
                const minimum = Number(state.validateValues['applicationFileSize'].minimum);
                const maximum = Number(state.validateValues['applicationFileSize'].maximum);

                // 必須チェック            
                if (!state.parameters.applicationFileSize.value) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REQUIRED, ['申請ファイルの最大ファイルサイズ']);
                }

                // 属性チェック（半角数字）
                if (!state.parameters.applicationFileSize.value.match(/^[0-9]+$/)) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REGEX, ['申請ファイルの最大ファイルサイズ', '半角数字']);
                }

                // 属性チェック（入力可能範囲）
                if (Number(state.parameters.applicationFileSize.value) < minimum || Number(state.parameters.applicationFileSize.value) > maximum) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_MIN_MAX_LENGTH, ['申請ファイルの最大ファイルサイズ', minimum, maximum]);
                }

                return true;
            }
        ];

        // （組織内管理：処理依頼管理）添付ファイルファイル拡張子のバリデーション
        const attachFileExtensionRules = [
            () => {
                // ファイル拡張子の文字数を格納する
                const fileExtensionCnt = state.parameters.attachFileExtension.value.join('').length;

                // Setオブジェクトを作成してファイル拡張子の重複を消す
                const setFileExtension = new Set(state.parameters.attachFileExtension.value);

                // 必須チェック            
                if (state.parameters.attachFileExtension.value.length === 0) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REQUIRED, ['添付ファイルのファイル拡張子']);
                }

                // 桁数チェック
                if (fileExtensionCnt > paramMaxLength['attachFileExtension']) {
                    // 添付ファイルファイル拡張子桁数エラーフラグをtrueにする
                    state.isAttachFileExtensionCountError = true;

                    return CommonFunction.replaceArray(
                        MessageConst.E_MAX, ['添付ファイルのファイル拡張子', paramMaxLength['attachFileExtension']]);
                }

                // ファイル拡張子桁数エラーフラグをfalseにする
                state.isAttachFileExtensionCountError = false;

                // ファイル拡張子配列でループ
                for (const key in state.parameters.attachFileExtension.value) {
                    // 属性チェック（半角英数字）
                    if (!state.parameters.attachFileExtension.value[key].match(/^[0-9a-zA-Z]+$/)) {
                        return CommonFunction.replaceArray(
                            MessageConst.E_REGEX, ['添付ファイルのファイル拡張子', '半角英数字']);
                    }
                }

                // 重複チェック
                if (state.parameters.attachFileExtension.value.length !== setFileExtension.size) {
                    // ファイル拡張子とSetオブジェクトの数が等しくない場合、ファイル拡張子が重複している
                    return MessageConst.E_FILE_EXTENSION_DUPLICATE;
                }

                return true;
            }
        ];

        // （組織内管理：処理依頼管理）添付ファイル最大ファイルサイズのバリデーション
        const attachFileSizeRules = [
            () => {
                // バリデーション値が取得されていない段階では以降を実行しない
                if (!('attachFileSize' in state.validateValues)) {
                    return;
                }

                // バリデーション値
                const minimum = Number(state.validateValues['attachFileSize'].minimum);
                const maximum = Number(state.validateValues['attachFileSize'].maximum);

                // 必須チェック            
                if (!state.parameters.attachFileSize.value) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REQUIRED, ['添付ファイルの最大ファイルサイズ']);
                }

                // 属性チェック（半角数字）
                if (!state.parameters.attachFileSize.value.match(/^[0-9]+$/)) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REGEX, ['添付ファイルの最大ファイルサイズ', '半角数字']);
                }

                // 属性チェック（入力可能範囲）
                if (Number(state.parameters.attachFileSize.value) < minimum || Number(state.parameters.attachFileSize.value) > maximum) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_MIN_MAX_LENGTH, ['添付ファイルの最大ファイルサイズ', minimum, maximum]);
                }

                return true;
            }
        ];

        // （組織内管理：処理依頼管理）添付ファイル最大ファイル数のバリデーション
        const attachFileCountRules = [
            () => {
                // バリデーション値が取得されていない段階では以降を実行しない
                if (!('attachFileCount' in state.validateValues)) {
                    return;
                }

                // バリデーション値
                const minimum = Number(state.validateValues['attachFileCount'].minimum);
                const maximum = Number(state.validateValues['attachFileCount'].maximum);

                // 必須チェック            
                if (!state.parameters.attachFileCount.value) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REQUIRED, ['添付ファイルの最大ファイル数']);
                }

                // 属性チェック（半角数字）
                if (!state.parameters.attachFileCount.value.match(/^[0-9]+$/)) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REGEX, ['添付ファイルの最大ファイル数', '半角数字']);
                }

                // 属性チェック（入力可能範囲）
                if (Number(state.parameters.attachFileCount.value) < minimum || Number(state.parameters.attachFileCount.value) > maximum) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_MIN_MAX_LENGTH, ['添付ファイルの最大ファイル数', minimum, maximum]);
                }

                return true;
            }
        ];

        // （共通（システム管理・組織内管理）：パラメータ設定）メールエイリアス削除日数のバリデーション
        const deleteDayRules = [
            () => {
                // バリデーション値が取得されていない段階では以降を実行しない
                if (!('deleteDays' in state.validateValues)) {
                    return;
                }

                // バリデーション値
                const minimum = Number(state.validateValues['deleteDays'].minimum);
                const maximum = Number(state.validateValues['deleteDays'].maximum);

                // 必須チェック            
                if (!state.parameters.deleteDays.value) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REQUIRED, ['メールエイリアス削除日数']);
                }

                // 属性チェック（半角数字）
                if (!state.parameters.deleteDays.value.match(/^[0-9]+$/)) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REGEX, ['メールエイリアス削除日数', '半角数字']);
                }

                // 属性チェック（入力可能範囲）
                if (Number(state.parameters.deleteDays.value) < minimum || Number(state.parameters.deleteDays.value) > maximum) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_MIN_MAX_LENGTH, ['メールエイリアス削除日数', minimum, maximum]);
                }

                // メールアドレス凍結日数のどちらも入力されており、数値のみか
                if (!!state.parameters.teacherMailReuseDay.value?.trim()
                    &&
                    !!state.parameters.studentGeneralMailReuseDay.value?.trim()
                    &&
                    !isNaN(Number(state.parameters.teacherMailReuseDay.value))
                    &&
                    !isNaN(Number(state.parameters.studentGeneralMailReuseDay.value))) {
                    // 入力されており、数値のみである場合
                    // 属性チェック（メールアドレス凍結日数以下か）
                    if (Number(state.parameters.deleteDays.value) > Number(state.parameters.teacherMailReuseDay.value)
                        ||
                        Number(state.parameters.deleteDays.value) > Number(state.parameters.studentGeneralMailReuseDay.value)) {
                        // メールアドレス凍結日数で小さい方の値をメッセージに設定する
                        if (Number(state.parameters.teacherMailReuseDay.value) < Number(state.parameters.studentGeneralMailReuseDay.value)) {
                            return CommonFunction.replaceArray(
                                MessageConst.E_MAX_LENGTH, [
                                    'メールエイリアス削除日数',
                                    state.parameters.teacherMailReuseDay.value + '（メールアドレス凍結日数）'
                                ]);
                        } else {
                            return CommonFunction.replaceArray(
                                MessageConst.E_MAX_LENGTH, [
                                    'メールエイリアス削除日数',
                                    state.parameters.studentGeneralMailReuseDay.value + '（メールアドレス凍結日数）'
                                ]);
                        }
                    }
                }

                return true;
            }
        ];

        // （共通（システム管理・組織内管理）：パラメータ設定）a1、c1、s1アカウントメールアドレス凍結日数のバリデーション
        const teacherMailReuseDayRules = [
            () => {
                // バリデーション値が取得されていない段階では以降を実行しない
                if (!('teacherMailReuseDay' in state.validateValues)) {
                    return;
                }

                // バリデーション値
                const minimum = Number(state.validateValues['teacherMailReuseDay'].minimum);
                const maximum = Number(state.validateValues['teacherMailReuseDay'].maximum);

                // 必須チェック            
                if (!state.parameters.teacherMailReuseDay.value) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REQUIRED, ['メールアドレス凍結日数']);
                }

                // 属性チェック（半角数字）
                if (!state.parameters.teacherMailReuseDay.value.match(/^[0-9]+$/)) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REGEX, ['メールアドレス凍結日数', '半角数字']);
                }

                // 属性チェック（入力可能範囲）
                // メールエイリアス削除日数が入力されおり、数値のみか
                if (!!state.parameters.deleteDays.value?.trim()
                    &&
                    !isNaN(Number(state.parameters.deleteDays.value))) {
                    // 入力されており、数字のみである場合、最小値はメールエイリアス削除日数でチェックする
                    if (Number(state.parameters.teacherMailReuseDay.value) < Number(state.parameters.deleteDays.value)
                        ||
                        Number(state.parameters.teacherMailReuseDay.value) > maximum
                    ) {
                        return CommonFunction.replaceArray(
                            MessageConst.E_MIN_MAX_PARAM, [
                                'メールアドレス凍結日数',
                                Number(state.parameters.deleteDays.value) + '（メールエイリアス削除日数）',
                                maximum
                            ]);
                    }
                } else {
                    // 入力されていないか、数値ではない場合、最小値はパラメータマスタのバリデーション値でチェックする
                    if (Number(state.parameters.teacherMailReuseDay.value) < minimum
                        ||
                        Number(state.parameters.teacherMailReuseDay.value) > maximum
                    ) {
                        return CommonFunction.replaceArray(
                            MessageConst.E_MIN_MAX_PARAM, [
                                'メールアドレス凍結日数',
                                minimum,
                                maximum
                            ]);
                    }
                }

                return true;
            }
        ];

        // （共通（システム管理・組織内管理）：パラメータ設定）b1、b2アカウントメールアドレス凍結日数のバリデーション
        const studentGeneralMailReuseDayRules = [
            () => {
                // バリデーション値が取得されていない段階では以降を実行しない
                if (!('studentGeneralMailReuseDay' in state.validateValues)) {
                    return;
                }

                // バリデーション値
                const minimum = Number(state.validateValues['studentGeneralMailReuseDay'].minimum);
                const maximum = Number(state.validateValues['studentGeneralMailReuseDay'].maximum);

                // 必須チェック            
                if (!state.parameters.studentGeneralMailReuseDay.value) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REQUIRED, ['メールアドレス凍結日数']);
                }

                // 属性チェック（半角数字）
                if (!state.parameters.studentGeneralMailReuseDay.value.match(/^[0-9]+$/)) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_REGEX, ['メールアドレス凍結日数', '半角数字']);
                }

                // 属性チェック（入力可能範囲）
                // メールエイリアス削除日数が入力されており、数値のみか
                if (!!state.parameters.deleteDays.value?.trim()
                    &&
                    !isNaN(Number(state.parameters.deleteDays.value))) {
                    // 入力されており、数値のみである場合、最小値はメールエイリアス削除日数でチェックする
                    if (Number(state.parameters.studentGeneralMailReuseDay.value) < Number(state.parameters.deleteDays.value)
                        ||
                        Number(state.parameters.studentGeneralMailReuseDay.value) > maximum
                    ) {
                        return CommonFunction.replaceArray(
                            MessageConst.E_MIN_MAX_PARAM, [
                                'メールアドレス凍結日数',
                                Number(state.parameters.deleteDays.value) + '（メールエイリアス削除日数）',
                                maximum
                            ]);
                    }
                } else {
                    // 入力されていないか、数値ではない場合、最小値はパラメータマスタのバリデーション値でチェックする
                    if (Number(state.parameters.studentGeneralMailReuseDay.value) < minimum
                        ||
                        Number(state.parameters.studentGeneralMailReuseDay.value) > maximum
                    ) {
                        return CommonFunction.replaceArray(
                            MessageConst.E_MIN_MAX_PARAM, [
                                'メールアドレス凍結日数',
                                minimum,
                                maximum
                            ]);
                    }
                }

                return true;
            }
        ];

        // メール通知定型文のバリデーション
        const mailTextRules = [
            // 必須チェック
            (v: any) => !!v || CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['メール通知定型文']),
            // 属性チェック（書式）
            (v: any) => v.match(/\S/) || CommonFunction.replaceArray(MessageConst.E_ONLY_BLANK, ['メール通知定型文']),
            // 桁数チェック
            (v: any) => CommonFunction.checkLength(v, paramMaxLength['mailText'], 'メール通知定型文')
        ];

        // カテゴリ設定（表示順）のバリデーション
        const displayOrderRules = [
            () => {
                // カテゴリ一覧情報でループ
                for (const key in state.categoryListItems) {
                    // 処理フラグが「削除」ではない場合
                    if (state.categoryListItems[key].processCode !== processCode['DELETE']) {
                        // 必須チェック
                        if (!state.categoryListItems[key].displayOrder) {
                            // 0、null、undefinedの場合エラー
                            errors.push(
                                CommonFunction.replaceArray(
                                    MessageConst.E_VALIDATE_INVALID_VALUE, [
                                        Number(key) + 1 + '行目',
                                        '表示順'
                                    ]
                                )
                            );
                        }
                    }
                }
            }
        ];

        // カテゴリ設定（組織ID）のバリデーション
        // 発番カテゴリ設定の組織名称バリデーションで使用
        const categoryIdRules = [
            () => {
                // 処理フラグが「削除」を除いたカテゴリ一覧配列を作成
                const categoryList = state.categoryListItems.filter(function(item){
                    return item.processCode !== processCode['DELETE'];
                });

                // 組織IDの配列を作成
                const categoryIdList = categoryList.map(item => item['categoryId']);

                // カテゴリ一覧情報でループ
                for (const key in state.categoryListItems) {
                    // 処理フラグが「新規」の場合
                    if (state.categoryListItems[key].processCode === processCode['STORE']) {
                        // 必須チェック
                        if (state.categoryListItems[key].categoryId === null || state.categoryListItems[key].categoryId === '') {
                            // nullまたは空文字の場合エラー
                            errors.push(
                                CommonFunction.replaceArray(
                                    MessageConst.E_VALIDATE_REQUIRED, [
                                        state.categoryListItems[key].displayOrder,
                                        '組織名称'
                                    ]
                                )
                            );
                        } else {
                            // 今回のループの組織IDがいくつ存在するか数える
                            const categoryIdCnt = categoryIdList.filter(function(value){
                                return value === state.categoryListItems[key].categoryId
                            }).length;

                            // 重複チェック
                            if (categoryIdCnt > 1) {
                                // 2つ以上同じ組織IDが存在する場合エラー
                                errors.push(
                                    CommonFunction.replaceArray(
                                        MessageConst.E_VALIDATE_DUPLICATE, [
                                            state.categoryListItems[key].displayOrder,
                                            '組織名称'
                                        ]
                                    )
                                );
                            }
                        }
                    }
                }
            }
        ];

        // カテゴリ設定（発番接頭辞）のバリデーション
        const numberPrefixRules = [
            () => {
                // 処理フラグが「削除」を除いたカテゴリ一覧配列を作成
                const categoryList = state.categoryListItems.filter(function(item){
                    return item.processCode !== processCode['DELETE'];
                });

                // 発番接頭辞の配列を作成
                const numberPrefixList = categoryList.map(item => item['numberPrefix']);

                // カテゴリ一覧情報でループ
                for (const key in state.categoryListItems) {
                    // チェックする発番接頭辞を保存
                    const numberPrefix = state.categoryListItems[key].numberPrefix;

                    // 発番接頭辞が定義されている場合
                    if (typeof numberPrefix === 'string') {
                        // 処理フラグが「削除」ではない場合
                        if (state.categoryListItems[key].processCode !== processCode['DELETE']) {
                            // 必須チェック
                            if (numberPrefix === null || numberPrefix === '') {
                                // nullまたは空文字の場合エラー
                                errors.push(
                                    CommonFunction.replaceArray(
                                        MessageConst.E_VALIDATE_REQUIRED, [
                                            state.categoryListItems[key].displayOrder,
                                            '発番接頭辞'
                                        ]
                                    )
                                );
                            } else if (!numberPrefix.match(/\S/)) {
                                // 属性チェック（書式）
                                // 半角・全角スペースのみである場合エラー
                                errors.push(
                                    CommonFunction.replaceArray(
                                        MessageConst.E_VALIDATE_ONLY_SPACE, [
                                            state.categoryListItems[key].displayOrder,
                                            '発番接頭辞'
                                        ]
                                    )
                                );
                            } else {
                                // 今回のループの発番接頭辞がいくつ存在するか数える
                                const numberPrefixCnt = numberPrefixList.filter(function(value){
                                    return value === numberPrefix
                                }).length;

                                // 重複チェック
                                if (numberPrefixCnt > 1) {
                                    // 2つ以上同じ発番接頭辞が存在する場合エラー
                                    errors.push(
                                        CommonFunction.replaceArray(
                                            MessageConst.E_VALIDATE_DUPLICATE, [
                                                state.categoryListItems[key].displayOrder,
                                                '発番接頭辞'
                                            ]
                                        )
                                    );
                                }
                            }

                            // 桁数チェック
                            if (String(numberPrefix).length > categoryMaxLength['numberPrefix']) {
                                errors.push(
                                    CommonFunction.replaceArray(
                                        MessageConst.E_VALIDATE_MAX, [
                                            state.categoryListItems[key].displayOrder,
                                            '発番接頭辞',
                                            categoryMaxLength['numberPrefix']
                                        ]
                                    )
                                );
                            }
                        }
                    }
                }
            }
        ];

        // カテゴリ設定（各種名称）のバリデーション
        const categoryNameRules = [
            () => {
                // 処理フラグが「削除」を除いたカテゴリ一覧配列を作成
                const categoryList = state.categoryListItems.filter(function(item){
                    return item.processCode !== processCode['DELETE'];
                });

                // 各種名称の配列を作成
                const categoryNameList = categoryList.map(item => item['categoryName']);

                // カテゴリ一覧情報でループ
                for (const key in state.categoryListItems) {
                    // 処理フラグが「削除」ではない場合
                    if (state.categoryListItems[key].processCode !== processCode['DELETE']) {
                        // 公文書の発番カテゴリ設定である場合
                        // 属性チェック（書式）
                        if (state.selectFunctionPulldown === selectFunctionPulldownItems['OFFICIAL_DOCUMENTS'] &&
                            state.selectTab === tabLists['SECOND']) {
                            if (state.categoryListItems[key].categoryName !== null
                                &&
                                state.categoryListItems[key].categoryName !== ''
                                &&
                                !state.categoryListItems[key].categoryName.match(/\S/)
                            ) {
                                // カテゴリ名称が入力されているかつ
                                // 半角・全角スペースのみである場合エラー
                                errors.push(
                                    CommonFunction.replaceArray(
                                        MessageConst.E_VALIDATE_ONLY_SPACE, [
                                            state.categoryListItems[key].displayOrder,
                                            'カテゴリ名称'
                                        ]
                                    )
                                );
                            }
                        }

                        // 公文書のメール通知先設定である場合
                        if (state.selectFunctionPulldown === selectFunctionPulldownItems['OFFICIAL_DOCUMENTS'] &&
                            state.selectTab === tabLists['THIRD']) {
                            if (state.categoryListItems[key].categoryName === null || state.categoryListItems[key].categoryName === '') {
                                // 必須チェック
                                // nullまたは空文字の場合エラー
                                errors.push(
                                    CommonFunction.replaceArray(
                                        MessageConst.E_VALIDATE_REQUIRED, [
                                            state.categoryListItems[key].displayOrder,
                                            'メール通知先名称'
                                        ]
                                    )
                                );
                            } else if (!state.categoryListItems[key].categoryName.match(/\S/)) {
                                // メール通知先名称が入力されている場合
                                // 属性チェック（書式）
                                // 半角・全角スペースのみである場合エラー
                                errors.push(
                                    CommonFunction.replaceArray(
                                        MessageConst.E_VALIDATE_ONLY_SPACE, [
                                            state.categoryListItems[key].displayOrder,
                                            'メール通知先名称'
                                        ]
                                    )
                                );
                            }
                        } else if (state.selectFunctionPulldown === selectFunctionPulldownItems['MATERIAL_DB'] &&
                            state.selectTab === tabLists['SECOND']) {
                            // 教材DBの校種設定である場合
                            if (state.categoryListItems[key].categoryName === null || state.categoryListItems[key].categoryName === '') {
                                // 必須チェック
                                // nullまたは空文字の場合エラー
                                errors.push(
                                    CommonFunction.replaceArray(
                                        MessageConst.E_VALIDATE_REQUIRED, [
                                            state.categoryListItems[key].displayOrder,
                                            '校種名称'
                                        ]
                                    )
                                );
                            } else if (!state.categoryListItems[key].categoryName.match(/\S/)) {
                                // 校種名称が入力されている場合
                                // 属性チェック（書式）
                                // 半角・全角スペースのみである場合エラー
                                errors.push(
                                    CommonFunction.replaceArray(
                                        MessageConst.E_VALIDATE_ONLY_SPACE, [
                                            state.categoryListItems[key].displayOrder,
                                            '校種名称'
                                        ]
                                    )
                                );
                            }
                        } else if (state.selectFunctionPulldown === selectFunctionPulldownItems['MATERIAL_DB'] &&
                            state.selectTab === tabLists['THIRD']) {
                            // 教材DBの教科設定である場合
                            if (state.categoryListItems[key].categoryName === null || state.categoryListItems[key].categoryName === '') {
                                // 必須チェック
                                // nullまたは空文字の場合エラー
                                errors.push(
                                    CommonFunction.replaceArray(
                                        MessageConst.E_VALIDATE_REQUIRED, [
                                            state.categoryListItems[key].displayOrder,
                                            '教科名称'
                                        ]
                                    )
                                );
                            } else if (!state.categoryListItems[key].categoryName.match(/\S/)) {
                                // 教科名称が入力されている場合
                                // 属性チェック（書式）
                                // 半角・全角スペースのみである場合エラー
                                errors.push(
                                    CommonFunction.replaceArray(
                                        MessageConst.E_VALIDATE_ONLY_SPACE, [
                                            state.categoryListItems[key].displayOrder,
                                            '教科名称'
                                        ]
                                    )
                                );
                            }
                        } else if (state.selectFunctionPulldown === selectFunctionPulldownItems['MATERIAL_DB'] &&
                            state.selectTab === tabLists['FOURTH']) {
                            // 教材DBの科目設定である場合
                            if (state.categoryListItems[key].categoryName === null || state.categoryListItems[key].categoryName === '') {
                                // 必須チェック
                                // nullまたは空文字の場合エラー
                                errors.push(
                                    CommonFunction.replaceArray(
                                        MessageConst.E_VALIDATE_REQUIRED, [
                                            state.categoryListItems[key].displayOrder,
                                            '科目名称'
                                        ]
                                    )
                                );
                            } else if (!state.categoryListItems[key].categoryName.match(/\S/)) {
                                // 科目名称が入力されている場合
                                // 属性チェック（書式）
                                // 半角・全角スペースのみである場合エラー
                                errors.push(
                                    CommonFunction.replaceArray(
                                        MessageConst.E_VALIDATE_ONLY_SPACE, [
                                            state.categoryListItems[key].displayOrder,
                                            '科目名称'
                                        ]
                                    )
                                );
                            }
                        } else if (state.selectFunctionPulldown === selectFunctionPulldownItems['CONTENT'] &&
                            state.selectTab === tabLists['SECOND']) {
                            // 教育用コンテンツのコンテンツカテゴリ設定である場合
                            if (state.categoryListItems[key].categoryName === null || state.categoryListItems[key].categoryName === '') {
                                // 必須チェック
                                // nullまたは空文字の場合エラー
                                errors.push(
                                    CommonFunction.replaceArray(
                                        MessageConst.E_VALIDATE_REQUIRED, [
                                            state.categoryListItems[key].displayOrder,
                                            'カテゴリ名称'
                                        ]
                                    )
                                );
                            } else if (!state.categoryListItems[key].categoryName.match(/\S/)) {
                                // カテゴリ名称が入力されている場合
                                // 属性チェック（書式）
                                // 半角・全角スペースのみである場合エラー
                                errors.push(
                                    CommonFunction.replaceArray(
                                        MessageConst.E_VALIDATE_ONLY_SPACE, [
                                            state.categoryListItems[key].displayOrder,
                                            'カテゴリ名称'
                                        ]
                                    )
                                );
                            }
                        } else if (state.selectFunctionPulldown === selectFunctionPulldownItems['ORG_ADMIN'] &&
                            state.selectTab === tabLists['SECOND']) {
                            // 組織内管理の処理依頼内容設定である場合
                            if (state.categoryListItems[key].categoryName === null || state.categoryListItems[key].categoryName === '') {
                                // 必須チェック
                                // nullまたは空文字の場合エラー
                                errors.push(
                                    CommonFunction.replaceArray(
                                        MessageConst.E_VALIDATE_REQUIRED, [
                                            state.categoryListItems[key].displayOrder,
                                            '依頼内容名称'
                                        ]
                                    )
                                );
                            } else if (!state.categoryListItems[key].categoryName.match(/\S/)) {
                                // 依頼内容名称が入力されている場合
                                // 属性チェック（書式）
                                // 半角・全角スペースのみである場合エラー
                                errors.push(
                                    CommonFunction.replaceArray(
                                        MessageConst.E_VALIDATE_ONLY_SPACE, [
                                            state.categoryListItems[key].displayOrder,
                                            '依頼内容名称'
                                        ]
                                    )
                                );
                            }
                        }

                        // 桁数チェック
                        // 公文書の発番カテゴリ設定である場合
                        if (state.selectFunctionPulldown === selectFunctionPulldownItems['OFFICIAL_DOCUMENTS'] &&
                            state.selectTab === tabLists['SECOND']) {
                            if (String(state.categoryListItems[key].categoryName).length > categoryMaxLength['numberPrefixCategoryName']) {
                                errors.push(
                                    CommonFunction.replaceArray(
                                        MessageConst.E_VALIDATE_MAX, [
                                            state.categoryListItems[key].displayOrder,
                                            'カテゴリ名称',
                                            categoryMaxLength['numberPrefixCategoryName']
                                        ]
                                    )
                                );
                            }
                        } else if (state.selectFunctionPulldown === selectFunctionPulldownItems['OFFICIAL_DOCUMENTS'] &&
                            state.selectTab === tabLists['THIRD']) {
                            // 公文書のメール通知先設定である場合
                            if (String(state.categoryListItems[key].categoryName).length > categoryMaxLength['notificationName']) {
                                errors.push(
                                    CommonFunction.replaceArray(
                                        MessageConst.E_VALIDATE_MAX, [
                                            state.categoryListItems[key].displayOrder,
                                            'メール通知先名称',
                                            categoryMaxLength['notificationName']
                                        ]
                                    )
                                );
                            }
                        } else if (state.selectFunctionPulldown === selectFunctionPulldownItems['MATERIAL_DB'] &&
                            state.selectTab === tabLists['SECOND']) {
                            // 教材DBの校種設定である場合
                            if (String(state.categoryListItems[key].categoryName).length > categoryMaxLength['categoryName']) {
                                errors.push(
                                    CommonFunction.replaceArray(
                                        MessageConst.E_VALIDATE_MAX, [
                                            state.categoryListItems[key].displayOrder,
                                            '校種名称',
                                            categoryMaxLength['categoryName']
                                        ]
                                    )
                                );
                            }
                        } else if (state.selectFunctionPulldown === selectFunctionPulldownItems['MATERIAL_DB'] &&
                            state.selectTab === tabLists['THIRD']) {
                            // 教材DBの教科設定である場合
                            if (String(state.categoryListItems[key].categoryName).length > categoryMaxLength['categoryName']) {
                                errors.push(
                                    CommonFunction.replaceArray(
                                        MessageConst.E_VALIDATE_MAX, [
                                            state.categoryListItems[key].displayOrder,
                                            '教科名称',
                                            categoryMaxLength['categoryName']
                                        ]
                                    )
                                );
                            }
                        } else if (state.selectFunctionPulldown === selectFunctionPulldownItems['MATERIAL_DB'] &&
                            state.selectTab === tabLists['FOURTH']) {
                            // 教材DBの科目設定である場合
                            if (String(state.categoryListItems[key].categoryName).length > categoryMaxLength['categoryName']) {
                                errors.push(
                                    CommonFunction.replaceArray(
                                        MessageConst.E_VALIDATE_MAX, [
                                            state.categoryListItems[key].displayOrder,
                                            '科目名称',
                                            categoryMaxLength['categoryName']
                                        ]
                                    )
                                );
                            }
                        } else if (state.selectFunctionPulldown === selectFunctionPulldownItems['CONTENT'] &&
                            state.selectTab === tabLists['SECOND']) {
                            // 教育用コンテンツのコンテンツカテゴリ設定である場合
                            if (String(state.categoryListItems[key].categoryName).length > categoryMaxLength['categoryName']) {
                                errors.push(
                                    CommonFunction.replaceArray(
                                        MessageConst.E_VALIDATE_MAX, [
                                            state.categoryListItems[key].displayOrder,
                                            'カテゴリ名称',
                                            categoryMaxLength['categoryName']
                                        ]
                                    )
                                );
                            }
                        } else if (state.selectFunctionPulldown === selectFunctionPulldownItems['ORG_ADMIN'] &&
                            state.selectTab === tabLists['SECOND']) {
                            // 組織内管理の処理依頼内容設定である場合
                            if (String(state.categoryListItems[key].categoryName).length > categoryMaxLength['categoryName']) {
                                errors.push(
                                    CommonFunction.replaceArray(
                                        MessageConst.E_VALIDATE_MAX, [
                                            state.categoryListItems[key].displayOrder,
                                            '依頼内容名称',
                                            categoryMaxLength['categoryName']
                                        ]
                                    )
                                );
                            }
                        }

                        // 各種名称が入力されている場合
                        if (state.categoryListItems[key].categoryName !== null && state.categoryListItems[key].categoryName !== '') {
                            // 今回のループの各種名称がいくつ存在するか数える
                            const categoryNameCnt = categoryNameList.filter(function(value){
                                return value === state.categoryListItems[key].categoryName
                            }).length;

                            // 重複チェック
                            if (categoryNameCnt > 1) {
                                // 2つ以上同じ名称が存在する場合エラー
                                // 公文書の発番カテゴリ設定である場合
                                if (state.selectFunctionPulldown === selectFunctionPulldownItems['OFFICIAL_DOCUMENTS'] &&
                                    state.selectTab === tabLists['SECOND']) {
                                        errors.push(
                                            CommonFunction.replaceArray(
                                                MessageConst.E_VALIDATE_DUPLICATE, [
                                                    state.categoryListItems[key].displayOrder,
                                                    'カテゴリ名称'
                                                ]
                                            )
                                    );
                                } else if (state.selectFunctionPulldown === selectFunctionPulldownItems['OFFICIAL_DOCUMENTS'] &&
                                    state.selectTab === tabLists['THIRD']) {
                                    // 公文書のメール通知先設定である場合
                                    errors.push(
                                        CommonFunction.replaceArray(
                                            MessageConst.E_VALIDATE_DUPLICATE, [
                                                state.categoryListItems[key].displayOrder,
                                                'メール通知先名称'
                                            ]
                                        )
                                    );
                                } else if (state.selectFunctionPulldown === selectFunctionPulldownItems['MATERIAL_DB'] &&
                                    state.selectTab === tabLists['SECOND']) {
                                    // 教材DBの校種設定である場合
                                    errors.push(
                                        CommonFunction.replaceArray(
                                            MessageConst.E_VALIDATE_DUPLICATE, [
                                                state.categoryListItems[key].displayOrder,
                                                '校種名称'
                                            ]
                                        )
                                    );
                                } else if (state.selectFunctionPulldown === selectFunctionPulldownItems['MATERIAL_DB'] &&
                                    state.selectTab === tabLists['THIRD']) {
                                    // 教材DBの教科設定である場合
                                    errors.push(
                                        CommonFunction.replaceArray(
                                            MessageConst.E_VALIDATE_DUPLICATE, [
                                                state.categoryListItems[key].displayOrder,
                                                '教科名称'
                                            ]
                                        )
                                    );
                                } else if (state.selectFunctionPulldown === selectFunctionPulldownItems['MATERIAL_DB'] &&
                                    state.selectTab === tabLists['FOURTH']) {
                                    // 教材DBの科目設定である場合
                                    errors.push(
                                        CommonFunction.replaceArray(
                                            MessageConst.E_VALIDATE_DUPLICATE, [
                                                state.categoryListItems[key].displayOrder,
                                                '科目名称'
                                            ]
                                        )
                                    );
                                } else if (state.selectFunctionPulldown === selectFunctionPulldownItems['CONTENT'] &&
                                    state.selectTab === tabLists['SECOND']) {
                                    // 教育用コンテンツのコンテンツカテゴリ設定である場合
                                    errors.push(
                                        CommonFunction.replaceArray(
                                            MessageConst.E_VALIDATE_DUPLICATE, [
                                                state.categoryListItems[key].displayOrder,
                                                'カテゴリ名称'
                                            ]
                                        )
                                    );
                                } else if (state.selectFunctionPulldown === selectFunctionPulldownItems['ORG_ADMIN'] &&
                                    state.selectTab === tabLists['SECOND']) {
                                    // 組織内管理の処理依頼内容設定である場合
                                    errors.push(
                                        CommonFunction.replaceArray(
                                            MessageConst.E_VALIDATE_DUPLICATE, [
                                                state.categoryListItems[key].displayOrder,
                                                '依頼内容名称'
                                            ]
                                        )
                                    );
                                }
                            }
                        }
                    }
                }
            }
        ];

        // カテゴリ設定（部門名）のバリデーション
        const departmentNameRules = [
            () => {
                // カテゴリ一覧情報でループ
                for (const key in state.categoryListItems) {
                    // チェックする部門名を保存
                    const departmentName = state.categoryListItems[key].departmentName;

                    // 部門名が定義されている場合
                    if (typeof departmentName === 'string') {
                        // 処理フラグが「削除」ではない場合
                        if (state.categoryListItems[key].processCode !== processCode['DELETE']) {
                            // 属性チェック（書式）
                            if (departmentName !== null && departmentName !== '' && !departmentName.match(/\S/)) {
                                // 部門名が入力されているかつ
                                // 半角・全角スペースのみである場合エラー
                                errors.push(
                                    CommonFunction.replaceArray(
                                        MessageConst.E_VALIDATE_ONLY_SPACE, [
                                            state.categoryListItems[key].displayOrder,
                                            '部門名'
                                        ]
                                    )
                                );
                            }

                            // 桁数チェック
                            if (String(departmentName).length > categoryMaxLength['departmentName']) {
                                errors.push(
                                    CommonFunction.replaceArray(
                                        MessageConst.E_VALIDATE_MAX, [
                                            state.categoryListItems[key].displayOrder,
                                            '部門名',
                                            categoryMaxLength['departmentName']
                                        ]
                                    )
                                );
                            }
                        }
                    }
                }
            }
        ];

        // カテゴリ設定（電話番号）のバリデーション
        const telRules = [
            () => {
                // カテゴリ一覧情報でループ
                for (const key in state.categoryListItems) {
                    // 処理フラグが「削除」ではない場合
                    if (state.categoryListItems[key].processCode !== processCode['DELETE']) {
                        // 桁数チェック
                        if (String(state.categoryListItems[key].tel).length > categoryMaxLength['tel']) {
                            errors.push(
                                CommonFunction.replaceArray(
                                    MessageConst.E_VALIDATE_MAX, [
                                        state.categoryListItems[key].displayOrder,
                                        '電話番号',
                                        categoryMaxLength['tel']
                                    ]
                                )
                            );
                        }

                        // 電話番号が入力されている場合
                        if (state.categoryListItems[key].tel !== null && state.categoryListItems[key].tel !== '') {
                            // 属性チェック（書式）
                            if (!String(state.categoryListItems[key].tel).match(/^[0-9-]+$/)) {
                                errors.push(
                                    CommonFunction.replaceArray(
                                        MessageConst.E_VALIDATE_REGEX, [
                                            state.categoryListItems[key].displayOrder,
                                            '電話番号',
                                            '数字とハイフン（-）のみ'
                                        ]
                                    )
                                );
                            }
    
                            // 属性チェック（数字のみまたは（数字）-（数字）-（数字）の形式であるかのチェック）
                            if (!String(state.categoryListItems[key].tel).match(/^[0-9]+$|^[0-9]+-[0-9]+-[0-9]+$/)) {
                                errors.push(
                                    CommonFunction.replaceArray(
                                        MessageConst.E_VALIDATE_REGEX, [
                                            state.categoryListItems[key].displayOrder,
                                            '電話番号',
                                            '数字または（数字）-（数字）-（数字）の形式'
                                        ]
                                    )
                                );
                            }
                        }
                    }
                }
            }
        ];

        // カテゴリ設定（管理者のみ表示）のバリデーション
        const isAdminOnlyRules = [
            () => {
                // カテゴリ一覧情報でループ
                for (const key in state.categoryListItems) {
                    // 処理フラグが「削除」ではない場合
                    if (state.categoryListItems[key].processCode !== processCode['DELETE']) {
                        // 必須チェック
                        if (state.categoryListItems[key].isAdminOnly !== '0' &&
                            state.categoryListItems[key].isAdminOnly !== '1') {
                            // 文字列の0か1ではない場合エラー
                            errors.push(
                                CommonFunction.replaceArray(
                                    MessageConst.E_VALIDATE_INVALID_VALUE, [
                                        state.categoryListItems[key].displayOrder,
                                        '管理者のみ表示'
                                    ]
                                )
                            );
                        }
                    }
                }
            }
        ];

        // カテゴリ設定（メールアドレス）のバリデーション
        const mailRules = [
            () => {
                // 処理フラグが「削除」を除いたカテゴリ一覧配列を作成
                const categoryList = state.categoryListItems.filter(function(item){
                    return item.processCode !== processCode['DELETE'];
                });

                // メールアドレスの配列を作成
                const mailList = categoryList.map(item => item['mail']);

                // カテゴリ一覧情報でループ
                for (const key in state.categoryListItems) {
                    // 処理フラグが「削除」ではない場合
                    if (state.categoryListItems[key].processCode !== processCode['DELETE']) {
                        // 必須チェック
                        if (state.categoryListItems[key].mail === null || state.categoryListItems[key].mail === '') {
                            // nullまたは空文字の場合エラー
                            errors.push(
                                CommonFunction.replaceArray(
                                    MessageConst.E_VALIDATE_REQUIRED, [
                                        state.categoryListItems[key].displayOrder,
                                        'メールアドレス'
                                    ]
                                )
                            );
                        } else {
                            // 属性チェック（使用可能文字）
                            if (!String(state.categoryListItems[key].mail).match(/^[0-9a-z-_'.@]*$/)) {
                                errors.push(
                                    CommonFunction.replaceArray(
                                        MessageConst.E_VALIDATE_REGEX, [
                                            state.categoryListItems[key].displayOrder,
                                            'メールアドレス',
                                            '「小文字の英数字」「ピリオド（.）」「ハイフン（-）」「アンダースコア（_）」「アポストロフィ（\'）」「アットマーク（@）」のみ'
                                        ]
                                    )
                                );
                            }

                            // 属性チェック（メールアドレス形式）
                            const resultCheckMail = EmailValidator.validate(String(state.categoryListItems[key].mail)) ||
                                CommonFunction.replaceArray(
                                    MessageConst.E_VALIDATE_EMAIL,[
                                        state.categoryListItems[key].displayOrder,
                                        'メールアドレス'
                                    ]
                                );
                            if (typeof resultCheckMail === 'string') {
                                // エラーメッセージが返ってきた場合
                                errors.push(resultCheckMail);
                            }

                            // 属性チェック（末尾のハイフン）
                            if (String(state.categoryListItems[key].mail).match(/-$/)) {
                                errors.push(
                                    CommonFunction.replaceArray(
                                        MessageConst.E_VALIDATE_ADDRESS_END_ERR, [
                                            state.categoryListItems[key].displayOrder,
                                            'メールアドレス',
                                            'ハイフン（-）'
                                        ]
                                    )
                                );
                            }

                            // 今回のループのメールアドレスがいくつ存在するか数える
                            const mailCnt = mailList.filter(function(value){
                                return value === state.categoryListItems[key].mail
                            }).length;

                            // 重複チェック
                            if (mailCnt > 1) {
                                // 2つ以上同じメールアドレスが存在する場合エラー
                                errors.push(
                                    CommonFunction.replaceArray(
                                        MessageConst.E_VALIDATE_DUPLICATE, [
                                            state.categoryListItems[key].displayOrder,
                                            'メールアドレス'
                                        ]
                                    )
                                );
                            }
                        }

                        // 桁数チェック
                        if (String(state.categoryListItems[key].mail).length > categoryMaxLength['mail']) {
                            errors.push(
                                CommonFunction.replaceArray(
                                    MessageConst.E_VALIDATE_MAX, [
                                        state.categoryListItems[key].displayOrder,
                                        'メールアドレス',
                                        categoryMaxLength['mail']
                                    ]
                                )
                            );
                        }
                    }
                }
            }
        ];

        // カテゴリ設定（状態）のバリデーション
        const isEnabledRules = [
            () => {
                // カテゴリ一覧情報でループ
                for (const key in state.categoryListItems) {
                    // 処理フラグが「削除」ではない場合
                    if (state.categoryListItems[key].processCode !== processCode['DELETE']) {
                        // 必須チェック
                        if (state.categoryListItems[key].isEnabled !== '0' &&
                            state.categoryListItems[key].isEnabled !== '1') {
                            // 文字列の0か1ではない場合エラー
                            errors.push(
                                CommonFunction.replaceArray(
                                    MessageConst.E_VALIDATE_INVALID_VALUE, [
                                        state.categoryListItems[key].displayOrder,
                                        '状態'
                                    ]
                                )
                            );
                        }
                    }
                }
            }
        ];

        /**
         * created
         */
        created();
        async function created() {
            // titleIcon変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-database-cog');

            // mainTitle変更
            ctx.root.$store.commit('setMainTitle', 'パラメータ管理');

            // 公文書タブを設定
            state.tabs = documentTabs;

            // 定数定義から公文書のサービスコードを設定
            state.serviceCode = SystemConst.SERVICE_CODE.OFFICIAL_DOCUMENTS;

            // パラメータマスタデータ取得関数を呼び出す
            getParameters();

            // 組織名称/管理者のみ表示/状態プルダウンデータ取得関数を呼び出す
            getPulldownItems();
        }

        /**
         * パラメータマスタデータ取得
         */
        async function getParameters() {
            // パラメータ設定初期表示取得APIでバリデーション値と初期表示データを取得する
            // パラメータ：サービスコード
            const response = await ctx.root.$store.dispatch('parameterSettings/getParameters',
                {
                    serviceCode: state.serviceCode
                }
            );

            // HTTPステータスコードが200以外の場合
            if (response.status !== HttpStatus.OK) {
                // HTTPステータスコード400であればエラーダイアログを表示し、それ以外はエラー画面へ遷移して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // 画面項目に初期表示データを設定する
            switch (state.serviceCode) {
                case SystemConst.SERVICE_CODE.OFFICIAL_DOCUMENTS:
                    // サービスコードが公文書である場合
                    // 新着日数
                    state.parameters.newArrivalDays = response.data.initParams.newArrivalDays;
                    // ファイル拡張子
                    state.parameters.fileExtension= response.data.initParams.fileExtension;
                    // 最大ファイルサイズ
                    state.parameters.fileSize = response.data.initParams.fileSize;
                    // 最大ファイル数
                    state.parameters.fileCount = response.data.initParams.fileCount;
                    // メール通知定型文
                    state.parameters.mailText = response.data.initParams.mailBodyTemplate;
                    break;
                case SystemConst.SERVICE_CODE.INFORMATION:
                    // サービスコードがお知らせである場合
                    // 新着日数
                    state.parameters.newArrivalDays = response.data.initParams.newArrivalDays;
                    // 掲載期間終了日数
                    state.parameters.publishEndDays = response.data.initParams.publishEndDays;
                    // ファイル拡張子
                    state.parameters.fileExtension= response.data.initParams.fileExtension;
                    // 最大ファイルサイズ
                    state.parameters.fileSize = response.data.initParams.fileSize;
                    // 最大ファイル数
                    state.parameters.fileCount = response.data.initParams.fileCount;
                    break;
                case SystemConst.SERVICE_CODE.IMPORTANT_INFORMATION:
                    // サービスコードが重要情報のお知らせである場合
                    // 新着日数
                    state.parameters.newArrivalDays = response.data.initParams.newArrivalDays;
                    // 基本データファイル最大ファイルサイズ
                    state.parameters.basicFileSize = response.data.initParams.basicFileSize;
                    // 添付データファイル最大ファイルサイズ
                    state.parameters.attachmentFileSize = response.data.initParams.attachmentFileSize;
                    // 送付ファイルファイル拡張子
                    state.parameters.attachmentDetailFileExtension = response.data.initParams.attachmentDetailFileExtension;
                    // 送付ファイル最大ファイルサイズ
                    state.parameters.attachmentDetailFileSize = response.data.initParams.attachmentDetailFileSize;
                    // 送付ファイル最大ファイル数
                    state.parameters.attachmentDetailFileCount = response.data.initParams.attachmentDetailFileCount;
                    break;
                case SystemConst.SERVICE_CODE.MATERIAL_DB:
                    // サービスコードが教材DBである場合
                    // 新着日数
                    state.parameters.newArrivalDays = response.data.initParams.newArrivalDays;
                    // 教材ファイルファイル拡張子
                    state.parameters.materialFileExtension = response.data.initParams.materialFileExtension;
                    // 教材ファイル最大ファイルサイズ
                    state.parameters.materialFileSize = response.data.initParams.materialFileSize;
                    // 教材ファイル最大ファイル数
                    state.parameters.materialFileCount = response.data.initParams.materialFileCount;
                    // サムネイルファイルファイル拡張子
                    state.parameters.thumbnailFileExtension = response.data.initParams.thumbnailFileExtension;
                    // サムネイルファイル最大ファイルサイズ
                    state.parameters.thumbnailFileSize = response.data.initParams.thumbnailFileSize;
                    break;
                case SystemConst.SERVICE_CODE.CONTENT:
                    // サービスコードが教育用コンテンツである場合
                    // 新着日数
                    state.parameters.newArrivalDays = response.data.initParams.newArrivalDays;
                    // ファイル拡張子
                    state.parameters.fileExtension= response.data.initParams.fileExtension;
                    // 最大ファイルサイズ
                    state.parameters.fileSize = response.data.initParams.fileSize;
                    // 最大ファイル数
                    state.parameters.fileCount = response.data.initParams.fileCount;
                    break;
                case SystemConst.PARAMETER_FUNCTION_CODE.ADMIN:
                    // サービスコードがシステム管理である場合
                    // 一括登録ファイル最大ファイルサイズ
                    state.parameters.bulkAddUsersFileSize = response.data.initParams.bulkAddUsersFileSize;
                    // 返信ファイルファイル拡張子
                    state.parameters.replyFileExtension = response.data.initParams.replyFileExtension;
                    // 返信ファイル最大ファイルサイズ
                    state.parameters.replyFileSize = response.data.initParams.replyFileSize;
                    // 返信ファイル最大ファイル数
                    state.parameters.replyFileCount = response.data.initParams.replyFileCount;
                    // 所属コードファイル最大ファイルサイズ
                    state.parameters.affiliationCodeFileSize = response.data.initParams.affiliationCodeFileSize;
                    // 人事異動データファイル最大ファイルサイズ
                    state.parameters.personnelChangeFileSize = response.data.initParams.personnelChangeFileSize;
                    // 通知データパスワード情報ファイル最大ファイルサイズ
                    state.parameters.notificationPasswordFileSize = response.data.initParams.notificationPasswordFileSize;
                    // 通知データ宛名ファイル最大ファイルサイズ
                    state.parameters.notificationAddresseeFileSize = response.data.initParams.notificationAddresseeFileSize;
                    // 通知データ雛形ファイル最大ファイルサイズ
                    state.parameters.notificationTemplateFileSize = response.data.initParams.notificationTemplateFileSize;
                    // 通知データタイトル
                    state.parameters.notificationDataTitle = response.data.initParams.notificationDataTitle;
                    // 通知データメッセージ
                    state.parameters.notificationDataMessage = response.data.initParams.notificationDataMessage;
                    break;
                case SystemConst.PARAMETER_FUNCTION_CODE.ORG_ADMIN:
                    // サービスコードが組織内管理である場合
                    // 一括登録ファイル最大ファイルサイズ
                    state.parameters.bulkAddUsersFileSize = response.data.initParams.bulkAddUsersFileSize;
                    // 申請ファイルファイル拡張子
                    state.parameters.applicationFileExtension = response.data.initParams.applicationFileExtension;
                    // 申請ファイル最大ファイルサイズ
                    state.parameters.applicationFileSize = response.data.initParams.applicationFileSize;
                    // 添付ファイルファイル拡張子
                    state.parameters.attachFileExtension = response.data.initParams.attachFileExtension;
                    // 添付ファイル最大ファイルサイズ
                    state.parameters.attachFileSize = response.data.initParams.attachFileSize;
                    // 添付ファイル最大ファイル数
                    state.parameters.attachFileCount = response.data.initParams.attachFileCount;
                    break;
                case SystemConst.PARAMETER_FUNCTION_CODE.COMMON:
                    // サービスコードが共通（システム管理・組織内管理）である場合
                    // メールエイリアス削除日数
                    state.parameters.deleteDays = response.data.initParams.deleteDays;
                    // a1、c1、s1アカウント
                    state.parameters.teacherMailReuseDay = response.data.initParams.teacherMailReuseDay;
                    // b1、b2アカウント
                    state.parameters.studentGeneralMailReuseDay = response.data.initParams.studentGeneralMailReuseDay;
                    break;
            }

            // バリデーション値を設定する
            state.validateValues = response.data.validateParams;
        }

        /**
         * 組織名称/管理者のみ表示/状態プルダウンデータ取得関数
         */
        async function getPulldownItems() {
            // 組織名称/管理者のみ表示/状態プルダウンデータ取得APIでデータを取得する
            // パラメータ：なし
            const response = await ctx.root.$store.dispatch('parameterSettings/getPulldownItems');

            // HTTPステータスコードが200以外の場合
            if (response.status !== HttpStatus.OK) {
                // HTTPステータスコードが500であればエラー画面へ遷移して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // state.orgPulldownItemsに取得した組織名称プルダウンデータを設定する
            state.orgPulldownItems = response.data.orgPulldownItems;

            // state.isAdminPulldownItemsに取得した管理者のみ表示プルダウンデータを設定する
            state.isAdminPulldownItems = response.data.isAdminPulldownItems;

            // state.isEnabledPulldownItemsに取得した状態プルダウンデータを設定する
            state.isEnabledPulldownItems = response.data.isEnabledPulldownItems;
        }

        /**
         * 機能選択プルダウンの値が変更された際のイベント
         */
        function changeFunction() {
            switch (state.selectFunctionPulldown) {
                case selectFunctionPulldownItems['OFFICIAL_DOCUMENTS']:
                    // 公文書が選択された場合
                    // パラメータ設定フォーム・カテゴリ一覧情報・バリデーション結果・バリデーションメッセージの初期化関数呼び出し
                    resetProcessData();

                    // 公文書タブを設定
                    state.tabs = documentTabs;

                    // 選択中のタブを初期化
                    state.selectTab = tabLists['FIRST'];

                    // 定数定義から公文書のサービスコードを設定
                    state.serviceCode = SystemConst.SERVICE_CODE.OFFICIAL_DOCUMENTS;

                    // パラメータマスタデータ取得関数呼び出し
                    getParameters();
                    break;
                case selectFunctionPulldownItems['INFORMATION']:
                    // お知らせが選択された場合
                    // パラメータ設定フォーム・カテゴリ一覧情報・バリデーション結果・バリデーションメッセージの初期化関数呼び出し
                    resetProcessData();

                    // お知らせタブを設定
                    state.tabs = singleTab;

                    // 選択中のタブを初期化
                    state.selectTab = tabLists['FIRST'];

                    // 定数定義からお知らせのサービスコードを設定
                    state.serviceCode = SystemConst.SERVICE_CODE.INFORMATION;

                    // パラメータマスタデータ取得関数呼び出し
                    getParameters();
                    break;
                case selectFunctionPulldownItems['IMPORTANT_INFORMATION']:
                    // 重要情報のお知らせが選択された場合
                    // パラメータ設定フォーム・カテゴリ一覧情報・バリデーション結果・バリデーションメッセージの初期化関数呼び出し
                    resetProcessData();

                    // 重要情報のお知らせタブを設定
                    state.tabs = singleTab;

                    // 選択中のタブを初期化
                    state.selectTab = tabLists['FIRST'];

                    // 定数定義から重要情報のお知らせのサービスコードを設定
                    state.serviceCode = SystemConst.SERVICE_CODE.IMPORTANT_INFORMATION;

                    // パラメータマスタデータ取得関数呼び出し
                    getParameters();
                    break;
                case selectFunctionPulldownItems['MATERIAL_DB']:
                    // 教材DBが選択された場合
                    // パラメータ設定フォーム・カテゴリ一覧情報・バリデーション結果・バリデーションメッセージの初期化関数呼び出し
                    resetProcessData();

                    // 教材DBタブを設定
                    state.tabs = materialDBTabs;

                    // 選択中のタブを初期化
                    state.selectTab = tabLists['FIRST'];

                    // 定数定義から教材DBのサービスコードを設定
                    state.serviceCode = SystemConst.SERVICE_CODE.MATERIAL_DB;

                    // パラメータマスタデータ取得関数呼び出し
                    getParameters();
                    break;
                case selectFunctionPulldownItems['CONTENT']:
                    // 教育用コンテンツが選択された場合
                    // パラメータ設定フォーム・カテゴリ一覧情報・バリデーション結果・バリデーションメッセージの初期化関数呼び出し
                    resetProcessData();

                    // 教育用コンテンツタブを設定
                    state.tabs = contentTabs;

                    // 選択中のタブを初期化
                    state.selectTab = tabLists['FIRST'];

                    // 定数定義から教育用コンテンツのサービスコードを設定
                    state.serviceCode = SystemConst.SERVICE_CODE.CONTENT;

                    // パラメータマスタデータ取得関数呼び出し
                    getParameters();
                    break;
                case selectFunctionPulldownItems['ADMIN']:
                    // システム管理が選択された場合
                    // パラメータ設定フォーム・カテゴリ一覧情報・バリデーション結果・バリデーションメッセージの初期化関数呼び出し
                    resetProcessData();

                    // システム管理タブを設定
                    state.tabs = singleTab;

                    // 選択中のタブを初期化
                    state.selectTab = tabLists['FIRST'];

                    // サービスコードに定数定義から「'admin'」を設定
                    state.serviceCode = SystemConst.PARAMETER_FUNCTION_CODE.ADMIN;

                    // パラメータマスタデータ取得関数呼び出し
                    getParameters();
                    break;
                case selectFunctionPulldownItems['ORG_ADMIN']:
                    // 組織内管理が選択された場合
                    // パラメータ設定フォーム・カテゴリ一覧情報・バリデーション結果・バリデーションメッセージの初期化関数呼び出し
                    resetProcessData();

                    // 組織内管理タブを設定
                    state.tabs = orgAdminTabs;

                    // 選択中のタブを初期化
                    state.selectTab = tabLists['FIRST'];

                    // サービスコードに定数定義から「'org_admin'」を設定
                    state.serviceCode = SystemConst.PARAMETER_FUNCTION_CODE.ORG_ADMIN;

                    // パラメータマスタデータ取得関数呼び出し
                    getParameters();
                    break;
                case selectFunctionPulldownItems['COMMON']:
                    // 共通（システム管理・組織内管理）が選択された場合
                    // パラメータ設定フォーム・カテゴリ一覧情報・バリデーション結果・バリデーションメッセージの初期化関数呼び出し
                    resetProcessData();

                    // 共通（システム管理・組織内管理）タブを設定
                    state.tabs = singleTab;

                    // 選択中のタブを初期化
                    state.selectTab = tabLists['FIRST'];

                    // サービスコードに定数定義から「'common'」を設定
                    state.serviceCode = SystemConst.PARAMETER_FUNCTION_CODE.COMMON;

                    // パラメータマスタデータ取得関数呼び出し
                    getParameters();
                    break;
            }
        }

        /**
         * タブをクリックした際のイベント
         */
        async function selectTab(tabId: string) {
            switch (String(tabId)) {
                case tabLists['FIRST']:
                    // 1番目のタブが選択された場合（各機能のパラメータ設定）
                    // パラメータ設定フォーム・カテゴリ一覧情報・バリデーション結果・バリデーションメッセージの初期化関数呼び出し
                    resetProcessData();

                    // 選択中のタブを「1」に変更
                    state.selectTab = tabLists['FIRST'];

                    // パラメータマスタデータ取得関数呼び出し
                    getParameters();
                    break;
                case tabLists['SECOND']:
                    // 2番目のタブが選択された場合（公文書：発番カテゴリ設定、教材DB：校種設定、教育用コンテンツ：コンテンツカテゴリ設定、組織内管理：処理依頼内容設定）
                    // パラメータ設定フォーム・カテゴリ一覧情報・バリデーション結果・バリデーションメッセージの初期化関数呼び出し
                    resetProcessData();

                    // 選択中のタブを「2」に変更
                    state.selectTab = tabLists['SECOND'];

                    switch (state.selectFunctionPulldown) {
                        case selectFunctionPulldownItems['OFFICIAL_DOCUMENTS']:
                            // 公文書である場合
                            // ヘッダーを変更
                            state.categoryDisplayTableHeaders = numberPrefixTableHeaders;

                            // テーブル名称変更
                            state.tableName = SystemConst.CATEGORY_TABLE_NAME.NUMBER_PREFIX;

                            // カテゴリデータ取得関数呼び出し
                            getCategories();
                            break;
                        case selectFunctionPulldownItems['MATERIAL_DB']:
                            // 教材DBである場合
                            // ヘッダーを変更
                            state.categoryDisplayTableHeaders = schoolTypeTableHeaders;

                            // テーブル名称変更
                            state.tableName = SystemConst.CATEGORY_TABLE_NAME.SCHOOL_TYPES;

                            // カテゴリデータ取得関数呼び出し
                            getCategories();
                            break;
                        case selectFunctionPulldownItems['CONTENT']:
                            // 教育用コンテンツである場合
                            // ヘッダーを変更
                            state.categoryDisplayTableHeaders = contentTableHeaders;

                            // テーブル名称変更
                            state.tableName = SystemConst.CATEGORY_TABLE_NAME.CONTENT;

                            // カテゴリデータ取得関数呼び出し
                            getCategories();
                            break;
                        case selectFunctionPulldownItems['ORG_ADMIN']:
                            // 組織内管理である場合
                            // ヘッダーを変更
                            state.categoryDisplayTableHeaders = processingRequestTableHeaders;

                            // テーブル名称変更
                            state.tableName = SystemConst.CATEGORY_TABLE_NAME.PROCESS_REQUEST;

                            // カテゴリデータ取得関数呼び出し
                            getCategories();
                            break;
                    }
                    break;
                case tabLists['THIRD']:
                    // 3番目のタブが選択された場合（公文書：メール通知先設定、教材DB：教科設定）
                    // パラメータ設定フォーム・カテゴリ一覧情報・バリデーション結果・バリデーションメッセージの初期化関数呼び出し
                    resetProcessData();

                    // 選択中のタブを「3」に変更
                    state.selectTab = tabLists['THIRD'];

                    switch (state.selectFunctionPulldown) {
                        case selectFunctionPulldownItems['OFFICIAL_DOCUMENTS']:
                            // 公文書である場合
                            // ヘッダーを変更
                            state.categoryDisplayTableHeaders = notificationsTableHeaders;

                            // テーブル名称変更
                            state.tableName = SystemConst.CATEGORY_TABLE_NAME.NOTIFICATIONS;

                            // カテゴリデータ取得関数呼び出し
                            getCategories();
                            break;
                        case selectFunctionPulldownItems['MATERIAL_DB']:
                            // 教材DBである場合
                            // ヘッダーを変更
                            state.categoryDisplayTableHeaders = curriculumTableHeaders;

                            // テーブル名称変更
                            state.tableName = SystemConst.CATEGORY_TABLE_NAME.CURRICULUMS;

                            // 校種プルダウンデータ取得関数呼び出し
                            await getSchoolTypePulldownItems();

                            // 校種プルダウンデータ取得に成功した場合
                            if (state.successGetSchoolTypePulldownItems) {
                                // カテゴリデータ取得関数呼び出し
                                getCategories();
                            }
                            break;
                    }
                    break;
                case tabLists['FOURTH']:
                    // 4番目のタブが選択された場合（教材DB：科目設定）
                    // パラメータ設定フォーム・カテゴリ一覧情報・バリデーション結果・バリデーションメッセージの初期化関数呼び出し
                    resetProcessData();

                    // 選択中のタブを「4」に変更
                    state.selectTab = tabLists['FOURTH'];

                    // ヘッダーを変更
                    state.categoryDisplayTableHeaders = subjectTableHeaders;

                    // テーブル名称変更
                    state.tableName = SystemConst.CATEGORY_TABLE_NAME.SUBJECTS;

                    // 校種プルダウンデータ取得関数呼び出し
                    await getSchoolTypePulldownItems();

                    // 校種プルダウンデータ取得に成功した場合
                    if (state.successGetSchoolTypePulldownItems) {
                        // 教科プルダウンデータ取得関数呼び出し
                        await getCurriculumPulldownItems();
                    }

                    // 校種プルダウンデータ取得に成功したかつ教科プルダウンデータ取得に成功した場合
                    if (state.successGetSchoolTypePulldownItems && state.successGetCurriculumPulldownItems) {
                        // カテゴリデータ取得関数呼び出し
                        getCategories();
                    }
                    break;
            }
        }

        /**
         * 校種プルダウンデータ取得関数
         */
        async function getSchoolTypePulldownItems() {
            // 校種プルダウンデータ取得成功フラグを初期化
            state.successGetSchoolTypePulldownItems = false;

            // 校種プルダウンデータ取得APIでデータを取得する
            // パラメータ：タブ番号
            const response = await ctx.root.$store.dispatch('parameterSettings/getSchoolTypePulldownItems',
                {
                    selectTab: state.selectTab
                }
            );

            // HTTPステータスコードが200以外の場合
            if (response.status !== HttpStatus.OK) {
                // HTTPステータスコードが400であればエラーダイアログを表示し、それ以外はエラー画面へ遷移して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // state.schoolTypePulldownItemsに取得した校種プルダウンデータを設定する
            state.schoolTypePulldownItems = response.data.schoolTypePulldownItems;

            // state.selectSchoolTypePulldownにstate.schoolTypePulldownItemsの最初の校種IDを設定する
            state.selectSchoolTypePulldown = state.schoolTypePulldownItems[0].value;

            // 校種プルダウンデータ取得成功フラグをtrueにする
            state.successGetSchoolTypePulldownItems = true;
        }

        /**
         * 教科プルダウンデータ取得関数呼び出し
         */
        async function getCurriculumPulldownItems() {
            // 教科プルダウンデータ取得成功フラグを初期化
            state.successGetCurriculumPulldownItems = false;

            // 教科プルダウンデータ取得APIでデータを取得する
            // パラメータ：なし
            const response = await ctx.root.$store.dispatch('parameterSettings/getCurriculumPulldownItems');

            // HTTPステータスコードが200以外の場合
            if (response.status !== HttpStatus.OK) {
                // HTTPステータスコードが404であればエラーダイアログを表示し、それ以外はエラー画面へ遷移して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);

                // HTTPステータスコードが404であれば教科設定タブに切り替える
                if (response.status === HttpStatus.NOT_FOUND) {
                    // パラメータ設定フォーム・カテゴリ一覧情報・バリデーション結果・バリデーションメッセージの初期化関数呼び出し
                    resetProcessData();

                    // 選択中のタブを「3」に変更
                    state.selectTab = tabLists['THIRD'];

                    // ヘッダーを変更
                    state.categoryDisplayTableHeaders = curriculumTableHeaders;

                    // テーブル名称変更
                    state.tableName = SystemConst.CATEGORY_TABLE_NAME.CURRICULUMS;

                    // 校種プルダウンデータ取得関数呼び出し
                    await getSchoolTypePulldownItems();

                    // 校種プルダウンデータ取得に成功した場合
                    if (state.successGetSchoolTypePulldownItems) {
                        // カテゴリデータ取得関数呼び出し
                        getCategories();
                    }
                }
                return;
            }

            // state.curriculumPulldownItemsに取得した教科プルダウンデータを設定する
            state.curriculumPulldownItems = response.data.curriculumPulldownItems;

            // state.selectCurriculumPulldownにstate.curriculumPulldownItemsの最初の教科IDを設定する
            state.selectCurriculumPulldown = state.curriculumPulldownItems[0].value;

            // 教科プルダウンデータ取得成功フラグをtrueにする
            state.successGetCurriculumPulldownItems = true;
        }

        /**
         * 科目設定の校種名称取得
         * 
         * @returns 校種プルダウンデータがある：校種名称、校種プルダウンデータがない：void
         */
        function getHighSchoolName() {
            // 校種プルダウンデータ取得前であれば以降の処理を行わない
            if (state.schoolTypePulldownItems.length === 0) {
                return;
            }

            // 校種プルダウンデータの最初の校種名称を返す
            return state.schoolTypePulldownItems[0].text;
        }

        /**
         * カテゴリデータ取得関数
         */
        async function getCategories() {
            // カテゴリ設定登録ボタン表示フラグの初期化
            state.displayProcessCategoryButton = false;

            // カテゴリデータの初期化
            state.categoryListItems = [];

            // テーブルがロード中のフラグをtrueにする
            state.isLoading = true;

            // カテゴリデータ取得APIでカテゴリデータを取得する
            // パラメータ：サービスコード
            //            タブ番号
            //            選択した校種ID（教科設定、科目設定以外は空文字）
            //            選択した教科ID（科目設定以外は空文字）
            const response = await ctx.root.$store.dispatch('parameterSettings/getCategories',
                {
                    serviceCode: state.serviceCode,
                    selectTab: state.selectTab,
                    selectSchoolTypePulldown: state.selectSchoolTypePulldown,
                    selectCurriculumPulldown: state.selectCurriculumPulldown
                }
            );

            // HTTPステータスコードが200以外の場合
            if (response.status !== HttpStatus.OK) {
                // テーブルがロード中のフラグをfalseにする
                state.isLoading = false;

                // HTTPステータスコードが400又は404であればエラーダイアログを表示し、それ以外はエラー画面へ遷移して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // state.categoryListItemsに取得したカテゴリデータを設定する
            state.categoryListItems = response.data.categoryListItems;

            // カテゴリ設定登録ボタン表示フラグにtrueを設定
            state.displayProcessCategoryButton = true;

            // テーブルがロード中のフラグをfalseにする
            state.isLoading = false;
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
        }

        /**
         * 行ドラッグイベント
         * 
         * @param event                    // ドラッグ&ドロップイベントデータ
         * @param dragCategoryRow          // ドラッグしたカテゴリの行
         * @param dragCategoryId           // ドラッグしたカテゴリのID
         * @param dragCategoryProcessCode  // ドラッグしたカテゴリの処理フラグ
         * @param dragCategoryName         // ドラッグしたカテゴリの名称
         * @param tel                      // ドラッグしたカテゴリの電話番号（発番カテゴリ設定）
         * @param numberPrefix             // ドラッグしたカテゴリの発番接頭辞（発番カテゴリ設定）
         * @param departmentName           // ドラッグしたカテゴリの部門名（発番カテゴリ設定）
         * @param isAdminOnly              // ドラッグしたカテゴリの管理者のみ表示フラグ（発番カテゴリ設定）
         * @param mail                     // ドラッグしたカテゴリのメールアドレス（メール通知先設定）
         * @param dbMail                   // ドラッグしたカテゴリのDB上のメールアドレス（メール通知先設定）
         * @param isEnabled                // ドラッグしたカテゴリの状態フラグ（メール通知先・コンテンツカテゴリ・処理依頼内容設定）
         */
        function dragRow(
            event: any,
            dragCategoryRow: number,
            dragCategoryId: string,
            dragCategoryProcessCode: number,
            dragCategoryName: string,
            tel?: string,
            numberPrefix?: string,
            departmentName?: string,
            isAdminOnly?: string,
            mail?: string,
            dbMail?: string,
            isEnabled?: string
        ) {
            // ドラッグ中のデータの移動を許可する
            event.dataTransfer.effectAllowed = 'move';
            // ドラッグ中のデータをドロップ先に移動する
            event.dataTransfer.dropEffect = 'move';

            // ドラッグデータの保存（共通）
            // 行、各種ID、処理フラグ、各種名称
            event.dataTransfer.setData('categoryRow', dragCategoryRow);
            event.dataTransfer.setData('categoryId', dragCategoryId);
            event.dataTransfer.setData('categoryProcessCode', dragCategoryProcessCode);
            event.dataTransfer.setData('categoryName', dragCategoryName);

            // 公文書の発番カテゴリ設定である場合
            if (state.selectFunctionPulldown === selectFunctionPulldownItems['OFFICIAL_DOCUMENTS'] &&
                state.selectTab === tabLists['SECOND']) {
                // 電話番号、発番、部門名、管理者のみ表示フラグを保存
                event.dataTransfer.setData('categoryTel', tel);
                event.dataTransfer.setData('categoryNumberPrefix', numberPrefix);
                event.dataTransfer.setData('categoryDepartmentName', departmentName);
                event.dataTransfer.setData('categoryIsAdminOnly', isAdminOnly);
            } else if (state.selectFunctionPulldown === selectFunctionPulldownItems['OFFICIAL_DOCUMENTS'] &&
                state.selectTab === tabLists['THIRD']) {
                // 公文書のメール通知先設定である場合
                // メールアドレス、DB上のメールアドレス、状態を保存
                event.dataTransfer.setData('categoryMail', mail);
                event.dataTransfer.setData('categoryDbMail', dbMail);
                event.dataTransfer.setData('categoryIsEnabled', isEnabled);
            } else if (state.selectFunctionPulldown === selectFunctionPulldownItems['CONTENT'] ||
                state.selectFunctionPulldown === selectFunctionPulldownItems['ORG_ADMIN']) {
                // 教育用コンテンツ又は組織内管理である場合
                // 状態を保存
                event.dataTransfer.setData('categoryIsEnabled', isEnabled);
            }
        }


        /**
         * 行ドロップイベント
         * 
         * @param event             // ドラッグ&ドロップイベントデータ
         * @param dropCategoryRow   // ドロップした行
         */
        function dropRow(event: any, dropCategoryRow: number) {
            // ドラッグデータの取得（共通）
            // 行、各種ID、処理フラグ、各種名称
            const dragCategoryRow = Number(event.dataTransfer.getData('categoryRow'));
            const dragCategoryId = event.dataTransfer.getData('categoryId');
            const dragCategoryProcessCode = Number(event.dataTransfer.getData('categoryProcessCode'));
            const dragCategoryName = event.dataTransfer.getData('categoryName');

            // 公文書の発番カテゴリ設定である場合
            if (state.selectFunctionPulldown === selectFunctionPulldownItems['OFFICIAL_DOCUMENTS'] &&
                state.selectTab === tabLists['SECOND']) {
                // 電話番号、発番、部門名、管理者のみ表示フラグの取得
                const tel = event.dataTransfer.getData('categoryTel');
                const numberPrefix = event.dataTransfer.getData('categoryNumberPrefix');
                const departmentName = event.dataTransfer.getData('categoryDepartmentName');
                const isAdminOnly = event.dataTransfer.getData('categoryIsAdminOnly');

                if (dragCategoryRow < dropCategoryRow) {
                    // ドラッグしたデータの表示順を下げる場合
                    for (let i = dragCategoryRow; i < dropCategoryRow; i++) {
                        // 配列の格納場所を一段ずつ上げる
                        state.categoryListItems[i].categoryId = state.categoryListItems[i + 1].categoryId;
                        state.categoryListItems[i].processCode = state.categoryListItems[i + 1].processCode;
                        state.categoryListItems[i].categoryName = state.categoryListItems[i + 1].categoryName;
                        state.categoryListItems[i].tel = state.categoryListItems[i + 1].tel;
                        state.categoryListItems[i].numberPrefix = state.categoryListItems[i + 1].numberPrefix;
                        state.categoryListItems[i].departmentName = state.categoryListItems[i + 1].departmentName;
                        state.categoryListItems[i].isAdminOnly = state.categoryListItems[i + 1].isAdminOnly;
                    }
                } else if (dragCategoryRow > dropCategoryRow) {
                    // ドラッグしたデータの表示順を上げる場合
                    for (let i = dragCategoryRow; i > dropCategoryRow; i--) {
                        // 配列の格納場所を一段ずつ下げる
                        state.categoryListItems[i].categoryId = state.categoryListItems[i - 1].categoryId;
                        state.categoryListItems[i].processCode = state.categoryListItems[i - 1].processCode;
                        state.categoryListItems[i].categoryName = state.categoryListItems[i - 1].categoryName;
                        state.categoryListItems[i].tel = state.categoryListItems[i - 1].tel;
                        state.categoryListItems[i].numberPrefix = state.categoryListItems[i - 1].numberPrefix;
                        state.categoryListItems[i].departmentName = state.categoryListItems[i - 1].departmentName;
                        state.categoryListItems[i].isAdminOnly = state.categoryListItems[i - 1].isAdminOnly;
                    }
                }

                // ドラッグしたデータをドロップした行に設定
                state.categoryListItems[dropCategoryRow].categoryId = dragCategoryId;
                state.categoryListItems[dropCategoryRow].processCode = dragCategoryProcessCode;
                state.categoryListItems[dropCategoryRow].categoryName = dragCategoryName;
                state.categoryListItems[dropCategoryRow].tel = tel;
                state.categoryListItems[dropCategoryRow].numberPrefix = numberPrefix;
                state.categoryListItems[dropCategoryRow].departmentName = departmentName;
                state.categoryListItems[dropCategoryRow].isAdminOnly = isAdminOnly;
            } else if (state.selectFunctionPulldown === selectFunctionPulldownItems['OFFICIAL_DOCUMENTS'] &&
                state.selectTab === tabLists['THIRD']) {
                // 公文書のメール通知先設定である場合
                // メールアドレス、DB上のメールアドレス、状態の取得
                const mail = event.dataTransfer.getData('categoryMail');
                const dbMail = event.dataTransfer.getData('categoryDbMail');
                const isEnabled = event.dataTransfer.getData('categoryIsEnabled');

                if (dragCategoryRow < dropCategoryRow) {
                    // 表示順を下げる場合
                    for (let i = dragCategoryRow; i < dropCategoryRow; i++) {
                        // 行を一段ずつ上げる
                        state.categoryListItems[i].categoryId = state.categoryListItems[i + 1].categoryId;
                        state.categoryListItems[i].processCode = state.categoryListItems[i + 1].processCode;
                        state.categoryListItems[i].categoryName = state.categoryListItems[i + 1].categoryName;
                        state.categoryListItems[i].mail = state.categoryListItems[i + 1].mail;
                        state.categoryListItems[i].dbMail = state.categoryListItems[i + 1].dbMail;
                        state.categoryListItems[i].isEnabled = state.categoryListItems[i + 1].isEnabled;
                    }
                } else if (dragCategoryRow > dropCategoryRow) {
                    // 表示順を上げる場合
                    for (let i = dragCategoryRow; i > dropCategoryRow; i--) {
                        // 行を一段ずつ下げる
                        state.categoryListItems[i].categoryId = state.categoryListItems[i - 1].categoryId;
                        state.categoryListItems[i].processCode = state.categoryListItems[i - 1].processCode;
                        state.categoryListItems[i].categoryName = state.categoryListItems[i - 1].categoryName;
                        state.categoryListItems[i].mail = state.categoryListItems[i - 1].mail;
                        state.categoryListItems[i].dbMail = state.categoryListItems[i - 1].dbMail;
                        state.categoryListItems[i].isEnabled = state.categoryListItems[i - 1].isEnabled;
                    }
                }

                // ドラッグしたデータをドロップ先に設定
                state.categoryListItems[dropCategoryRow].categoryId = dragCategoryId;
                state.categoryListItems[dropCategoryRow].processCode = dragCategoryProcessCode;
                state.categoryListItems[dropCategoryRow].categoryName = dragCategoryName;
                state.categoryListItems[dropCategoryRow].mail = mail;
                state.categoryListItems[dropCategoryRow].dbMail = dbMail;
                state.categoryListItems[dropCategoryRow].isEnabled = isEnabled;
            } else if (state.selectFunctionPulldown === selectFunctionPulldownItems['MATERIAL_DB']) {
                // 教材DBである場合
                if (dragCategoryRow < dropCategoryRow) {
                    // 表示順を下げる場合
                    for (let i = dragCategoryRow; i < dropCategoryRow; i++) {
                        // 行を一段ずつ上げる
                        state.categoryListItems[i].categoryId = state.categoryListItems[i + 1].categoryId;
                        state.categoryListItems[i].processCode = state.categoryListItems[i + 1].processCode;
                        state.categoryListItems[i].categoryName = state.categoryListItems[i + 1].categoryName;
                    }
                } else if (dragCategoryRow > dropCategoryRow) {
                    // 表示順を上げる場合
                    for (let i = dragCategoryRow; i > dropCategoryRow; i--) {
                        // 行を一段ずつ下げる
                        state.categoryListItems[i].categoryId = state.categoryListItems[i - 1].categoryId;
                        state.categoryListItems[i].processCode = state.categoryListItems[i - 1].processCode;
                        state.categoryListItems[i].categoryName = state.categoryListItems[i - 1].categoryName;
                    }
                }

                // ドラッグしたデータをドロップ先に設定
                state.categoryListItems[dropCategoryRow].categoryId = dragCategoryId;
                state.categoryListItems[dropCategoryRow].processCode = dragCategoryProcessCode;
                state.categoryListItems[dropCategoryRow].categoryName = dragCategoryName;
            } else if (state.selectFunctionPulldown === selectFunctionPulldownItems['CONTENT'] ||
                state.selectFunctionPulldown === selectFunctionPulldownItems['ORG_ADMIN']) {
                // 教育用コンテンツ又は組織内管理である場合
                // 状態の取得
                const isEnabled = event.dataTransfer.getData('categoryIsEnabled');

                if (dragCategoryRow < dropCategoryRow) {
                    // 表示順を下げる場合
                    for (let i = dragCategoryRow; i < dropCategoryRow; i++) {
                        // 行を一段ずつ上げる
                        state.categoryListItems[i].categoryId = state.categoryListItems[i + 1].categoryId;
                        state.categoryListItems[i].processCode = state.categoryListItems[i + 1].processCode;
                        state.categoryListItems[i].categoryName = state.categoryListItems[i + 1].categoryName;
                        state.categoryListItems[i].isEnabled = state.categoryListItems[i + 1].isEnabled;
                    }
                } else if (dragCategoryRow > dropCategoryRow) {
                    // 表示順を上げる場合
                    for (let i = dragCategoryRow; i > dropCategoryRow; i--) {
                        // 行を一段ずつ下げる
                        state.categoryListItems[i].categoryId = state.categoryListItems[i - 1].categoryId;
                        state.categoryListItems[i].processCode = state.categoryListItems[i - 1].processCode;
                        state.categoryListItems[i].categoryName = state.categoryListItems[i - 1].categoryName;
                        state.categoryListItems[i].isEnabled = state.categoryListItems[i - 1].isEnabled;
                    }
                }

                // ドラッグしたデータをドロップ先に設定
                state.categoryListItems[dropCategoryRow].categoryId = dragCategoryId;
                state.categoryListItems[dropCategoryRow].processCode = dragCategoryProcessCode;
                state.categoryListItems[dropCategoryRow].categoryName = dragCategoryName;
                state.categoryListItems[dropCategoryRow].isEnabled = isEnabled;
            }
        }

        /**
         * 行追加イベント
         */
        function addRow() {
            // データ配列初期化
            let addData: CategoryList[] = [];

            // 表示順初期化
            let displayOrder = 0;

            // 通知先/教科/科目ID/カテゴリID/依頼内容ID初期化
            let nextId = 0;

            // 処理フラグが「削除」を除いたカテゴリ一覧配列を作成
            const categoryList = state.categoryListItems.filter(function(item){
                return item.processCode !== processCode['DELETE'];
            });

            // 処理フラグが「削除」以外のデータが無い場合
            if (categoryList.length === 0) {
                // 表示順に「1」を設定
                displayOrder = 1;
            } else {
                // 「削除」以外のデータがある場合
                // 作成した配列の最後のデータの表示順を+1して取得する
                displayOrder = categoryList[categoryList.length - 1].displayOrder + 1;
            }

            // 追加するデータの配列を作成する
            if (state.selectFunctionPulldown === selectFunctionPulldownItems['OFFICIAL_DOCUMENTS'] &&
                state.selectTab === tabLists['SECOND']) {
                // 公文書の発番カテゴリ設定である場合
                addData = [
                    {
                        categoryId: '',             // 組織名称（組織ID）
                        processCode: 1,             // 処理フラグ
                        displayOrder: displayOrder, // 表示順
                        categoryName: '',           // カテゴリ名称
                        tel: '',                    // 電話番号
                        numberPrefix: '',           // 発番接頭辞
                        departmentName: '',         // 部門名
                        isAdminOnly: '0'            // 管理者のみ表示
                    }
                ];
            } else if (state.selectFunctionPulldown === selectFunctionPulldownItems['OFFICIAL_DOCUMENTS'] &&
                state.selectTab === tabLists['THIRD']) {
                // 公文書のメール通知先設定である場合
                if (state.categoryListItems.length === 0) {
                    // メール通知先データがない場合
                    // 通知先IDに「1」を設定
                    nextId = 1;
                } else {
                    // メール通知先データがある場合
                    // state.categoryListItemsから「categoryId」の最大値+1を取得する
                    nextId = Math.max.apply(null,state.categoryListItems.map(function(item){
                        return Number(item.categoryId);
                    })) + 1;
                }

                addData = [
                    {
                        categoryId: String(nextId), // 通知先ID
                        processCode: 1,             // 処理フラグ
                        displayOrder: displayOrder, // 表示順
                        categoryName: '',           // メール通知先名称
                        mail: '',                   // メールアドレス
                        dbMail: '',                 // DB上のメールアドレス
                        isEnabled: '1'              // 状態
                    }
                ];
            } else if (state.selectFunctionPulldown === selectFunctionPulldownItems['MATERIAL_DB'] &&
                (state.selectTab === tabLists['THIRD'] || state.selectTab === tabLists['FOURTH'])) {
                // 教材DBの教科設定又は科目設定である場合
                if (state.categoryListItems.length === 0) {
                    // 教科/科目データがない場合
                    // 教科/科目IDに「1」を設定
                    nextId = 1;
                } else {
                    // 教科/科目データがある場合
                    // state.categoryListItemsから「categoryId」の最大値+1を取得する
                    nextId = Math.max.apply(null,state.categoryListItems.map(function(item){
                        return Number(item.categoryId);
                    })) + 1;
                }

                addData = [
                    {
                        categoryId: String(nextId), // 教科/科目ID
                        processCode: 1,             // 処理フラグ
                        displayOrder: displayOrder, // 表示順
                        categoryName: ''            // 教科/科目名称
                    }
                ];
            } else if (state.selectFunctionPulldown === selectFunctionPulldownItems['CONTENT'] &&
                (state.selectTab === tabLists['SECOND'])) {
                // 教育用コンテンツのコンテンツカテゴリ設定である場合
                if (state.categoryListItems.length === 0) {
                    // コンテンツカテゴリデータがない場合
                    // カテゴリIDに「1」を設定
                    nextId = 1;
                } else {
                    // コンテンツカテゴリデータがある場合
                    // state.categoryListItemsから「categoryId」の最大値+1を取得する
                    nextId = Math.max.apply(null,state.categoryListItems.map(function(item){
                        return Number(item.categoryId);
                    })) + 1;
                }

                addData = [
                    {
                        categoryId: String(nextId), // カテゴリID
                        processCode: 1,             // 処理フラグ
                        displayOrder: displayOrder, // 表示順
                        categoryName: '',           // カテゴリ名称
                        isEnabled: '1'              // 状態
                    }
                ];
            } else if (state.selectFunctionPulldown === selectFunctionPulldownItems['ORG_ADMIN'] &&
                (state.selectTab === tabLists['SECOND'])) {
                // 組織内管理の処理依頼内容設定である場合
                if (state.categoryListItems.length === 0) {
                    // 処理依頼内容データがない場合
                    // 処理依頼カテゴリIDに「1」を設定
                    nextId = 1;
                } else {
                    // 処理依頼内容データがある場合
                    // state.categoryListItemsから「categoryId」の最大値+1を取得する
                    nextId = Math.max.apply(null,state.categoryListItems.map(function(item){
                        return Number(item.categoryId);
                    })) + 1;
                }

                addData = [
                    {
                        categoryId: String(nextId), // 処理依頼カテゴリID
                        processCode: 1,             // 処理フラグ
                        displayOrder: displayOrder, // 表示順
                        categoryName: '',           // 依頼内容名称
                        isEnabled: '1'              // 状態
                    }
                ];
            }

            // 処理フラグの配列を作成
            const processCodeList = state.categoryListItems.map(item => item['processCode']);

            // state.categoryListItemsに「processCode」が「削除」のデータが存在するか
            if (processCodeList.includes(SystemConst.PROCESS_CODE.DELETE)) {
                // 存在する場合
                // 最初の「削除」データの1つ前に追加するデータの配列を挿入する
                // splice([「削除」が最初に存在する場所], [挿入する場所からいくつデータを削除するか], [挿入データ])
                state.categoryListItems.splice(processCodeList.indexOf(SystemConst.PROCESS_CODE.DELETE), 0, addData[0]);
            } else {
                // 存在しない場合
                // state.categoryListItemsの最後に追加するデータの配列を挿入する
                state.categoryListItems.push(addData[0]);
            }

            // カテゴリ設定登録ボタン表示フラグをtrueに設定
            state.displayProcessCategoryButton = true;
        }

        /**
         * 行削除イベント
         * 
         * @param deleteCategoryRow   // 削除する行
         * @param dialogId            // ダイアログID（発番カテゴリ設定の場合表示順）
         */
        async function deleteRow(deleteCategoryRow: number, dialogId: string) {
            // 確認ダイアログを非表示にする
            state.displayConfirmationDialog = false;

            // ローダーtrueにする
            state.isDeleteRow = true;

            // 新規カテゴリフラグ初期化
            let isNew = true;

            // 削除するデータの処理フラグが「11（編集）」である場合
            if (state.categoryListItems[deleteCategoryRow].processCode === SystemConst.PROCESS_CODE.UPDATE) {
                // 新規カテゴリフラグをfalseにする
                isNew = false;

                //   公文書のメール通知先設定ではない場合
                if (!(state.selectFunctionPulldown === selectFunctionPulldownItems['OFFICIAL_DOCUMENTS'] &&
                    state.selectTab === tabLists['THIRD'])) {
                    // カテゴリ削除チェックAPIで削除可能なカテゴリかチェックする
                    // パラメータ：サービスコード
                    //            タブ番号
                    //            各種ID
                    //            選択した校種ID（教科設定、科目設定以外は空文字）
                    //            選択した教科ID（科目設定以外は空文字）
                    const response = await ctx.root.$store.dispatch('parameterSettings/checkDeleteCategory',
                        {
                            serviceCode: state.serviceCode,
                            selectTab: state.selectTab,
                            categoryId: state.categoryListItems[deleteCategoryRow].categoryId,
                            selectSchoolTypePulldown: state.selectSchoolTypePulldown,
                            selectCurriculumPulldown: state.selectCurriculumPulldown
                        }
                    );

                    // HTTPステータスコードが200以外の場合
                    if (response.status !== HttpStatus.OK) {
                        // 削除確認ダイアログをfalseにする
                        state.categoryDeleteDialog[dialogId] = false;

                        // ローダーをfalseにする
                        state.isDeleteRow = false;

                        if (response.status === HttpStatus.NOT_FOUND) {
                            // HTTPステータスコードが404であればエラーダイアログを表示し、カテゴリデータ取得関数を呼び出す
                            commonRef.value.errorDisplayTransitionControl(response);
                            getCategories();
                        } else {
                            // HTTPステータスコードが400であればエラーダイアログを表示する
                            // それ以外はエラー画面へ遷移して処理を終了する
                            commonRef.value.errorDisplayTransitionControl(response);
                        }
                        return;
                    }
                }
            }

            // 以下新規と編集共通の処理
            // 削除するデータの処理フラグを「21（削除）」にする
            state.categoryListItems[deleteCategoryRow].processCode = SystemConst.PROCESS_CODE.DELETE;

            // 削除する行のデータを変数に一時保存
            // 変数の参照渡しを行わないようにするため、一度オブジェクトをJSON文字列に変換し、オブジェクトに戻して設定している
            const deleteData = JSON.parse(JSON.stringify(state.categoryListItems[deleteCategoryRow]));

            // 削除する行以降のカテゴリの表示順を早める
            // 公文書の発番カテゴリ設定である場合
            if (state.selectFunctionPulldown === selectFunctionPulldownItems['OFFICIAL_DOCUMENTS'] &&
                state.selectTab === tabLists['SECOND']) {
                for (let i = deleteCategoryRow; i < state.categoryListItems.length - 1; i++) {
                    // 削除する行まで表示順以外のstate.categoryListItems内データの格納場所を一段ずつ上げる
                    state.categoryListItems[i].categoryId = state.categoryListItems[i + 1].categoryId;
                    state.categoryListItems[i].processCode = state.categoryListItems[i + 1].processCode;
                    state.categoryListItems[i].categoryName = state.categoryListItems[i + 1].categoryName;
                    state.categoryListItems[i].tel = state.categoryListItems[i + 1].tel;
                    state.categoryListItems[i].numberPrefix = state.categoryListItems[i + 1].numberPrefix;
                    state.categoryListItems[i].departmentName = state.categoryListItems[i + 1].departmentName;
                    state.categoryListItems[i].isAdminOnly = state.categoryListItems[i + 1].isAdminOnly;
                }

                if (isNew) {
                    // 新規カテゴリフラグがtrueの場合
                    // state.categoryListItemsの最後のデータを削除する
                    state.categoryListItems.pop();
                } else {
                    // 新規カテゴリフラグがfalseの場合
                    // state.categoryListItemsの最後に一時保存したデータを設定する
                    state.categoryListItems[state.categoryListItems.length - 1].categoryId = deleteData.categoryId;
                    state.categoryListItems[state.categoryListItems.length - 1].processCode = deleteData.processCode;
                    state.categoryListItems[state.categoryListItems.length - 1].categoryName = deleteData.categoryName;
                    state.categoryListItems[state.categoryListItems.length - 1].tel = deleteData.tel;
                    state.categoryListItems[state.categoryListItems.length - 1].numberPrefix = deleteData.numberPrefix;
                    state.categoryListItems[state.categoryListItems.length - 1].departmentName = deleteData.departmentName;
                    state.categoryListItems[state.categoryListItems.length - 1].isAdminOnly = deleteData.isAdminOnly;
                }
            } else if (state.selectFunctionPulldown === selectFunctionPulldownItems['OFFICIAL_DOCUMENTS'] &&
                state.selectTab === tabLists['THIRD']) {
                // 公文書のメール通知先設定である場合
                for (let i = deleteCategoryRow; i < state.categoryListItems.length - 1; i++) {
                    // 削除する行まで表示順以外のstate.categoryListItems内データの格納場所を一段ずつ上げる
                    state.categoryListItems[i].categoryId = state.categoryListItems[i + 1].categoryId;
                    state.categoryListItems[i].processCode = state.categoryListItems[i + 1].processCode;
                    state.categoryListItems[i].categoryName = state.categoryListItems[i + 1].categoryName;
                    state.categoryListItems[i].mail = state.categoryListItems[i + 1].mail;
                    state.categoryListItems[i].dbMail = state.categoryListItems[i + 1].dbMail;
                    state.categoryListItems[i].isEnabled = state.categoryListItems[i + 1].isEnabled;
                }

                if (isNew) {
                    // 新規カテゴリフラグがtrueの場合
                    // state.categoryListItemsの最後のデータを削除する
                    state.categoryListItems.pop();
                } else {
                    // 新規カテゴリフラグがfalseの場合
                    // state.categoryListItemsの最後に一時保存したデータを設定する
                    state.categoryListItems[state.categoryListItems.length - 1].categoryId = deleteData.categoryId;
                    state.categoryListItems[state.categoryListItems.length - 1].processCode = deleteData.processCode;
                    state.categoryListItems[state.categoryListItems.length - 1].categoryName = deleteData.categoryName;
                    state.categoryListItems[state.categoryListItems.length - 1].mail = deleteData.mail;
                    state.categoryListItems[state.categoryListItems.length - 1].dbMail = deleteData.dbMail;
                    state.categoryListItems[state.categoryListItems.length - 1].isEnabled = deleteData.isEnabled;
                }
            } else if (state.selectFunctionPulldown === selectFunctionPulldownItems['MATERIAL_DB'] &&
                (state.selectTab === tabLists['THIRD'] || state.selectTab === tabLists['FOURTH'])) {
                // 教材DBの教科設定又は科目設定である場合
                for (let i = deleteCategoryRow; i < state.categoryListItems.length - 1; i++) {
                    // 削除する行まで表示順以外のstate.categoryListItems内データの格納場所を一段ずつ上げる
                    state.categoryListItems[i].categoryId = state.categoryListItems[i + 1].categoryId;
                    state.categoryListItems[i].processCode = state.categoryListItems[i + 1].processCode;
                    state.categoryListItems[i].categoryName = state.categoryListItems[i + 1].categoryName;
                }

                if (isNew) {
                    // 新規カテゴリフラグがtrueの場合
                    // state.categoryListItemsの最後のデータを削除する
                    state.categoryListItems.pop();
                } else {
                    // 新規カテゴリフラグがfalseの場合
                    // state.categoryListItemsの最後に一時保存したデータを設定する
                    state.categoryListItems[state.categoryListItems.length - 1].categoryId = deleteData.categoryId;
                    state.categoryListItems[state.categoryListItems.length - 1].processCode = deleteData.processCode;
                    state.categoryListItems[state.categoryListItems.length - 1].categoryName = deleteData.categoryName;
                }
            } else if (state.selectFunctionPulldown === selectFunctionPulldownItems['CONTENT'] &&
                (state.selectTab === tabLists['SECOND'])) {
                // 教育用コンテンツのコンテンツカテゴリ設定である場合
                for (let i = deleteCategoryRow; i < state.categoryListItems.length - 1; i++) {
                    // 削除する行まで表示順以外のstate.categoryListItems内データの格納場所を一段ずつ上げる
                    state.categoryListItems[i].categoryId = state.categoryListItems[i + 1].categoryId;
                    state.categoryListItems[i].processCode = state.categoryListItems[i + 1].processCode;
                    state.categoryListItems[i].categoryName = state.categoryListItems[i + 1].categoryName;
                    state.categoryListItems[i].isEnabled = state.categoryListItems[i + 1].isEnabled;
                }

                if (isNew) {
                    // 新規カテゴリフラグがtrueの場合
                    // state.categoryListItemsの最後のデータを削除する
                    state.categoryListItems.pop();
                } else {
                    // 新規カテゴリフラグがfalseの場合
                    // state.categoryListItemsの最後に一時保存したデータを設定する
                    state.categoryListItems[state.categoryListItems.length - 1].categoryId = deleteData.categoryId;
                    state.categoryListItems[state.categoryListItems.length - 1].processCode = deleteData.processCode;
                    state.categoryListItems[state.categoryListItems.length - 1].categoryName = deleteData.categoryName;
                    state.categoryListItems[state.categoryListItems.length - 1].isEnabled = deleteData.isEnabled;
                }
            } else if (state.selectFunctionPulldown === selectFunctionPulldownItems['ORG_ADMIN'] &&
                (state.selectTab === tabLists['SECOND'])) {
                // 組織内管理の処理依頼内容設定である場合
                for (let i = deleteCategoryRow; i < state.categoryListItems.length - 1; i++) {
                    // 削除する行まで表示順以外のstate.categoryListItems内データの格納場所を一段ずつ上げる
                    state.categoryListItems[i].categoryId = state.categoryListItems[i + 1].categoryId;
                    state.categoryListItems[i].processCode = state.categoryListItems[i + 1].processCode;
                    state.categoryListItems[i].categoryName = state.categoryListItems[i + 1].categoryName;
                    state.categoryListItems[i].isEnabled = state.categoryListItems[i + 1].isEnabled;
                }

                if (isNew) {
                    // 新規カテゴリフラグがtrueの場合
                    // state.categoryListItemsの最後のデータを削除する
                    state.categoryListItems.pop();
                } else {
                    // 新規カテゴリフラグがfalseの場合
                    // state.categoryListItemsの最後に一時保存したデータを設定する
                    state.categoryListItems[state.categoryListItems.length - 1].categoryId = deleteData.categoryId;
                    state.categoryListItems[state.categoryListItems.length - 1].processCode = deleteData.processCode;
                    state.categoryListItems[state.categoryListItems.length - 1].categoryName = deleteData.categoryName;
                    state.categoryListItems[state.categoryListItems.length - 1].isEnabled = deleteData.isEnabled;
                }
            }

            // 削除確認ダイアログをfalseにする
            state.categoryDeleteDialog[dialogId] = false;

            // ローダーをfalseにする
            state.isDeleteRow = false;

            // 行削除によってstate.categoryListItemsのデータが無くなった場合
            // ※DBから取得したデータを削除した場合は保持しているため、この条件に当てはまるのは追加したデータのみだった場合
            if (state.categoryListItems.length === 0) {
                // カテゴリ設定登録ボタン表示フラグをfalseに設定
                state.displayProcessCategoryButton = false;
            }
        }

        /**
         * 発番組織のラベル表示
         * 
         * @param orgId 組織ID
         * @return 組織名称
         */
        function displayOrg(orgId: string) {
            // 公文書の発番カテゴリであるかつデータ取得中ではない場合
            if (state.selectFunctionPulldown === selectFunctionPulldownItems['OFFICIAL_DOCUMENTS']
                &&
                state.selectTab === tabLists['SECOND']
                &&
                !state.isLoading
            ) {
                // 組織IDの配列を作成
                const orgIdList = state.orgPulldownItems.map(item => item['value']);

                // 発番の組織IDに一致する組織が存在するか
                if (orgIdList.includes(orgId)) {
                    // state.orgPulldownItemsからorgIdに一致する組織名称のキーを取得
                    const index = state.orgPulldownItems.findIndex(({value}) => value === orgId);
    
                    // 組織名称を返却する
                    return state.orgPulldownItems[index].text;
                } else {
                    // 「削除された組織」と返却する
                    return MessageConst.DELETED_ORG;
                }
            }
        }

        /**
         * 校種選択プルダウンの値が変更された際のイベント
         */
        function changeSchoolType() {
            // カテゴリデータ取得関数呼び出し
            getCategories();
        }

        /**
         * 教科選択プルダウンの値が変更された際のイベント
         */
        function changeCurriculum() {
            // カテゴリデータ取得関数呼び出し
            getCategories();
        }

        /**
         * 以下の変数を初期化する
         * ・パラメータ設定フォームデータ
         * ・カテゴリ一覧情報
         * ・行メニュー表示切替フラグ
         * ・バリデーション結果
         * ・バリデーションメッセージ
         * ・ファイル拡張子桁数エラーフラグ
         * ・校種/教科プルダウンデータ
         * ・カテゴリ設定登録ボタン表示フラグ
         */
        function resetProcessData() {
            // state.parametersの初期化
            state.parameters = {
                newArrivalDays: { parameterCode: '', value: '' },
                fileExtension: { parameterCode: '', value: [] },
                fileSize: { parameterCode: '', value: '' },
                fileCount: { parameterCode: '', value: '' },
                mailText: { parameterCode: '', value: '' },
                publishEndDays: { parameterCode: '', value: '' },
                basicFileSize: { parameterCode: '', value: '' },
                attachmentFileSize: { parameterCode: '', value: '' },
                attachmentDetailFileExtension: { parameterCode: '', value: [] },
                attachmentDetailFileSize: { parameterCode: '', value: '' },
                attachmentDetailFileCount: { parameterCode: '', value: '' },
                materialFileExtension: { parameterCode: '', value: [] },
                materialFileSize: { parameterCode: '', value: '' },
                materialFileCount: { parameterCode: '', value: '' },
                thumbnailFileExtension: { parameterCode: '', value: [] },
                thumbnailFileSize: { parameterCode: '', value: '' },
                bulkAddUsersFileSize: { parameterCode: '', value: '' },
                replyFileExtension: { parameterCode: '', value: [] },
                replyFileSize: { parameterCode: '', value: '' },
                replyFileCount: { parameterCode: '', value: '' },
                affiliationCodeFileSize: { parameterCode: '', value: '' },
                personnelChangeFileSize: { parameterCode: '', value: '' },
                notificationPasswordFileSize: { parameterCode: '', value: '' },
                notificationAddresseeFileSize: { parameterCode: '', value: '' },
                notificationTemplateFileSize: { parameterCode: '', value: '' },
                notificationDataTitle: { parameterCode: '', value: '' },
                notificationDataMessage: { parameterCode: '', value: '' },
                applicationFileExtension: { parameterCode: '', value: [] },
                applicationFileSize: { parameterCode: '', value: '' },
                attachFileExtension: { parameterCode: '', value: [] },
                attachFileSize: { parameterCode: '', value: '' },
                attachFileCount: { parameterCode: '', value: '' },
                deleteDays: { parameterCode: '', value: '' },
                teacherMailReuseDay: { parameterCode: '', value: '' },
                studentGeneralMailReuseDay: { parameterCode: '', value: '' }
            };

            // state.categoryListItemsの初期化
            state.categoryListItems = [];

            // state.visibleRowMenuの初期化
            state.visibleRowMenu = {};

            // state.schoolTypePulldownItemsの初期化
            state.schoolTypePulldownItems = [];

            // state.selectSchoolTypePulldownの初期化
            state.selectSchoolTypePulldown = '';

            // state.curriculumPulldownItemsの初期化
            state.curriculumPulldownItems = [];

            // state.selectCurriculumPulldownの初期化
            state.selectCurriculumPulldown = '';

            // カテゴリ設定登録ボタン表示フラグの初期化
            state.displayProcessCategoryButton = false;

            // 各バリデーション結果及びバリデーションメッセージ文字列を初期化
            state.isNewArrivalDaysValid = true;
            state.isFileExtensionValid = true;
            state.isFileSizeValid = true;
            state.isFileCountValid = true;
            state.isPublishEndDaysValid = true;
            state.isBasicFileSizeValid = true;
            state.isAttachmentFileSizeValid = true;
            state.isAttachmentDetailFileExtensionValid = true;
            state.isAttachmentDetailFileSizeValid = true;
            state.isAttachmentDetailFileCountValid = true;
            state.isMaterialFileExtensionValid = true;
            state.isMaterialFileSizeValid = true;
            state.isMaterialFileCountValid = true;
            state.isThumbnailFileExtensionValid = true;
            state.isThumbnailFileSizeValid = true;
            state.isBulkAddUsersFileSizeValid = true;
            state.isReplyFileExtensionValid = true;
            state.isReplyFileSizeValid = true;
            state.isReplyFileCountValid = true;
            state.isApplicationFileExtensionValid = true;
            state.isApplicationFileSizeValid = true;
            state.isAttachFileExtensionValid = true;
            state.isAttachFileSizeValid = true;
            state.isAttachFileCountValid = true;
            state.isAffiliationCodeFileSizeValid = true;
            state.isPersonnelChangeFileSizeValid = true;
            state.isNotificationPasswordFileSizeValid = true;
            state.isNotificationAddresseeFileSizeValid = true;
            state.isNotificationTemplateFileSizeValid = true;
            state.isNotificationDataTitleValid = true;
            state.isDeleteDaysValid = true;
            state.isTeacherMailReuseDayValid = true;
            state.isStudentGeneralMailReuseDayValid = true;
            state.newArrivalDaysErrorMessage = '';
            state.fileExtensionErrorMessage = '';
            state.fileSizeErrorMessage = '';
            state.fileCountErrorMessage = '';
            state.publishEndDaysErrorMessage = '';
            state.basicFileSizeErrorMessage = '';
            state.attachmentFileSizeErrorMessage = '';
            state.attachmentDetailFileExtensionErrorMessage = '';
            state.attachmentDetailFileSizeErrorMessage = '';
            state.attachmentDetailFileCountErrorMessage = '';
            state.materialFileExtensionErrorMessage = '';
            state.materialFileSizeErrorMessage = '';
            state.materialFileCountErrorMessage = '';
            state.thumbnailFileExtensionErrorMessage = '';
            state.thumbnailFileSizeErrorMessage = '';
            state.bulkAddUsersFileSizeErrorMessage = '';
            state.replyFileExtensionErrorMessage = '';
            state.replyFileSizeErrorMessage = '';
            state.replyFileCountErrorMessage = '';
            state.applicationFileExtensionErrorMessage = '';
            state.applicationFileSizeErrorMessage = '';
            state.attachFileExtensionErrorMessage = '';
            state.attachFileSizeErrorMessage = '';
            state.attachFileCountErrorMessage = '';
            state.affiliationCodeFileSizeErrorMessage = '';
            state.personnelChangeFileSizeErrorMessage = '';
            state.notificationPasswordFileSizeErrorMessage = '';
            state.notificationAddresseeFileSizeErrorMessage = '';
            state.notificationTemplateFileSizeErrorMessage = '';
            state.notificationDataTitleErrorMessage = '';
            state.deleteDaysErrorMessage = '';
            state.teacherMailReuseDayErrorMessage = '';
            state.studentGeneralMailReuseDayErrorMessage = '';

            // 各ファイル拡張子桁数エラーフラグ初期化
            state.isFileExtensionCountError = false;
            state.isAttachmentDetailFileExtensionCountError = false;
            state.isMaterialFileExtensionCountError = false;
            state.isThumbnailFileExtensionCountError = false;
            state.isReplyFileExtensionCountError = false;
            state.isApplicationFileExtensionCountError = false;
            state.isAttachFileExtensionCountError = false;
        }

        /**
         * 新着日数の入力イベント及びバリデーション
         */
        function validateNewArrivalDays() {
            // 値がnullである場合
            if (state.parameters.newArrivalDays.value === null) {
                // 空文字を設定する
                state.parameters.newArrivalDays.value = '';
            }

            // 先頭の0をすべて削除する
            state.parameters.newArrivalDays.value = state.parameters.newArrivalDays.value.replace(/^0+/, '');

            // 新着日数のバリデーションを行う
            for (let index = 0; index < newArrivalDayRules.length; index++) {
                const rule: any = newArrivalDayRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    // エラーメッセージを設定する
                    state.newArrivalDaysErrorMessage = valid;
                    // 新着日数のバリデーション結果フラグをfalseにする
                    state.isNewArrivalDaysValid = false;
                    break;
                } else {
                    // エラーなし
                    // エラーメッセージに空文字設定する
                    state.newArrivalDaysErrorMessage = '';
                    // 新着日数のバリデーション結果フラグをtrueにする
                    state.isNewArrivalDaysValid = true;
                }
            }
        }

        /**
         * 掲載期間終了日数の入力イベント及びバリデーション
         */
        function validatePublishEndDays() {
            // 値がnullである場合
            if (state.parameters.publishEndDays.value === null) {
                // 空文字を設定する
                state.parameters.publishEndDays.value = '';
            }

            // 先頭の0をすべて削除する
            state.parameters.publishEndDays.value = state.parameters.publishEndDays.value.replace(/^0+/, '');

            // 掲載期間終了日数のバリデーションを行う
            for (let index = 0; index < publishEndDayRules.length; index++) {
                const rule: any = publishEndDayRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    // エラーメッセージを設定する
                    state.publishEndDaysErrorMessage = valid;
                    // 掲載期間終了日数のバリデーション結果フラグをfalseにする
                    state.isPublishEndDaysValid = false;
                    break;
                } else {
                    // エラーなし
                    // エラーメッセージに空文字設定する
                    state.publishEndDaysErrorMessage = '';
                    // 掲載期間終了日数のバリデーション結果フラグをtrueにする
                    state.isPublishEndDaysValid = true;
                }
            }
        }

        /**
         * ファイル拡張子の入力イベント及びバリデーション
         */
        function validateFileExtension() {
            // ファイル拡張子のバリデーションを行う
            for (let index = 0; index < fileExtensionRules.length; index++) {
                const rule: any = fileExtensionRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    // エラーメッセージを設定する
                    state.fileExtensionErrorMessage = valid;
                    // ファイル拡張子のバリデーション結果フラグをfalseにする
                    state.isFileExtensionValid = false;
                    break;
                } else {
                    // エラーなし
                    // エラーメッセージに空文字設定する
                    state.fileExtensionErrorMessage = '';
                    // ファイル拡張子のバリデーション結果フラグをtrueにする
                    state.isFileExtensionValid = true;
                }
            }
        }

        /**
         * 最大ファイルサイズの入力イベント及びバリデーション
         */
        function validateFileSize() {
            // 値がnullである場合
            if (state.parameters.fileSize.value === null) {
                // 空文字を設定する
                state.parameters.fileSize.value = '';
            }

            // 先頭の0をすべて削除する
            state.parameters.fileSize.value = state.parameters.fileSize.value.replace(/^0+/, '');

            // 最大ファイルサイズのバリデーションを行う
            for (let index = 0; index < fileSizeRules.length; index++) {
                const rule: any = fileSizeRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    // エラーメッセージを設定する
                    state.fileSizeErrorMessage = valid;
                    // 最大ファイルサイズのバリデーション結果フラグをfalseにする
                    state.isFileSizeValid = false;
                    break;
                } else {
                    // エラーなし
                    // エラーメッセージに空文字設定する
                    state.fileSizeErrorMessage = '';
                    // 最大ファイルサイズのバリデーション結果フラグをtrueにする
                    state.isFileSizeValid = true;
                }
            }
        }

        /**
         * 最大ファイル数の入力イベント及びバリデーション
         */
        function validateFileCount() {
            // 値がnullである場合
            if (state.parameters.fileCount.value === null) {
                // 空文字を設定する
                state.parameters.fileCount.value = '';
            }

            // 先頭の0をすべて削除する
            state.parameters.fileCount.value = state.parameters.fileCount.value.replace(/^0+/, '');

            // 最大ファイル数のバリデーションを行う
            for (let index = 0; index < fileCountRules.length; index++) {
                const rule: any = fileCountRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    // エラーメッセージを設定する
                    state.fileCountErrorMessage = valid;
                    // 最大ファイル数のバリデーション結果フラグをfalseにする
                    state.isFileCountValid = false;
                    break;
                } else {
                    // エラーなし
                    // エラーメッセージに空文字設定する
                    state.fileCountErrorMessage = '';
                    // 最大ファイル数のバリデーション結果フラグをtrueにする
                    state.isFileCountValid = true;
                }
            }
        }

        /**
         * 基本データファイル最大ファイルサイズの入力イベント及びバリデーション
         */
        function validateBasicFileSize() {
            // 値がnullである場合
            if (state.parameters.basicFileSize.value === null) {
                // 空文字を設定する
                state.parameters.basicFileSize.value = '';
            }

            // 先頭の0をすべて削除する
            state.parameters.basicFileSize.value = state.parameters.basicFileSize.value.replace(/^0+/, '');

            // 基本データファイル最大ファイルサイズのバリデーションを行う
            for (let index = 0; index < basicFileSizeRules.length; index++) {
                const rule: any = basicFileSizeRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    // エラーメッセージを設定する
                    state.basicFileSizeErrorMessage = valid;
                    // 基本データファイル最大ファイルサイズのバリデーション結果フラグをfalseにする
                    state.isBasicFileSizeValid = false;
                    break;
                } else {
                    // エラーなし
                    // エラーメッセージに空文字設定する
                    state.basicFileSizeErrorMessage = '';
                    // 基本データファイル最大ファイルサイズのバリデーション結果フラグをtrueにする
                    state.isBasicFileSizeValid = true;
                }
            }
        }

        /**
         * 添付データファイル最大ファイルサイズの入力イベント及びバリデーション
         */
        function validateAttachmentFileSize() {
            // 値がnullである場合
            if (state.parameters.attachmentFileSize.value === null) {
                // 空文字を設定する
                state.parameters.attachmentFileSize.value = '';
            }

            // 先頭の0をすべて削除する
            state.parameters.attachmentFileSize.value = state.parameters.attachmentFileSize.value.replace(/^0+/, '');

            // 添付データファイル最大ファイルサイズのバリデーションを行う
            for (let index = 0; index < attachmentFileSizeRules.length; index++) {
                const rule: any = attachmentFileSizeRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    // エラーメッセージを設定する
                    state.attachmentFileSizeErrorMessage = valid;
                    // 添付データファイル最大ファイルサイズのバリデーション結果フラグをfalseにする
                    state.isAttachmentFileSizeValid = false;
                    break;
                } else {
                    // エラーなし
                    // エラーメッセージに空文字設定する
                    state.attachmentFileSizeErrorMessage = '';
                    // 添付データファイル最大ファイルサイズのバリデーション結果フラグをtrueにする
                    state.isAttachmentFileSizeValid = true;
                }
            }
        }

        /**
         * 送付ファイルファイル拡張子の入力イベント及びバリデーション
         */
        function validateAttachmentDetailFileExtension() {
            // 送付ファイルファイル拡張子のバリデーションを行う
            for (let index = 0; index < attachmentDetailFileExtensionRules.length; index++) {
                const rule: any = attachmentDetailFileExtensionRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    // エラーメッセージを設定する
                    state.attachmentDetailFileExtensionErrorMessage = valid;
                    // 送付ファイルファイル拡張子のバリデーション結果フラグをfalseにする
                    state.isAttachmentDetailFileExtensionValid = false;
                    break;
                } else {
                    // エラーなし
                    // エラーメッセージに空文字設定する
                    state.attachmentDetailFileExtensionErrorMessage = '';
                    // 送付ファイルファイル拡張子のバリデーション結果フラグをtrueにする
                    state.isAttachmentDetailFileExtensionValid = true;
                }
            }
        }

        /**
         * 送付ファイル最大ファイルサイズの入力イベント及びバリデーション
         */
        function validateAttachmentDetailFileSize() {
            // 値がnullである場合
            if (state.parameters.attachmentDetailFileSize.value === null) {
                // 空文字を設定する
                state.parameters.attachmentDetailFileSize.value = '';
            }

            // 先頭の0をすべて削除する
            state.parameters.attachmentDetailFileSize.value = state.parameters.attachmentDetailFileSize.value.replace(/^0+/, '');

            // 送付ファイル最大ファイルサイズのバリデーションを行う
            for (let index = 0; index < attachmentDetailFileSizeRules.length; index++) {
                const rule: any = attachmentDetailFileSizeRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    // エラーメッセージを設定する
                    state.attachmentDetailFileSizeErrorMessage = valid;
                    // 送付ファイル最大ファイルサイズのバリデーション結果フラグをfalseにする
                    state.isAttachmentDetailFileSizeValid = false;
                    break;
                } else {
                    // エラーなし
                    // エラーメッセージに空文字設定する
                    state.attachmentDetailFileSizeErrorMessage = '';
                    // 送付ファイル最大ファイルサイズのバリデーション結果フラグをtrueにする
                    state.isAttachmentDetailFileSizeValid = true;
                }
            }
        }

        /**
         * 送付ファイル最大ファイル数の入力イベント及びバリデーション
         */
        function validateAttachmentDetailFileCount() {
            // 値がnullである場合
            if (state.parameters.attachmentDetailFileCount.value === null) {
                // 空文字を設定する
                state.parameters.attachmentDetailFileCount.value = '';
            }

            // 先頭の0をすべて削除する
            state.parameters.attachmentDetailFileCount.value = state.parameters.attachmentDetailFileCount.value.replace(/^0+/, '');

            // 送付ファイル最大ファイル数のバリデーションを行う
            for (let index = 0; index < attachmentDetailFileCountRules.length; index++) {
                const rule: any = attachmentDetailFileCountRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    // エラーメッセージを設定する
                    state.attachmentDetailFileCountErrorMessage = valid;
                    // 送付ファイル最大ファイル数のバリデーション結果フラグをfalseにする
                    state.isAttachmentDetailFileCountValid = false;
                    break;
                } else {
                    // エラーなし
                    // エラーメッセージに空文字設定する
                    state.attachmentDetailFileCountErrorMessage = '';
                    // 送付ファイル最大ファイル数のバリデーション結果フラグをtrueにする
                    state.isAttachmentDetailFileCountValid = true;
                }
            }
        }

        /**
         * 教材ファイルファイル拡張子の入力イベント及びバリデーション
         */
        function validateMaterialFileExtension() {
            // 教材ファイルファイル拡張子のバリデーションを行う
            for (let index = 0; index < materialFileExtensionRules.length; index++) {
                const rule: any = materialFileExtensionRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    // エラーメッセージを設定する
                    state.materialFileExtensionErrorMessage = valid;
                    // 教材ファイルファイル拡張子のバリデーション結果フラグをfalseにする
                    state.isMaterialFileExtensionValid = false;
                    break;
                } else {
                    // エラーなし
                    // エラーメッセージに空文字を設定する
                    state.materialFileExtensionErrorMessage = '';
                    // 教材ファイルファイル拡張子のバリデーション結果フラグをtrueにする
                    state.isMaterialFileExtensionValid = true;
                }
            }
        }

        /**
         * 教材ファイル最大ファイルサイズの入力イベント及びバリデーション
         */
        function validateMaterialFileSize() {
            // 値がnullである場合
            if (state.parameters.materialFileSize.value === null) {
                // 空文字を設定する
                state.parameters.materialFileSize.value = '';
            }

            // 先頭の0をすべて削除する
            state.parameters.materialFileSize.value = state.parameters.materialFileSize.value.replace(/^0+/, '');

            // 教材ファイル最大ファイルサイズのバリデーションを行う
            for (let index = 0; index < materialFileSizeRules.length; index++) {
                const rule: any = materialFileSizeRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    // エラーメッセージを設定する
                    state.materialFileSizeErrorMessage = valid;
                    // 教材ファイル最大ファイルサイズのバリデーション結果フラグをfalseにする
                    state.isMaterialFileSizeValid = false;
                    break;
                } else {
                    // エラーなし
                    // エラーメッセージに空文字を設定する
                    state.materialFileSizeErrorMessage = '';
                    // 教材ファイル最大ファイルサイズのバリデーション結果フラグをtrueにする
                    state.isMaterialFileSizeValid = true;
                }
            }
        }

        /**
         * 教材ファイル最大ファイル数の入力イベント及びバリデーション
         */
        function validateMaterialFileCount() {
            // 値がnullである場合
            if (state.parameters.materialFileCount.value === null) {
                // 空文字を設定する
                state.parameters.materialFileCount.value = '';
            }

            // 先頭の0をすべて削除する
            state.parameters.materialFileCount.value = state.parameters.materialFileCount.value.replace(/^0+/, '');

            // 教材ファイル最大ファイル数のバリデーションを行う
            for (let index = 0; index < materialFileCountRules.length; index++) {
                const rule: any = materialFileCountRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    // エラーメッセージを設定する
                    state.materialFileCountErrorMessage = valid;
                    // 教材ファイル最大ファイル数のバリデーション結果フラグをfalseにする
                    state.isMaterialFileCountValid = false;
                    break;
                } else {
                    // エラーなし
                    // エラーメッセージに空文字を設定する
                    state.materialFileCountErrorMessage = '';
                    // 教材ファイル最大ファイル数のバリデーション結果フラグをtrueにする
                    state.isMaterialFileCountValid = true;
                }
            }
        }

        /**
         * サムネイルファイルファイル拡張子の入力イベント及びバリデーション
         */
        function validateThumbnailFileExtension() {
            // サムネイルファイルファイル拡張子のバリデーションを行う
            for (let index = 0; index < thumbnailFileExtensionRules.length; index++) {
                const rule: any = thumbnailFileExtensionRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    // エラーメッセージを設定する
                    state.thumbnailFileExtensionErrorMessage = valid;
                    // サムネイルファイルファイル拡張子のバリデーション結果フラグをfalseにする
                    state.isThumbnailFileExtensionValid = false;
                    break;
                } else {
                    // エラーなし
                    // エラーメッセージに空文字を設定する
                    state.thumbnailFileExtensionErrorMessage = '';
                    // サムネイルファイルファイル拡張子のバリデーション結果フラグをtrueにする
                    state.isThumbnailFileExtensionValid = true;
                }
            }
        }

        /**
         * サムネイルファイル最大ファイルサイズの入力イベント及びバリデーション
         */
        function validateThumbnailFileSize() {
            // 値がnullである場合
            if (state.parameters.thumbnailFileSize.value === null) {
                // 空文字を設定する
                state.parameters.thumbnailFileSize.value = '';
            }

            // 先頭の0をすべて削除する
            state.parameters.thumbnailFileSize.value = state.parameters.thumbnailFileSize.value.replace(/^0+/, '');

            // サムネイルファイル最大ファイルサイズのバリデーションを行う
            for (let index = 0; index < thumbnailFileSizeRules.length; index++) {
                const rule: any = thumbnailFileSizeRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    // エラーメッセージを設定する
                    state.thumbnailFileSizeErrorMessage = valid;
                    // サムネイルファイル最大ファイルサイズのバリデーション結果フラグをfalseにする
                    state.isThumbnailFileSizeValid = false;
                    break;
                } else {
                    // エラーなし
                    // エラーメッセージに空文字を設定する
                    state.thumbnailFileSizeErrorMessage = '';
                    // サムネイルファイル最大ファイルサイズのバリデーション結果フラグをtrueにする
                    state.isThumbnailFileSizeValid = true;
                }
            }
        }

        /**
         * （システム管理・組織内管理：ユーザー管理）一括登録ファイル最大ファイルサイズの入力イベント及びバリデーション
         */
        function validateBulkAddUsersFileSize() {
            // 値がnullである場合
            if (state.parameters.bulkAddUsersFileSize.value === null) {
                // 空文字を設定する
                state.parameters.bulkAddUsersFileSize.value = '';
            }

            // 先頭の0をすべて削除する
            state.parameters.bulkAddUsersFileSize.value = state.parameters.bulkAddUsersFileSize.value.replace(/^0+/, '');

            // 一括登録ファイル最大ファイルサイズのバリデーションを行う
            for (let index = 0; index < bulkAddUsersFileSizeRules.length; index++) {
                const rule: any = bulkAddUsersFileSizeRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    // エラーメッセージを設定する
                    state.bulkAddUsersFileSizeErrorMessage = valid;
                    // 一括登録ファイル最大ファイルサイズのバリデーション結果フラグをfalseにする
                    state.isBulkAddUsersFileSizeValid = false;
                    break;
                } else {
                    // エラーなし
                    // エラーメッセージに空文字を設定する
                    state.bulkAddUsersFileSizeErrorMessage = '';
                    // 一括登録ファイル最大ファイルサイズのバリデーション結果フラグをtrueにする
                    state.isBulkAddUsersFileSizeValid = true;
                }
            }
        }

        /**
         * （システム管理：処理依頼管理）返信ファイルファイル拡張子の入力イベント及びバリデーション
         */
        function validateReplyFileExtension() {
            // 返信ファイルファイル拡張子のバリデーションを行う
            for (let index = 0; index < replyFileExtensionRules.length; index++) {
                const rule: any = replyFileExtensionRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    // エラーメッセージを設定する
                    state.replyFileExtensionErrorMessage = valid;
                    // 返信ファイルファイル拡張子のバリデーション結果フラグをfalseにする
                    state.isReplyFileExtensionValid = false;
                    break;
                } else {
                    // エラーなし
                    // エラーメッセージに空文字を設定する
                    state.replyFileExtensionErrorMessage = '';
                    // 返信ファイルファイル拡張子のバリデーション結果フラグをtrueにする
                    state.isReplyFileExtensionValid = true;
                }
            }
        }

        /**
         * （システム管理：処理依頼管理）返信ファイル最大ファイルサイズの入力イベント及びバリデーション
         */
        function validateReplyFileSize() {
            // 値がnullである場合
            if (state.parameters.replyFileSize.value === null) {
                // 空文字を設定する
                state.parameters.replyFileSize.value = '';
            }

            // 先頭の0をすべて削除する
            state.parameters.replyFileSize.value = state.parameters.replyFileSize.value.replace(/^0+/, '');

            // 返信ファイル最大ファイルサイズのバリデーションを行う
            for (let index = 0; index < replyFileSizeRules.length; index++) {
                const rule: any = replyFileSizeRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    // エラーメッセージを設定する
                    state.replyFileSizeErrorMessage = valid;
                    // 返信ファイル最大ファイルサイズのバリデーション結果フラグをfalseにする
                    state.isReplyFileSizeValid = false;
                    break;
                } else {
                    // エラーなし
                    // エラーメッセージに空文字を設定する
                    state.replyFileSizeErrorMessage = '';
                    // 返信ファイル最大ファイルサイズのバリデーション結果フラグをtrueにする
                    state.isReplyFileSizeValid = true;
                }
            }
        }

        /**
         * （システム管理：処理依頼管理）返信ファイル最大ファイル数の入力イベント及びバリデーション
         */
        function validateReplyFileCount() {
            // 値がnullである場合
            if (state.parameters.replyFileCount.value === null) {
                // 空文字を設定する
                state.parameters.replyFileCount.value = '';
            }

            // 先頭の0をすべて削除する
            state.parameters.replyFileCount.value = state.parameters.replyFileCount.value.replace(/^0+/, '');

            // 返信ファイル最大ファイル数のバリデーションを行う
            for (let index = 0; index < replyFileCountRules.length; index++) {
                const rule: any = replyFileCountRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    // エラーメッセージを設定する
                    state.replyFileCountErrorMessage = valid;
                    // 返信ファイル最大ファイル数のバリデーション結果フラグをfalseにする
                    state.isReplyFileCountValid = false;
                    break;
                } else {
                    // エラーなし
                    // エラーメッセージに空文字を設定する
                    state.replyFileCountErrorMessage = '';
                    // 返信ファイル最大ファイル数のバリデーション結果フラグをtrueにする
                    state.isReplyFileCountValid = true;
                }
            }
        }

        /**
         * （システム管理：年度更新）所属コードファイル最大ファイルサイズの入力イベント及びバリデーション
         */
        function validateAffiliationCodeFileSize() {
            // 入力値がnullである場合
            if (state.parameters.affiliationCodeFileSize.value === null) {
                // 空文字を設定する
                state.parameters.affiliationCodeFileSize.value = '';
            }

            // 先頭の0をすべて削除する
            state.parameters.affiliationCodeFileSize.value = state.parameters.affiliationCodeFileSize.value.replace(/^0+/, '');

            // 所属コードファイル最大ファイルサイズのバリデーションを行う
            for (let index = 0; index < affiliationCodeFileSizeRules.length; index++) {
                const rule: any = affiliationCodeFileSizeRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    // エラーメッセージを設定する
                    state.affiliationCodeFileSizeErrorMessage = valid;
                    // 所属コードファイル最大ファイルサイズのバリデーション結果フラグをfalseにする
                    state.isAffiliationCodeFileSizeValid = false;
                    break;
                } else {
                    // エラーなし
                    // エラーメッセージに空文字を設定する
                    state.affiliationCodeFileSizeErrorMessage = '';
                    // 所属コードファイル最大ファイルサイズのバリデーション結果フラグをtrueにする
                    state.isAffiliationCodeFileSizeValid = true;
                }
            }
        }

        /**
         * （システム管理：年度更新）人事異動データファイル最大ファイルサイズの入力イベント及びバリデーション
         */
        function validatePersonnelChangeFileSize() {
            // 入力値がnullである場合
            if (state.parameters.personnelChangeFileSize.value === null) {
                // 空文字を設定する
                state.parameters.personnelChangeFileSize.value = '';
            }

            // 先頭の0をすべて削除する
            state.parameters.personnelChangeFileSize.value = state.parameters.personnelChangeFileSize.value.replace(/^0+/, '');

            // 人事異動データファイル最大ファイルサイズのバリデーションを行う
            for (let index = 0; index < personnelChangeFileSizeRules.length; index++) {
                const rule: any = personnelChangeFileSizeRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    // エラーメッセージを設定する
                    state.personnelChangeFileSizeErrorMessage = valid;
                    // 人事異動データ最大ファイルサイズのバリデーション結果フラグをfalseにする
                    state.isPersonnelChangeFileSizeValid = false;
                    break;
                } else {
                    // エラーなし
                    // エラーメッセージに空文字を設定する
                    state.personnelChangeFileSizeErrorMessage = '';
                    // 人事異動データ最大ファイルサイズのバリデーション結果フラグをtrueにする
                    state.isPersonnelChangeFileSizeValid = true;
                }
            }
        }

        /**
         * （システム管理：年度更新）パスワード情報ファイル最大ファイルサイズの入力イベント及びバリデーション
         */
        function validateNotificationPasswordFileSize() {
            // 入力値がnullである場合
            if (state.parameters.notificationPasswordFileSize.value === null) {
                // 空文字を設定する
                state.parameters.notificationPasswordFileSize.value = '';
            }

            // 先頭の0をすべて削除する
            state.parameters.notificationPasswordFileSize.value = state.parameters.notificationPasswordFileSize.value.replace(/^0+/, '');

            // パスワード情報ファイル最大ファイルサイズのバリデーションを行う
            for (let index = 0; index < notificationPasswordFileSizeRules.length; index++) {
                const rule: any = notificationPasswordFileSizeRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    // エラーメッセージを設定する
                    state.notificationPasswordFileSizeErrorMessage = valid;
                    // パスワード情報ファイル最大ファイルサイズのバリデーション結果フラグをfalseにする
                    state.isNotificationPasswordFileSizeValid = false;
                    break;
                } else {
                    // エラーなし
                    // エラーメッセージに空文字を設定する
                    state.notificationPasswordFileSizeErrorMessage = '';
                    // パスワード情報ファイル最大ファイルサイズのバリデーション結果フラグをtrueにする
                    state.isNotificationPasswordFileSizeValid = true;
                }
            }
        }

        /**
         * （システム管理：年度更新）宛名ファイル最大ファイルサイズの入力イベント及びバリデーション
         */
        function validateNotificationAddresseeFileSize() {
            // 入力値がnullである場合
            if (state.parameters.notificationAddresseeFileSize.value === null) {
                // 空文字を設定する
                state.parameters.notificationAddresseeFileSize.value = '';
            }

            // 先頭の0をすべて削除する
            state.parameters.notificationAddresseeFileSize.value = state.parameters.notificationAddresseeFileSize.value.replace(/^0+/, '');

            // 宛名ファイル最大ファイルサイズのバリデーションを行う
            for (let index = 0; index < notificationAddresseeFileSizeRules.length; index++) {
                const rule: any = notificationAddresseeFileSizeRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    // エラーメッセージを設定する
                    state.notificationAddresseeFileSizeErrorMessage = valid;
                    // 宛名ファイル最大ファイルサイズのバリデーション結果フラグをfalseにする
                    state.isNotificationAddresseeFileSizeValid = false;
                    break;
                } else {
                    // エラーなし
                    // エラーメッセージに空文字を設定する
                    state.notificationAddresseeFileSizeErrorMessage = '';
                    // 宛名ファイル最大ファイルサイズのバリデーション結果フラグをtrueにする
                    state.isNotificationAddresseeFileSizeValid = true;
                }
            }
        }

        /**
         * （システム管理：年度更新）雛形ファイル最大ファイルサイズの入力イベント及びバリデーション
         */
        function validateNotificationTemplateFileSize() {
            // 入力値がnullである場合
            if (state.parameters.notificationTemplateFileSize.value === null) {
                // 空文字を設定する
                state.parameters.notificationTemplateFileSize.value = '';
            }

            // 先頭の0をすべて削除する
            state.parameters.notificationTemplateFileSize.value = state.parameters.notificationTemplateFileSize.value.replace(/^0+/, '');

            // 雛形ファイル最大ファイルサイズのバリデーションを行う
            for (let index = 0; index < notificationTemplateFileSizeRules.length; index++) {
                const rule: any = notificationTemplateFileSizeRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    // エラーメッセージを設定する
                    state.notificationTemplateFileSizeErrorMessage = valid;
                    // 雛形ファイル最大ファイルサイズのバリデーション結果フラグをfalseにする
                    state.isNotificationTemplateFileSizeValid = false;
                    break;
                } else {
                    // エラーなし
                    // エラーメッセージに空文字を設定する
                    state.notificationTemplateFileSizeErrorMessage = '';
                    // 雛形ファイル最大ファイルサイズのバリデーション結果フラグをtrueにする
                    state.isNotificationTemplateFileSizeValid = true;
                }
            }
        }

        /**
         * （システム管理：年度更新）基本データファイルタイトルの入力イベント及びバリデーション
         */
        function validateNotificationDataTitle() {
            // 入力値がnullである場合
            if (state.parameters.notificationDataTitle.value === null) {
                // 空文字を設定する
                state.parameters.notificationDataTitle.value = '';
            }

            // 基本データファイルタイトルのバリデーションを行う
            for (let index = 0; index < notificationDataTitleRules.length; index++) {
                const rule: any = notificationDataTitleRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    // エラーメッセージを設定する
                    state.notificationDataTitleErrorMessage = valid;
                    // 基本データファイルタイトルのバリデーション結果フラグをtrueにする
                    state.isNotificationDataTitleValid = false;
                    break;
                } else {
                    // エラーなし
                    // エラーメッセージに空文字を設定する
                    state.notificationDataTitleErrorMessage = '';
                    // 基本データファイルタイトルのバリデーション結果フラグをtrueにする
                    state.isNotificationDataTitleValid = true;
                }
            }
        }

        /**
         * （組織内管理：処理依頼管理）申請ファイルファイル拡張子の入力イベント及びバリデーション
         */
        function validateApplicationFileExtension() {
            // 申請ファイルファイル拡張子のバリデーションを行う
            for (let index = 0; index < applicationFileExtensionRules.length; index++) {
                const rule: any = applicationFileExtensionRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    // エラーメッセージを設定する
                    state.applicationFileExtensionErrorMessage = valid;
                    // 申請ファイルファイル拡張子のバリデーション結果フラグをfalseにする
                    state.isApplicationFileExtensionValid = false;
                    break;
                } else {
                    // エラーなし
                    // エラーメッセージに空文字を設定する
                    state.applicationFileExtensionErrorMessage = '';
                    // 申請ファイルファイル拡張子のバリデーション結果フラグをtrueにする
                    state.isApplicationFileExtensionValid = true;
                }
            }
        }

        /**
         * （組織内管理：処理依頼管理）申請ファイル最大ファイルサイズの入力イベント及びバリデーション
         */
        function validateApplicationFileSize() {
            // 値がnullである場合
            if (state.parameters.applicationFileSize.value === null) {
                // 空文字を設定する
                state.parameters.applicationFileSize.value = '';
            }

            // 先頭の0をすべて削除する
            state.parameters.applicationFileSize.value = state.parameters.applicationFileSize.value.replace(/^0+/, '');

            // 申請ファイル最大ファイルサイズのバリデーションを行う
            for (let index = 0; index < applicationFileSizeRules.length; index++) {
                const rule: any = applicationFileSizeRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    // エラーメッセージを設定する
                    state.applicationFileSizeErrorMessage = valid;
                    // 申請ファイル最大ファイルサイズのバリデーション結果フラグをfalseにする
                    state.isApplicationFileSizeValid = false;
                    break;
                } else {
                    // エラーなし
                    // エラーメッセージに空文字を設定する
                    state.applicationFileSizeErrorMessage = '';
                    // 申請ファイル最大ファイルサイズのバリデーション結果フラグをtrueにする
                    state.isApplicationFileSizeValid = true;
                }
            }
        }

        /**
         * （組織内管理：処理依頼管理）添付ファイルファイル拡張子の入力イベント及びバリデーション
         */
        function validateAttachFileExtension() {
            // 添付ファイルファイル拡張子のバリデーションを行う
            for (let index = 0; index < attachFileExtensionRules.length; index++) {
                const rule: any = attachFileExtensionRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    // エラーメッセージを設定する
                    state.attachFileExtensionErrorMessage = valid;
                    // 添付ファイルファイル拡張子のバリデーション結果フラグをfalseにする
                    state.isAttachFileExtensionValid = false;
                    break;
                } else {
                    // エラーなし
                    // エラーメッセージに空文字を設定する
                    state.attachFileExtensionErrorMessage = '';
                    // 添付ファイルファイル拡張子のバリデーション結果フラグをtrueにする
                    state.isAttachFileExtensionValid = true;
                }
            }
        }

        /**
         * （組織内管理：処理依頼管理）添付ファイル最大ファイルサイズの入力イベント及びバリデーション
         */
        function validateAttachFileSize() {
            // 値がnullである場合
            if (state.parameters.attachFileSize.value === null) {
                // 空文字を設定する
                state.parameters.attachFileSize.value = '';
            }

            // 先頭の0をすべて削除する
            state.parameters.attachFileSize.value = state.parameters.attachFileSize.value.replace(/^0+/, '');

            // 添付ファイル最大ファイルサイズのバリデーションを行う
            for (let index = 0; index < attachFileSizeRules.length; index++) {
                const rule: any = attachFileSizeRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    // エラーメッセージを設定する
                    state.attachFileSizeErrorMessage = valid;
                    // 添付ファイル最大ファイルサイズのバリデーション結果フラグをfalseにする
                    state.isAttachFileSizeValid = false;
                    break;
                } else {
                    // エラーなし
                    // エラーメッセージに空文字を設定する
                    state.attachFileSizeErrorMessage = '';
                    // 添付ファイル最大ファイルサイズのバリデーション結果フラグをtrueにする
                    state.isAttachFileSizeValid = true;
                }
            }
        }

        /**
         * （組織内管理：処理依頼管理）添付ファイル最大ファイル数の入力イベント及びバリデーション
         */
        function validateAttachFileCount() {
            // 値がnullである場合
            if (state.parameters.attachFileCount.value === null) {
                // 空文字を設定する
                state.parameters.attachFileCount.value = '';
            }

            // 先頭の0をすべて削除する
            state.parameters.attachFileCount.value = state.parameters.attachFileCount.value.replace(/^0+/, '');

            // 添付ファイル最大ファイル数のバリデーションを行う
            for (let index = 0; index < attachFileCountRules.length; index++) {
                const rule: any = attachFileCountRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    // エラーメッセージを設定する
                    state.attachFileCountErrorMessage = valid;
                    // 添付ファイル最大ファイル数のバリデーション結果フラグをfalseにする
                    state.isAttachFileCountValid = false;
                    break;
                } else {
                    // エラーなし
                    // エラーメッセージに空文字を設定する
                    state.attachFileCountErrorMessage = '';
                    // 添付ファイル最大ファイル数のバリデーション結果フラグをtrueにする
                    state.isAttachFileCountValid = true;
                }
            }
        }

        /**
         * （共通（システム管理・組織内管理）：パラメータ設定）メールエイリアス削除日数の入力イベント及びバリデーション
         * 
         * @param isInput 入力イベントか（true:入力イベントである, false:入力イベントではない）
         */
        function validateDeleteDays(isInput: boolean) {
            // 値がnullである場合
            if (state.parameters.deleteDays.value === null) {
                // 空文字を設定する
                state.parameters.deleteDays.value = '';
            }

            // 先頭の0をすべて削除する
            state.parameters.deleteDays.value = state.parameters.deleteDays.value.replace(/^0+/, '');

            // メールエイリアス削除日数のバリデーションを行う
            for (let index = 0; index < deleteDayRules.length; index++) {
                const rule: any = deleteDayRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    // エラーメッセージを設定する
                    state.deleteDaysErrorMessage = valid;
                    // メールエイリアス削除日数のバリデーション結果フラグをfalseにする
                    state.isDeleteDaysValid = false;
                    break;
                } else {
                    // エラーなし
                    // エラーメッセージに空文字設定する
                    state.deleteDaysErrorMessage = '';
                    // メールエイリアス削除日数のバリデーション結果フラグをtrueにする
                    state.isDeleteDaysValid = true;
                }
            }

            // 画面の入力イベントによる呼び出しである場合
            if (isInput) {
                // メールアドレス凍結日数のバリデーションを行う
                validateTeacherMailReuseDay(false);
                validateStudentGeneralMailReuseDay(false);
            }
        }

        /**
         * （共通（システム管理・組織内管理）：パラメータ設定）a1、c1、s1アカウントメールアドレス凍結日数の入力イベント及びバリデーション
         * 
         * @param isInput 入力イベントか（true:入力イベントである, false:入力イベントではない）
         */
        function validateTeacherMailReuseDay(isInput: boolean) {
            // 値がnullである場合
            if (state.parameters.teacherMailReuseDay.value === null) {
                // 空文字を設定する
                state.parameters.teacherMailReuseDay.value = '';
            }

            // 先頭の0をすべて削除する
            state.parameters.teacherMailReuseDay.value = state.parameters.teacherMailReuseDay.value.replace(/^0+/, '');

            // a1、c1、s1アカウントメールアドレス凍結日数のバリデーションを行う
            for (let index = 0; index < teacherMailReuseDayRules.length; index++) {
                const rule: any = teacherMailReuseDayRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    // エラーメッセージを設定する
                    state.teacherMailReuseDayErrorMessage = valid;
                    // a1、c1、s1アカウントメールアドレス凍結日数のバリデーション結果フラグをfalseにする
                    state.isTeacherMailReuseDayValid = false;
                    break;
                } else {
                    // エラーなし
                    // エラーメッセージに空文字設定する
                    state.teacherMailReuseDayErrorMessage = '';
                    // a1、c1、s1アカウントメールアドレス凍結日数のバリデーション結果フラグをtrueにする
                    state.isTeacherMailReuseDayValid = true;
                }
            }

            // 画面の入力イベントによる呼び出しである場合
            if (isInput) {
                // メールエイリアス削除日数のバリデーションを行う
                validateDeleteDays(false);
            }
        }

        /**
         * （共通（システム管理・組織内管理）：パラメータ設定）b1、b2アカウントメールアドレス凍結日数の入力イベント及びバリデーション
         * 
         * @param isInput 入力イベントか（true:入力イベントである, false:入力イベントではない）
         */
        function validateStudentGeneralMailReuseDay(isInput: boolean) {
            // 値がnullである場合
            if (state.parameters.studentGeneralMailReuseDay.value === null) {
                // 空文字を設定する
                state.parameters.studentGeneralMailReuseDay.value = '';
            }

            // 先頭の0をすべて削除する
            state.parameters.teacherMailReuseDay.value = state.parameters.teacherMailReuseDay.value.replace(/^0+/, '');

            // b1、b2アカウントメールアドレス凍結日数のバリデーションを行う
            for (let index = 0; index < studentGeneralMailReuseDayRules.length; index++) {
                const rule: any = studentGeneralMailReuseDayRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    // エラーメッセージを設定する
                    state.studentGeneralMailReuseDayErrorMessage = valid;
                    // b1、b2アカウントメールアドレス凍結日数のバリデーション結果フラグをfalseにする
                    state.isStudentGeneralMailReuseDayValid = false;
                    break;
                } else {
                    // エラーなし
                    // エラーメッセージに空文字設定する
                    state.studentGeneralMailReuseDayErrorMessage = '';
                    // b1、b2アカウントメールアドレス凍結日数のバリデーション結果フラグをtrueにする
                    state.isStudentGeneralMailReuseDayValid = true;
                }
            }

            // 画面の入力イベントによる呼び出しである場合
            if (isInput) {
                // メールエイリアス削除日数のバリデーションを行う
                validateDeleteDays(false);
            }
        }

        /**
         * ファイル拡張子の文字数を取得
         * 
         * @param isFirstExtension 1番目のファイル拡張子か
         * @returns ファイル拡張子の文字数
         */
        function fileExtensionCounter(isFirstExtension: boolean) {
            // 重要情報のお知らせの場合
            if (state.serviceCode === SystemConst.SERVICE_CODE.IMPORTANT_INFORMATION) {
                return state.parameters.attachmentDetailFileExtension.value.join('').length;
            } else if (state.serviceCode === SystemConst.SERVICE_CODE.MATERIAL_DB) {
                // 教材DBの場合
                if (isFirstExtension) {
                    // 1番目のファイル拡張子（教材ファイル）である場合
                    return state.parameters.materialFileExtension.value.join('').length;
                } else {
                    // 上記の条件に当てはまらない場合（サムネイルファイル）
                    return state.parameters.thumbnailFileExtension.value.join('').length;
                }
            } else if (state.serviceCode === SystemConst.PARAMETER_FUNCTION_CODE.ADMIN) {
                // システム管理の場合
                return state.parameters.replyFileExtension.value.join('').length;

            } else if (state.serviceCode === SystemConst.PARAMETER_FUNCTION_CODE.ORG_ADMIN) {
                // 組織内管理の場合
                if (isFirstExtension) {
                    // 1項目目のファイル拡張子（申請ファイル）である場合
                    return state.parameters.applicationFileExtension.value.join('').length;
                } else {
                    // 上記の条件に当てはまらない場合（添付ファイル）
                    return state.parameters.attachFileExtension.value.join('').length;
                }
            } else {
                // 上記の条件に当てはまらない場合
                return state.parameters.fileExtension.value.join('').length;
            }
        }

        /**
         * ファイル拡張子削除イベント
         * 
         * @param extension 削除するファイル拡張子
         * @param isFirstExtension 1番目のファイル拡張子か
         */
        function removeExtension(extension: string, isFirstExtension: boolean) {
            // 重要情報のお知らせの場合
            if (state.serviceCode === SystemConst.SERVICE_CODE.IMPORTANT_INFORMATION) {
                // state.parameters.attachmentDetailFileExtension.valueからextensionに一致するファイル拡張子を削除する
                state.parameters.attachmentDetailFileExtension.value.splice(state.parameters.attachmentDetailFileExtension.value.indexOf(extension), 1);
                // 送付ファイルファイル拡張子のバリデーションを行う
                validateAttachmentDetailFileExtension();
            } else if (state.serviceCode === SystemConst.SERVICE_CODE.MATERIAL_DB) {
                // 教材DBの場合
                if (isFirstExtension) {
                    // 1番目のファイル拡張子（教材ファイル）である場合
                    // state.parameters.materialFileExtension.valueからextensionに一致するファイル拡張子を削除する
                    state.parameters.materialFileExtension.value.splice(state.parameters.materialFileExtension.value.indexOf(extension), 1);
                    // 教材ファイルファイル拡張子のバリデーションを行う
                    validateMaterialFileExtension();
                } else {
                    // 上記の条件に当てはまらない場合（サムネイルファイル）
                    // state.parameters.thumbnailFileExtension.valueからextensionに一致するファイル拡張子を削除する
                    state.parameters.thumbnailFileExtension.value.splice(state.parameters.thumbnailFileExtension.value.indexOf(extension), 1);
                    // サムネイルファイルファイル拡張子のバリデーションを行う
                    validateThumbnailFileExtension();
                }
            } else if (state.serviceCode === SystemConst.PARAMETER_FUNCTION_CODE.ADMIN) {
                // システム管理の場合
                // state.parameters.replyFileExtension.valueからextensionに一致するファイル拡張子を削除する
                state.parameters.replyFileExtension.value.splice(state.parameters.replyFileExtension.value.indexOf(extension), 1);
                // 返信ファイルファイル拡張子のバリデーションを行う
                validateReplyFileExtension();
            } else if (state.serviceCode === SystemConst.PARAMETER_FUNCTION_CODE.ORG_ADMIN) {
                // 組織内管理の場合
                if (isFirstExtension) {
                    // 1項目目のファイル拡張子（申請ファイル）である場合
                    // state.parameters.applicationFileExtension.valueからextensionに一致するファイル拡張子を削除する
                    state.parameters.applicationFileExtension.value.splice(state.parameters.applicationFileExtension.value.indexOf(extension), 1);
                    // 申請ファイルファイル拡張子のバリデーションを行う
                    validateApplicationFileExtension();
                } else {
                    // 上記の条件に当てはまらない場合（添付ファイル）
                    // state.parameters.attachFileExtension.valueからextensionに一致するファイル拡張子を削除する
                    state.parameters.attachFileExtension.value.splice(state.parameters.attachFileExtension.value.indexOf(extension), 1);
                    // 添付ファイルファイル拡張子のバリデーションを行う
                    validateAttachFileExtension();
                }
            } else {
                // 上記の条件に当てはまらない場合
                // state.parameters.fileExtension.valueからextensionに一致するファイル拡張子を削除する
                state.parameters.fileExtension.value.splice(state.parameters.fileExtension.value.indexOf(extension), 1);
                // ファイル拡張子のバリデーションを行う
                validateFileExtension();
            }
        }

        /**
         * パラメータ設定登録確認ダイアログ表示
         */
        function displayParametersDialog() {
            // フォームのバリデーション結果フラグを初期化
            let isForm = true;

            // 各項目のバリデーションを行う
            switch (state.serviceCode) {
                case SystemConst.SERVICE_CODE.OFFICIAL_DOCUMENTS:
                    // 公文書の場合
                    // 新着日数
                    validateNewArrivalDays();
                    // ファイル拡張子
                    validateFileExtension();
                    // 最大値ファイルサイズ
                    validateFileSize();
                    // 最大値ファイル数
                    validateFileCount();
                    // メール通知定型文
                    isForm = form.value.validate();
                    break;
                case SystemConst.SERVICE_CODE.INFORMATION:
                    // お知らせの場合
                    // 新着日数
                    validateNewArrivalDays();
                    // ファイル拡張子
                    validateFileExtension();
                    // 最大値ファイルサイズ
                    validateFileSize();
                    // 最大値ファイル数
                    validateFileCount();
                    // 掲載期間終了日数
                    validatePublishEndDays();
                    break;
                case SystemConst.SERVICE_CODE.IMPORTANT_INFORMATION:
                    // 重要情報のお知らせの場合
                    // 新着日数
                    validateNewArrivalDays();
                    // 基本データファイル最大ファイルサイズ
                    validateBasicFileSize();
                    // 添付データファイル最大ファイルサイズ
                    validateAttachmentFileSize();
                    // 送付ファイルファイル拡張子
                    validateAttachmentDetailFileExtension();
                    // 送付ファイル最大ファイルサイズ
                    validateAttachmentDetailFileSize();
                    // 送付ファイル最大ファイル数
                    validateAttachmentDetailFileCount();
                    break;
                case SystemConst.SERVICE_CODE.MATERIAL_DB:
                    // 教材DBの場合
                    // 新着日数
                    validateNewArrivalDays();
                    // 教材ファイルファイル拡張子
                    validateMaterialFileExtension();
                    // 教材ファイル最大ファイルサイズ
                    validateMaterialFileSize();
                    // 教材ファイル最大ファイル数
                    validateMaterialFileCount();
                    // サムネイルファイルファイル拡張子
                    validateThumbnailFileExtension();
                    // サムネイル最大ファイルサイズ
                    validateThumbnailFileSize();
                    break;
                case SystemConst.SERVICE_CODE.CONTENT:
                    // 教育用コンテンツの場合
                    // 新着日数
                    validateNewArrivalDays();
                    // ファイル拡張子
                    validateFileExtension();
                    // 最大値ファイルサイズ
                    validateFileSize();
                    // 最大値ファイル数
                    validateFileCount();
                    break;
                case SystemConst.PARAMETER_FUNCTION_CODE.ADMIN:
                    // システム管理の場合
                    // 一括登録ファイル最大ファイルサイズ
                    validateBulkAddUsersFileSize();
                    // 返信ファイルファイル拡張子
                    validateReplyFileExtension();
                    // 返信ファイル最大ファイルサイズ
                    validateReplyFileSize();
                    // 返信ファイル最大ファイル数
                    validateReplyFileCount();
                    // 所属コードファイル最大ファイルサイズ
                    validateAffiliationCodeFileSize();
                    // 人事異動データファイル最大ファイルサイズ
                    validatePersonnelChangeFileSize();
                    // パスワード情報ファイル最大ファイルサイズ
                    validateNotificationPasswordFileSize();
                    // 宛名ファイル最大ファイルサイズ
                    validateNotificationAddresseeFileSize();
                    // 雛形ファイル最大ファイルサイズ
                    validateNotificationTemplateFileSize();
                    // 基本データファイルタイトル
                    validateNotificationDataTitle();
                    // 基本データファイルメッセージ
                    isForm = form.value.validate();
                    break;
                case SystemConst.PARAMETER_FUNCTION_CODE.ORG_ADMIN:
                    // 組織内管理の場合
                    // 一括登録ファイル最大ファイルサイズ
                    validateBulkAddUsersFileSize();
                    // 申請ファイルファイル拡張子
                    validateApplicationFileExtension();
                    // 申請ファイル最大ファイルサイズ
                    validateApplicationFileSize();
                    // 添付ファイルファイル拡張子
                    validateAttachFileExtension();
                    // 添付ファイル最大ファイルサイズ
                    validateAttachFileSize();
                    // 添付ファイル最大ファイル数
                    validateAttachFileCount();
                    break;
                case SystemConst.PARAMETER_FUNCTION_CODE.COMMON:
                    // 共通（システム管理・組織内管理）の場合
                    // メールエイリアス削除日数
                    validateDeleteDays(false);
                    // a1、c1、s1アカウントメールアドレス凍結日数
                    validateTeacherMailReuseDay(false);
                    // b1、b2アカウントメールアドレス凍結日数
                    validateStudentGeneralMailReuseDay(false);
                    break;
            }

            // バリデーションエラーの際にフォームの一番上にスクロールする
            if (!isForm
                ||
                !state.isNewArrivalDaysValid
                ||
                !state.isFileExtensionValid
                ||
                !state.isFileSizeValid
                ||
                !state.isFileCountValid
                ||
                !state.isPublishEndDaysValid
                ||
                !state.isBasicFileSizeValid
                ||
                !state.isAttachmentFileSizeValid
                ||
                !state.isAttachmentDetailFileExtensionValid
                ||
                !state.isAttachmentDetailFileSizeValid
                ||
                !state.isAttachmentDetailFileCountValid
                ||
                !state.isMaterialFileExtensionValid
                ||
                !state.isMaterialFileSizeValid
                ||
                !state.isMaterialFileCountValid
                ||
                !state.isThumbnailFileExtensionValid
                ||
                !state.isThumbnailFileSizeValid
                ||
                !state.isBulkAddUsersFileSizeValid
                ||
                !state.isReplyFileExtensionValid
                ||
                !state.isReplyFileSizeValid
                ||
                !state.isReplyFileCountValid
                ||
                !state.isApplicationFileExtensionValid
                ||
                !state.isApplicationFileSizeValid
                ||
                !state.isAttachFileExtensionValid
                ||
                !state.isAttachFileSizeValid
                ||
                !state.isAttachFileCountValid
                ||
                !state.isAffiliationCodeFileSizeValid
                ||
                !state.isPersonnelChangeFileSizeValid
                ||
                !state.isNotificationPasswordFileSizeValid
                ||
                !state.isNotificationAddresseeFileSizeValid
                ||
                !state.isNotificationTemplateFileSizeValid
                ||
                !state.isNotificationDataTitleValid
                ||
                !state.isDeleteDaysValid
                ||
                !state.isTeacherMailReuseDayValid
                ||
                !state.isStudentGeneralMailReuseDayValid
                )
            {
                const formScroll = document.getElementById('formScroll');
                if (formScroll !== null) {
                    formScroll.scrollTo(0, 0);
                }
                return;
            }

            // パラメータ設定処理実行確認ダイアログに表示するメッセージを格納する
            let displayMessage = '';
            displayMessage = CommonFunction.replaceArray(MessageConst.S_CONFIRMATION, ['登録']);

            // 確認メッセージダイアログ表示
            //   「はい」をクリックした場合はprocessingData()メソッドが呼び出され、
            //   「いいえ」をクリックした場合はダイアログを閉じる
            commonRef.value.displayConfirmationMessageDialog(displayMessage);
        }

        /**
         * カテゴリ設定登録確認ダイアログ表示
         */
        function displayCategoriesDialog() {
            // errorsを初期化
            errors = [];

            // validateCategories()を呼び出しでバリデーションを行う
            validateCategories();

            // errorsにメッセージが格納されている場合
            if (errors.length !== 0) {
                // displayValidateDialog()を呼び出す
                displayValidateDialog();
                return;
            }

            // カテゴリ設定登録確認ダイアログに表示するメッセージを格納する
            let displayMessage = '';
            displayMessage = CommonFunction.replaceArray(MessageConst.S_CONFIRMATION, ['登録']);

            // 確認メッセージダイアログ表示
            //   「はい」をクリックした場合はprocessingData()メソッドが呼び出され、
            //   「いいえ」をクリックした場合はダイアログを閉じる
            commonRef.value.displayConfirmationMessageDialog(displayMessage);
        }

        /**
         * カテゴリ設定バリデーション
         */
        function validateCategories() {
            // 各項目のバリデーションを行う
            // 公文書の発番カテゴリ設定である場合
            if (state.selectFunctionPulldown === selectFunctionPulldownItems['OFFICIAL_DOCUMENTS'] &&
                state.selectTab === tabLists['SECOND']) {
                // 表示順
                for (let index = 0; index < displayOrderRules.length; index++) {
                    const rule: any = displayOrderRules[index];
                    typeof rule === 'function' ? rule() : rule
                }

                // 組織名称
                for (let index = 0; index < categoryIdRules.length; index++) {
                    const rule: any = categoryIdRules[index];
                    typeof rule === 'function' ? rule() : rule
                }

                // 発番接頭辞
                for (let index = 0; index < numberPrefixRules.length; index++) {
                    const rule: any = numberPrefixRules[index];
                    typeof rule === 'function' ? rule() : rule
                }

                // カテゴリ名称
                for (let index = 0; index < categoryNameRules.length; index++) {
                    const rule: any = categoryNameRules[index];
                    typeof rule === 'function' ? rule() : rule
                }

                // 部門名
                for (let index = 0; index < departmentNameRules.length; index++) {
                    const rule: any = departmentNameRules[index];
                    typeof rule === 'function' ? rule() : rule
                }

                // 電話番号
                for (let index = 0; index < telRules.length; index++) {
                    const rule: any = telRules[index];
                    typeof rule === 'function' ? rule() : rule
                }

                // 管理者のみ表示
                for (let index = 0; index < isAdminOnlyRules.length; index++) {
                    const rule: any = isAdminOnlyRules[index];
                    typeof rule === 'function' ? rule() : rule
                }
            } else if (state.selectFunctionPulldown === selectFunctionPulldownItems['OFFICIAL_DOCUMENTS'] &&
                state.selectTab === tabLists['THIRD']) {
                // 公文書のメール通知先設定である場合
                // 表示順
                for (let index = 0; index < displayOrderRules.length; index++) {
                    const rule: any = displayOrderRules[index];
                    typeof rule === 'function' ? rule() : rule
                }

                // メール通知先名称
                for (let index = 0; index < categoryNameRules.length; index++) {
                    const rule: any = categoryNameRules[index];
                    typeof rule === 'function' ? rule() : rule
                }

                // メールアドレス
                for (let index = 0; index < mailRules.length; index++) {
                    const rule: any = mailRules[index];
                    typeof rule === 'function' ? rule() : rule
                }

                // 状態
                for (let index = 0; index < isEnabledRules.length; index++) {
                    const rule: any = isEnabledRules[index];
                    typeof rule === 'function' ? rule() : rule
                }
            } else if (state.selectFunctionPulldown === selectFunctionPulldownItems['MATERIAL_DB']) {
                // 教材DBである場合（校種設定、教科設定、科目設定）
                // 表示順
                for (let index = 0; index < displayOrderRules.length; index++) {
                    const rule: any = displayOrderRules[index];
                    typeof rule === 'function' ? rule() : rule
                }

                // 校種名称
                for (let index = 0; index < categoryNameRules.length; index++) {
                    const rule: any = categoryNameRules[index];
                    typeof rule === 'function' ? rule() : rule
                }
            } else if (state.selectFunctionPulldown === selectFunctionPulldownItems['CONTENT'] &&
                state.selectTab === tabLists['SECOND']) {
                // 教育用コンテンツのコンテンツカテゴリ設定である場合
                // 表示順
                for (let index = 0; index < displayOrderRules.length; index++) {
                    const rule: any = displayOrderRules[index];
                    typeof rule === 'function' ? rule() : rule
                }

                // カテゴリ名称
                for (let index = 0; index < categoryNameRules.length; index++) {
                    const rule: any = categoryNameRules[index];
                    typeof rule === 'function' ? rule() : rule
                }

                // 状態
                for (let index = 0; index < isEnabledRules.length; index++) {
                    const rule: any = isEnabledRules[index];
                    typeof rule === 'function' ? rule() : rule
                }
            } else if (state.selectFunctionPulldown === selectFunctionPulldownItems['ORG_ADMIN'] &&
                state.selectTab === tabLists['SECOND']) {
                // 組織内管理の処理依頼内容設定である場合
                // 表示順
                for (let index = 0; index < displayOrderRules.length; index++) {
                    const rule: any = displayOrderRules[index];
                    typeof rule === 'function' ? rule() : rule
                }

                // 依頼内容名称
                for (let index = 0; index < categoryNameRules.length; index++) {
                    const rule: any = categoryNameRules[index];
                    typeof rule === 'function' ? rule() : rule
                }

                // 状態
                for (let index = 0; index < isEnabledRules.length; index++) {
                    const rule: any = isEnabledRules[index];
                    typeof rule === 'function' ? rule() : rule
                }
            }
        }

        /**
         * カテゴリ設定バリデーションエラーダイアログ表示
         */
        function displayValidateDialog() {
            // エラーダイアログを表示する
            state.showErrorMessageDialog = true;

            // エラー表示内容を初期化する
            state.errorItems = [];

            // errorsの件数分繰り返す
            for (let i = 0; i < errors.length; i++) {
                // エラーメッセージ一件取得して「、」で分割する
                const oneError = errors[i].split('、');

                // 「、」で分割した最初のデータをstate.errorItemsの'errorLocation'に設定する
                // 「、」で分割した最後のデータをstate.errorItemsの'message'に設定する
                state.errorItems[i] = {errorLocation: oneError[0], message: oneError[1]};
            }
        }

        /**
         * 確認メッセージダイアログの「はい」押下時の処理
         */
        function processingData() {
            if (state.selectTab === tabLists['FIRST']) {
                // 1番目のタブである場合(パラメータ設定)
                updateParameters();
            } else {
                // 上記以外のタブである場合
                processCategories();
            }
        }

        /**
         * パラメータ設定編集処理
         */
        async function updateParameters() {
            // フォームデータ配列初期化
            const parameters = {
                'newArrivalDays': state.parameters.newArrivalDays,
                'fileExtension': state.parameters.fileExtension,
                'fileSize': state.parameters.fileSize,
                'fileCount': state.parameters.fileCount,
                'mailBodyTemplate': state.parameters.mailText,
                'publishEndDays': state.parameters.publishEndDays,
                'basicFileSize': state.parameters.basicFileSize,
                'attachmentFileSize': state.parameters.attachmentFileSize,
                'attachmentDetailFileExtension': state.parameters.attachmentDetailFileExtension,
                'attachmentDetailFileSize': state.parameters.attachmentDetailFileSize,
                'attachmentDetailFileCount': state.parameters.attachmentDetailFileCount,
                'materialFileExtension': state.parameters.materialFileExtension,
                'materialFileSize': state.parameters.materialFileSize,
                'materialFileCount': state.parameters.materialFileCount,
                'thumbnailFileExtension': state.parameters.thumbnailFileExtension,
                'thumbnailFileSize': state.parameters.thumbnailFileSize,
                'bulkAddUsersFileSize': state.parameters.bulkAddUsersFileSize,
                'replyFileExtension': state.parameters.replyFileExtension,
                'replyFileSize': state.parameters.replyFileSize,
                'replyFileCount': state.parameters.replyFileCount,
                'applicationFileExtension': state.parameters.applicationFileExtension,
                'applicationFileSize': state.parameters.applicationFileSize,
                'attachFileExtension': state.parameters.attachFileExtension,
                'attachFileSize': state.parameters.attachFileSize,
                'attachFileCount': state.parameters.attachFileCount,
                'affiliationCodeFileSize': state.parameters.affiliationCodeFileSize,
                'personnelChangeFileSize': state.parameters.personnelChangeFileSize,
                'notificationPasswordFileSize': state.parameters.notificationPasswordFileSize,
                'notificationAddresseeFileSize': state.parameters.notificationAddresseeFileSize,
                'notificationTemplateFileSize': state.parameters.notificationTemplateFileSize,
                'notificationDataTitle': state.parameters.notificationDataTitle,
                'notificationDataMessage': state.parameters.notificationDataMessage,
                'deleteDays': state.parameters.deleteDays,
                'teacherMailReuseDay': state.parameters.teacherMailReuseDay,
                'studentGeneralMailReuseDay': state.parameters.studentGeneralMailReuseDay
            };

            // パラメータ設定編集APIでデータを編集する
            // パラメータ：サービスコード
            //            フォームデータ
            const response = await ctx.root.$store.dispatch('parameterSettings/updateParameters',
                {
                    serviceCode: state.serviceCode,
                    forms: parameters
                }
            );

            // HTTPステータスコードが200以外の場合
            if (response.status !== HttpStatus.OK) {
                // HTTPステータスコード400又は404であればエラーダイアログを表示し、それ以外はエラー画面へ遷移して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // HTTPステータスコードが200の場合
            // 登録完了メッセージダイアログ表示
            commonRef.value.displayMessageDialog(response);

            // パラメータマスタデータ取得関数を呼び出す
            getParameters();
        }

        /**
         * カテゴリ設定登録処理
         */
        async function processCategories() {
            // カテゴリ設定登録APIでカテゴリデータを登録する
            // パラメータ：サービスコード
            //            タブ番号
            //            カテゴリ一覧情報
            //            選択した校種ID（教科設定、科目設定以外は空文字）
            //            選択した教科ID（科目設定以外は空文字）
            const response = await ctx.root.$store.dispatch('parameterSettings/processCategories',
                {
                    serviceCode: state.serviceCode,
                    selectTab: state.selectTab,
                    forms: state.categoryListItems,
                    selectSchoolTypePulldown: state.selectSchoolTypePulldown,
                    selectCurriculumPulldown: state.selectCurriculumPulldown
                }
            );

            // HTTPステータスコードが200以外の場合
            if (response.status !== HttpStatus.OK) {
                if (response.status === HttpStatus.BAD_REQUEST && response.data.isValidateError) {
                    // バリデーションチェックでエラーが発生した場合（400のステータスコード）
                    // ローダーの表示を閉じる
                    commonRef.value.state.showDialog = false;
                    // レスポンスのエラー内容をerrorsに保存する
                    errors = response.data.errors;
                    // カテゴリ設定バリデーションエラーダイアログ表示関数呼び出し
                    displayValidateDialog();
                    return;
                } else if (response.status === HttpStatus.NOT_FOUND) {
                    // HTTPステータスコードが404であればエラーダイアログを表示し、カテゴリデータ取得関数を呼び出す
                    commonRef.value.errorDisplayTransitionControl(response);
                    getCategories();
                    return;
                } else {
                    // バリデーションエラーではない400及び404以外はエラー画面へ遷移して処理を終了する
                    commonRef.value.errorDisplayTransitionControl(response);
                    return;
                }
            }

            // HTTPステータスコードが200の場合
            // 登録完了メッセージダイアログ表示
            commonRef.value.displayMessageDialog(response);

            // カテゴリデータ取得関数を呼び出し、一覧情報を更新する
            getCategories();
        }

        /**
         * 発番カテゴリ設定の組織名称の入力値がNULLであるかチェックする
         * 
         * @param checkRow チェック行
         */
        function checkNull(checkRow: number) {
            if (state.categoryListItems[checkRow].categoryId === null) {
                // NULLである場合空文字を設定する
                state.categoryListItems[checkRow].categoryId = '';
            }
        }

        /**
         * chipで表示する各ファイル拡張子の長さをチェックする
         * 
         * @param item チェックするファイル拡張子
         * @returns 表示するファイル拡張子
         */
        function displayChips(item: string) {
            // ファイル拡張子の戻り値初期化
            let chipItem = '';

            if (item.length > SystemConst.DISPLAY_EXTENSION_LENGTH) {
                // ファイル拡張子の長さが10文字以上の場合、11文字以降を表示しない
                // 10文字目までを取得し、11文字目を三点リーダーにする
                chipItem = item.substring(0, SystemConst.DISPLAY_EXTENSION_LENGTH).concat('...');
            } else {
                // 桁数を超過していない場合、引数のファイル拡張子を戻り値に設定する
                chipItem = item;
            }
            return chipItem;
        }

        return {
            state: readonly(state),
            selectFunctionPulldownItems,
            processCode,
            tabLists,
            tableHeight,
            curriculumTableHeight,
            subjectTableHeight,
            tableLoadingText,
            formHeight,
            commonRef,
            form,
            paramMaxLength,
            mailTextRules,
            notificationDataMessageRules,
            changeFunction,
            displayOrg,
            onResize,
            addRow,
            deleteRow,
            dragRow,
            dropRow,
            selectTab,
            getHighSchoolName,
            changeSchoolType,
            changeCurriculum,
            validateNewArrivalDays,
            validatePublishEndDays,
            validateFileExtension,
            validateFileSize,
            validateFileCount,
            validateBasicFileSize,
            validateAttachmentFileSize,
            validateAttachmentDetailFileExtension,
            validateAttachmentDetailFileSize,
            validateAttachmentDetailFileCount,
            validateMaterialFileExtension,
            validateMaterialFileSize,
            validateMaterialFileCount,
            validateThumbnailFileExtension,
            validateThumbnailFileSize,
            validateBulkAddUsersFileSize,
            validateReplyFileExtension,
            validateReplyFileSize,
            validateReplyFileCount,
            validateAffiliationCodeFileSize,
            validatePersonnelChangeFileSize,
            validateNotificationPasswordFileSize,
            validateNotificationAddresseeFileSize,
            validateNotificationTemplateFileSize,
            validateNotificationDataTitle,
            validateApplicationFileExtension,
            validateApplicationFileSize,
            validateAttachFileExtension,
            validateAttachFileSize,
            validateAttachFileCount,
            validateDeleteDays,
            validateTeacherMailReuseDay,
            validateStudentGeneralMailReuseDay,
            fileExtensionCounter,
            removeExtension,
            displayParametersDialog,
            displayCategoriesDialog,
            processingData,
            checkNull,
            displayChips
        };
    }
});
